import {
  BUNDLE_SET_TYPES,
  BUNDLE_STATUSES,
  DEFAULT_BUNDLE_CONFIG,
  DEFAULT_DRIVER_STATUS,
  DEFAULT_OFFER_SOURCE,
  DEFAULT_OFFER_STRATEGY,
  DEFAULT_STRATEGY_PARAMS,
  DISPATCHABLE_DRIVER_STATUSES,
  DISPATCHABLE_DRIVER_WHERE_CLAUSES,
  DRIVER_ACCOUNT_STATUSES,
  DRIVER_SELECTION_ALGORITHMS,
  DRIVER_SELECTION_ALGORITHM_VARIABLES,
  DRIVER_STATUSES,
  MOVE_TYPES,
  OFFER_SOURCES,
  OFFER_STATUSES,
  OFFER_STRATEGIES,
  OfferAlreadyAcceptedError,
  RIDE_POSITION,
  RIDE_TYPES,
  SCENARIO_STATUS,
  SCENARIO_TYPES,
  USE_CASES,
  VALID_BUNDLE_SET_TYPES,
  VALID_BUNDLE_STATUSES,
  VALID_DRIVER_SELECTION_ALGORITHMS,
  VALID_DRIVER_STATUSES,
  VALID_MOVE_TYPES,
  VALID_OFFER_SOURCES,
  VALID_OFFER_STATUSES,
  VALID_OFFER_STRATEGIES,
  VALID_RIDE_POSITIONS,
  VALID_RIDE_TYPES,
  VALID_SCENARIO_STATUSES,
  VALID_SCENARIO_TYPES,
  VALID_USE_CASES,
  __require,
  addScenariosToBundle,
  consoleLoggerShim,
  deleteBundle,
  getBundleById,
  getBundleWithMoveDetails,
  getBundleWithMoveDetailsLightweight,
  getNestedProperty,
  getOriginLocationFromBundle,
  gqlErrors,
  log,
  logger,
  resolveLogger,
  safeJsonParse,
  safeJsonStringify,
  updateBundleConfig,
  updateBundleStatus,
  updateBundleStatuses
} from "./chunk-PQYQ2DKQ.mjs";

// src/bundles/bundleCreation.ts
import { gql } from "graphql-request";

// src/offers/offerStrategies.ts
function isBundle(obj) {
  return obj && typeof obj.id === "number" && typeof obj.status === "string" && typeof obj.config === "object";
}
function isOfferRound(obj) {
  return obj && typeof obj.round === "number" && typeof obj.type === "string" && typeof obj.completed === "boolean";
}
function isOfferRoundArray(arr) {
  return Array.isArray(arr) && arr.every(isOfferRound);
}
function getOfferStrategy(config = {}) {
  if (!config || !config.offerStrategy) {
    return DEFAULT_OFFER_STRATEGY;
  }
  const strategy = config.offerStrategy;
  if (!VALID_OFFER_STRATEGIES.includes(strategy)) {
    log(
      "getOfferStrategy",
      `Invalid strategy: ${strategy}, using default: ${DEFAULT_OFFER_STRATEGY}`
    );
    return DEFAULT_OFFER_STRATEGY;
  }
  if (strategy === OFFER_STRATEGIES.TARGETED && !config.driverIdOverride) {
    log("getOfferStrategy", "Targeted strategy requires driverIdOverride, using default strategy");
    return DEFAULT_OFFER_STRATEGY;
  }
  const offerRounds = config.offerRounds || [];
  if (offerRounds.length > 0 && offerRounds[0].type !== strategy) {
    log(
      "getOfferStrategy",
      `First round type ${offerRounds[0].type} does not match strategy ${strategy}, correcting`
    );
    offerRounds[0] = {
      ...offerRounds[0],
      type: strategy
    };
  }
  return strategy;
}
function getOfferRounds(config = {}, strategy = DEFAULT_OFFER_STRATEGY) {
  if (!config.offerRounds || !Array.isArray(config.offerRounds) || config.offerRounds.length === 0) {
    return initializeOfferRounds({ strategy });
  }
  const rounds = [...config.offerRounds];
  if (rounds.length > 0 && rounds[0].type !== strategy) {
    log(
      "getOfferRounds",
      `First round type ${rounds[0].type} does not match root strategy ${strategy}, correcting`
    );
    rounds[0] = {
      ...rounds[0],
      type: strategy
    };
  }
  return rounds;
}
function initializeOfferRounds({
  strategy = DEFAULT_OFFER_STRATEGY,
  params = {}
} = {}) {
  switch (strategy) {
    case OFFER_STRATEGIES.TARGETED: {
      const { timeoutMinutes: timeoutMinutesDefault = 15 } = DEFAULT_STRATEGY_PARAMS[OFFER_STRATEGIES.TARGETED] || {};
      const timeoutMinutes = params?.timeoutMinutes || timeoutMinutesDefault;
      return [
        {
          round: 1,
          type: OFFER_STRATEGIES.TARGETED,
          timeoutMinutes,
          completed: false
        }
      ];
    }
    case OFFER_STRATEGIES.CASCADING: {
      const {
        initialRadiusMiles: initialRadiusMilesDefault = 5,
        maxRadiusMiles: maxRadiusMilesDefault = 50,
        radiusIncrementMiles: radiusIncrementMilesDefault = 5,
        timeoutMinutes: timeoutMinutesDefault = 30,
        maxDriversPerRound: maxDriversPerRoundDefault = 10,
        maxRounds: maxRoundsDefault = 10
      } = DEFAULT_STRATEGY_PARAMS[OFFER_STRATEGIES.CASCADING] || {};
      const initialRadiusMiles = params?.initialRadiusMiles | initialRadiusMilesDefault;
      const maxRadiusMiles = params?.maxRadiusMiles || maxRadiusMilesDefault;
      const radiusIncrementMiles = params?.radiusIncrementMiles || radiusIncrementMilesDefault;
      const timeoutMinutes = params?.timeoutMinutes || timeoutMinutesDefault;
      const maxDriversPerRound = params?.maxDriversPerRound || maxDriversPerRoundDefault;
      const maxRounds = params?.maxRounds || maxRoundsDefault;
      const rounds = [];
      let currentRadius = initialRadiusMiles;
      let currentRound = 1;
      while (currentRadius <= maxRadiusMiles && currentRound <= maxRounds) {
        rounds.push({
          round: currentRound,
          type: OFFER_STRATEGIES.CASCADING,
          radiusMiles: currentRadius,
          maxDrivers: maxDriversPerRound,
          timeoutMinutes,
          completed: false
        });
        currentRadius += radiusIncrementMiles;
        currentRound++;
      }
      return rounds;
    }
    case OFFER_STRATEGIES.BROADCAST: {
      const {
        radiusMiles: radiusMilesDefault = 100,
        maxDrivers: maxDriversDefault = 10,
        timeoutMinutes: timeoutMinutesDefault = 5
      } = DEFAULT_STRATEGY_PARAMS[OFFER_STRATEGIES.BROADCAST] || {};
      const radiusMiles = params?.radiusMiles || radiusMilesDefault;
      const maxDrivers = params?.maxDrivers || maxDriversDefault;
      const timeoutMinutes = params?.timeoutMinutes || timeoutMinutesDefault;
      return [
        {
          round: 1,
          type: OFFER_STRATEGIES.BROADCAST,
          radiusMiles,
          maxDrivers,
          timeoutMinutes,
          completed: false
        }
      ];
    }
    case OFFER_STRATEGIES.ROLLING_BROADCAST: {
      const {
        radiusMiles: radiusMilesDefault = 100,
        maxDrivers: maxDriversDefault = 10,
        timeoutMinutes: timeoutMinutesDefault = 5
      } = DEFAULT_STRATEGY_PARAMS[OFFER_STRATEGIES.ROLLING_BROADCAST] || {};
      const radiusMiles = params?.radiusMiles || radiusMilesDefault;
      const maxDrivers = params?.maxDrivers || maxDriversDefault;
      const timeoutMinutes = params?.timeoutMinutes || timeoutMinutesDefault;
      return [
        {
          round: 1,
          type: OFFER_STRATEGIES.ROLLING_BROADCAST,
          radiusMiles,
          maxDrivers: 1,
          delayMinutes: 0,
          timeoutMinutes: 28,
          completed: false
        },
        {
          round: 2,
          type: OFFER_STRATEGIES.ROLLING_BROADCAST,
          radiusMiles,
          delayMinutes: 10,
          timeoutMinutes: 16,
          completed: false
        },
        {
          round: 3,
          type: OFFER_STRATEGIES.ROLLING_BROADCAST,
          radiusMiles,
          delayMinutes: 8,
          timeoutMinutes: 8,
          completed: false
        },
        {
          round: 4,
          type: OFFER_STRATEGIES.ROLLING_BROADCAST,
          radiusMiles,
          delayMinutes: 5,
          timeoutMinutes: 3,
          completed: false
        },
        {
          round: 5,
          type: OFFER_STRATEGIES.ROLLING_BROADCAST,
          radiusMiles,
          maxDrivers,
          delayMinutes: 3,
          timeoutMinutes,
          completed: false
        }
      ];
    }
    default:
      log("initializeOfferRounds", `Unrecognized strategy: ${strategy}, using cascading`);
      return initializeOfferRounds({ strategy: OFFER_STRATEGIES.CASCADING });
  }
}
function areAllRoundsCompleted(config = {}) {
  if (!config.offerRounds || !Array.isArray(config.offerRounds) || config.offerRounds.length === 0) {
    return false;
  }
  return config.offerRounds.every((round) => round.completed);
}
function isValidRound(round) {
  if (!round || typeof round !== "object") {
    return false;
  }
  if (typeof round.round !== "number" || typeof round.type !== "string" || typeof round.completed !== "boolean") {
    return false;
  }
  switch (round.type) {
    case OFFER_STRATEGIES.TARGETED:
      return typeof round.timeoutMinutes === "number";
    case OFFER_STRATEGIES.CASCADING:
    case OFFER_STRATEGIES.BROADCAST:
      return typeof round.radiusMiles === "number" && typeof round.maxDrivers === "number" && typeof round.timeoutMinutes === "number";
    case OFFER_STRATEGIES.ROLLING_BROADCAST:
      return typeof round.timeoutMinutes === "number" && typeof round.delayMinutes === "number";
    default:
      return false;
  }
}
function getNextIncompleteRound(config = {}) {
  if (!config || !Array.isArray(config.offerRounds) || config.offerRounds.length === 0) {
    const strategy = getOfferStrategy(config);
    const defaultRounds = initializeOfferRounds({ strategy });
    return defaultRounds.find((round) => !round.completed) || null;
  }
  const hasInvalidRounds = config.offerRounds.some((round) => !isValidRound(round));
  if (hasInvalidRounds) {
    const strategy = getOfferStrategy(config);
    const defaultRounds = initializeOfferRounds({ strategy });
    return defaultRounds.find((round) => !round.completed) || null;
  }
  return config.offerRounds.find((round) => !round.completed) || null;
}
async function markRoundCompleted(client, bundleId, config, roundNumber, options = {}) {
  const { user = "system" } = options;
  if (!client) {
    throw new Error("GraphQL client is required to mark a round completed");
  }
  if (!bundleId) {
    throw new Error("Bundle ID is required");
  }
  if (!config || !config.offerRounds) {
    throw new Error("Invalid bundle config, missing offerRounds");
  }
  if (!roundNumber) {
    throw new Error("Round number is required");
  }
  const updatedConfig = { ...config };
  const offerRounds = updatedConfig.offerRounds || [];
  updatedConfig.offerRounds = offerRounds.map((round) => {
    if (round.round === roundNumber) {
      return { ...round, completed: true };
    }
    return round;
  });
  const result = await updateBundleConfig(client, bundleId, updatedConfig, { user });
  if (!isBundle(result)) {
    throw new Error("Invalid bundle response from updateBundleConfig");
  }
  return result;
}
async function initializeOfferStrategy(client, bundleId, options = {}) {
  let { strategy = DEFAULT_OFFER_STRATEGY, params = {}, user = "system" } = options;
  if (!client) {
    throw new Error("GraphQL client is required to initialize offer strategy");
  }
  if (!bundleId) {
    throw new Error("Bundle ID is required");
  }
  if (!VALID_OFFER_STRATEGIES.includes(strategy)) {
    log("initializeOfferStrategy", `Invalid strategy: ${strategy}, using default`);
    strategy = DEFAULT_OFFER_STRATEGY;
  }
  if (strategy === OFFER_STRATEGIES.TARGETED && !params.driverIdOverride) {
    throw new Error("Targeted strategy requires driverIdOverride parameter");
  }
  const { getBundleById: getBundleById2 } = await import("./bundleManagement-VYO6OCFK.mjs");
  const bundle = await getBundleById2(client, bundleId);
  if (!bundle || !isBundle(bundle)) {
    throw new Error(`Bundle ${bundleId} not found or invalid`);
  }
  const currentConfig = bundle.config || {};
  const newConfig = {
    ...currentConfig,
    offerStrategy: strategy,
    offerRounds: initializeOfferRounds({ strategy, params })
  };
  if (strategy === OFFER_STRATEGIES.TARGETED && params.driverIdOverride) {
    newConfig.driverIdOverride = params.driverIdOverride;
  }
  if (params.offerRounds) {
    if (!isOfferRoundArray(params.offerRounds)) {
      throw new Error("Invalid offer rounds configuration");
    }
    newConfig.offerRounds = params.offerRounds;
  }
  const result = await updateBundleConfig(client, bundleId, newConfig, { user });
  if (!isBundle(result)) {
    throw new Error("Invalid bundle response from updateBundleConfig");
  }
  return result;
}

// src/bundles/bundleCreation.ts
var CREATE_BUNDLE_SET = gql`
  mutation createBundleSet($bundleSet: bundlesets_insert_input!) {
    insert_bundlesets_one(object: $bundleSet) {
      id
      type
      config
      createdat
      createdby
      updatedat
      updatedby
    }
  }
`;
var CREATE_BUNDLE = gql`
  mutation createBundle($bundle: bundles_insert_input!) {
    insert_bundles_one(object: $bundle) {
      id
      bundle_set_id
      status
      assigned_offer_id
      config
      createdby
      createdat
      updatedby
      updatedat
      bundleset {
        id
        type
      }
    }
  }
`;
async function createBundleSet(client, options = {}) {
  const {
    bundleSetType = BUNDLE_SET_TYPES.SINGLE,
    bundleSetConfig = {},
    user = "system"
  } = options;
  log(
    "createBundle",
    `Creating bundle set with options: ${JSON.stringify({
      bundleSetType,
      user
    })}`
  );
  if (!client) {
    throw new Error("GraphQL client is required to create a bundle set");
  }
  if (!VALID_BUNDLE_SET_TYPES.includes(bundleSetType)) {
    throw new Error(
      `Invalid bundle set type. Must be one of: ${VALID_BUNDLE_SET_TYPES.join(", ")}`
    );
  }
  const now = (/* @__PURE__ */ new Date()).toISOString();
  try {
    const bundleSetVariables = {
      bundleSet: {
        type: bundleSetType,
        config: bundleSetConfig,
        createdby: user,
        createdat: now,
        updatedby: user,
        updatedat: now
      }
    };
    const bundleSetResult = await client.request(CREATE_BUNDLE_SET, bundleSetVariables);
    if (!bundleSetResult?.insert_bundlesets_one?.id) {
      throw new Error("Failed to create bundle set");
    }
    const bundleSetId = bundleSetResult.insert_bundlesets_one.id;
    log("createBundle", `Created bundle set with ID ${bundleSetId}`);
    return bundleSetResult.insert_bundlesets_one;
  } catch (error) {
    log("createBundleSet", `Error: ${error.message}`);
    throw new Error(`Failed to create bundle set: ${error.message}`);
  }
}
async function createBundle(client, options = {}) {
  const {
    bundleSetType = BUNDLE_SET_TYPES.SINGLE,
    status = BUNDLE_STATUSES.NEW,
    assignedOfferId = null,
    config = {},
    bundleSetConfig = {},
    user = "system"
  } = options;
  log(
    "createBundle",
    `Creating bundle with options: ${JSON.stringify({
      bundleSetType,
      status,
      assignedOfferId: assignedOfferId ? "present" : "none",
      user
    })}`
  );
  if (!client) {
    throw new Error("GraphQL client is required to create a bundle");
  }
  if (!VALID_BUNDLE_SET_TYPES.includes(bundleSetType)) {
    throw new Error(
      `Invalid bundle set type. Must be one of: ${VALID_BUNDLE_SET_TYPES.join(", ")}`
    );
  }
  if (!VALID_BUNDLE_STATUSES.includes(status)) {
    throw new Error(`Invalid status. Must be one of: ${VALID_BUNDLE_STATUSES.join(", ")}`);
  }
  const now = (/* @__PURE__ */ new Date()).toISOString();
  try {
    const bundleSetVariables = {
      bundleSet: {
        type: bundleSetType,
        config: bundleSetConfig,
        createdby: user,
        createdat: now,
        updatedby: user,
        updatedat: now
      }
    };
    const bundleSetResult = await client.request(CREATE_BUNDLE_SET, bundleSetVariables);
    if (!bundleSetResult?.insert_bundlesets_one?.id) {
      throw new Error("Failed to create bundle set");
    }
    const bundleSetId = bundleSetResult.insert_bundlesets_one.id;
    log("createBundle", `Created bundle set with ID ${bundleSetId}`);
    let bundleConfig = { ...config };
    if (!bundleConfig.offerStrategy) {
      bundleConfig.offerStrategy = DEFAULT_OFFER_STRATEGY;
    }
    if (!bundleConfig.offerRounds || !Array.isArray(bundleConfig.offerRounds) || bundleConfig.offerRounds.length === 0) {
      bundleConfig.offerRounds = initializeOfferRounds({ strategy: bundleConfig.offerStrategy });
    }
    const bundleVariables = {
      bundle: {
        bundle_set_id: bundleSetId,
        status,
        assigned_offer_id: assignedOfferId,
        config: bundleConfig,
        createdby: user,
        createdat: now,
        updatedby: user,
        updatedat: now
      }
    };
    const bundleResult = await client.request(CREATE_BUNDLE, bundleVariables);
    if (!bundleResult?.insert_bundles_one?.id) {
      throw new Error("Failed to create bundle");
    }
    log("createBundle", `Created bundle with ID ${bundleResult.insert_bundles_one.id}`);
    bundleResult.insert_bundles_one.bundleset = bundleSetResult.insert_bundlesets_one;
    return bundleResult.insert_bundles_one;
  } catch (error) {
    log("createBundle", `Error: ${error.message}`);
    throw new Error(`Failed to create bundle: ${error.message}`);
  }
}
async function addNewBundleToBundleSet(client, bundleSetId, options = {}) {
  const {
    status = BUNDLE_STATUSES.NEW,
    assignedOfferId = null,
    config = {},
    user = "system"
  } = options;
  log("addNewBundleToBundleSet", `Adding bundle to set ${bundleSetId}`);
  if (!client) {
    throw new Error("GraphQL client is required to create a bundle");
  }
  if (!bundleSetId) {
    throw new Error("Bundle set ID is required");
  }
  if (!VALID_BUNDLE_STATUSES.includes(status)) {
    throw new Error(`Invalid status. Must be one of: ${VALID_BUNDLE_STATUSES.join(", ")}`);
  }
  try {
    let bundleConfig = { ...config };
    if (!bundleConfig.offerStrategy) {
      bundleConfig.offerStrategy = DEFAULT_OFFER_STRATEGY;
    }
    if (!bundleConfig.offerRounds || !Array.isArray(bundleConfig.offerRounds) || bundleConfig.offerRounds.length === 0) {
      bundleConfig.offerRounds = initializeOfferRounds({ strategy: bundleConfig.offerStrategy });
    }
    const now = (/* @__PURE__ */ new Date()).toISOString();
    const bundleVariables = {
      bundle: {
        bundle_set_id: bundleSetId,
        status,
        assigned_offer_id: assignedOfferId,
        config: bundleConfig,
        createdby: user,
        createdat: now,
        updatedby: user,
        updatedat: now
      }
    };
    const bundleResult = await client.request(CREATE_BUNDLE, bundleVariables);
    if (!bundleResult?.insert_bundles_one?.id) {
      throw new Error("Failed to create bundle");
    }
    log("addNewBundleToBundleSet", `Created bundle with ID ${bundleResult.insert_bundles_one.id}`);
    return bundleResult.insert_bundles_one;
  } catch (error) {
    log("addNewBundleToBundleSet", `Error: ${error.message}`);
    throw new Error(`Failed to add bundle to bundle set: ${error.message}`);
  }
}

// src/offers/offerCreation.ts
import { gql as gql2 } from "graphql-request";
var CREATE_OFFER = gql2`
  mutation createOffer($offer: offers_insert_input!) {
    insert_offers_one(object: $offer) {
      id
      bundle_id
      driver_id
      status
      expiration_timestamp
      createdat
      createdby
      updatedat
      updatedby
      driver {
        id
        user {
          display_name
        }
      }
    }
  }
`;
async function createOffer(client, bundleId, driverId, expirationTimestamp, options = {}) {
  const { status = OFFER_STATUSES.NEW, user = "system" } = options;
  if (!client) {
    throw new Error("GraphQL client is required to create an offer");
  }
  if (!bundleId) {
    throw new Error("Bundle ID is required");
  }
  if (!driverId) {
    throw new Error("Driver ID is required");
  }
  if (!expirationTimestamp) {
    throw new Error("Expiration timestamp is required");
  }
  if (!VALID_OFFER_STATUSES.includes(status)) {
    throw new Error(`Invalid offer status. Must be one of: ${VALID_OFFER_STATUSES.join(", ")}`);
  }
  const now = (/* @__PURE__ */ new Date()).toISOString();
  try {
    const variables = {
      offer: {
        bundle_id: bundleId,
        driver_id: driverId,
        status,
        expiration_timestamp: expirationTimestamp,
        createdby: user,
        createdat: now,
        updatedby: user,
        updatedat: now
      }
    };
    log("createOffer", `Creating offer from bundle ${bundleId} to driver ${driverId}`);
    const result = await client.request(CREATE_OFFER, variables);
    if (!result?.insert_offers_one?.id) {
      throw new Error("Failed to create offer");
    }
    log("createOffer", `Created offer with ID ${result.insert_offers_one.id}`);
    return result.insert_offers_one;
  } catch (error) {
    log("createOffer", `Error: ${error instanceof Error ? error.message : "Unknown error"}`);
    throw new Error(
      `Failed to create offer: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}
async function updateOfferStatus(client, offerId, status, options = {}) {
  const { user = "system" } = options;
  if (!client) {
    throw new Error("GraphQL client is required to update offer status");
  }
  if (!offerId) {
    throw new Error("Offer ID is required");
  }
  if (!status) {
    throw new Error("Status is required");
  }
  try {
    const mutation = gql2`
      mutation updateOfferStatusByOfferId($offerId: bigint!, $status: String!, $updatedBy: String!) {
        update_offers_by_pk(
          pk_columns: { id: $offerId }
          _set: { status: $status, updatedby: $updatedBy, updatedat: "now()" }
        ) {
          id
          status
          updatedby
          updatedat
        }
      }
    `;
    const variables = {
      offerId,
      status,
      updatedBy: user
    };
    const res = await client.request(mutation, variables);
    if (gqlErrors(res)) {
      log("updateOfferStatus", "Error updating offer status:", { errors: gqlErrors(res) });
      throw new Error("Error updating offer status");
    }
    return res.update_offers_by_pk;
  } catch (error) {
    log("updateOfferStatus", `Error: ${error instanceof Error ? error.message : "Unknown error"}`);
    throw new Error(
      `Error updating offer status: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

// src/offers/offerManagement.ts
import { gql as gql5 } from "graphql-request";

// src/drivers/driverManagement.ts
import { gql as gql3 } from "graphql-request";
var GET_DRIVER_BY_ID = gql3`
  query getDriverById($id: bigint!) {
    drivers_by_pk(id: $id) {
      id
      driver_app_version
      device_token
      user {
        display_name
      }
    }
  }
`;
async function getDriverById(client, driverId) {
  if (!client) {
    throw new Error("GraphQL client is required to get a driver by ID");
  }
  if (!driverId) {
    throw new Error("Driver ID is required to get a driver by ID");
  }
  try {
    const data = await client.request(GET_DRIVER_BY_ID, { id: driverId });
    return data.drivers_by_pk;
  } catch (error) {
    log("getDriverById", `Error getting driver ${driverId}: ${error.message}`);
    throw new Error(`Failed to get driver: ${error.message}`);
  }
}

// src/offers/promos/constants.ts
import {
  PROMO_STATUS,
  DRIVERSTOPROMOS_STATUS,
  COUNTING_STATUSES,
  ALGORITHM_KEY,
  PROMO_TEMPLATE_KEY,
  TEMPLATE_REGISTRY,
  CUSTOMER_TIER,
  DRIVER_TAX_CLASS,
  TEMPLATE_EXCLUDED_TAX_CLASSES,
  ELIGIBILITY_REASON as SDK_ELIGIBILITY_REASON
} from "@hopdrive/sdk-promos";
var ELIGIBILITY_REASON = Object.freeze({
  ...SDK_ELIGIBILITY_REASON,
  UNKNOWN: "unknown",
  EVALUATION_FAILED: "evaluation_failed"
});

// src/offers/promos/lookups.ts
import { gql as gql4 } from "graphql-request";
var GET_ACTIVE_DRIVERSTOPROMOS = gql4`
  query getActiveDriverstopromosForCandidates(
    $driverIds: [bigint!]!
    $active: String!
    $now: timestamptz!
  ) {
    driverstopromos(
      where: {
        driver_id: { _in: $driverIds }
        status: { _eq: $active }
        begin_time: { _lte: $now }
        _or: [{ end_time: { _is_null: true } }, { end_time: { _gte: $now } }]
      }
    ) {
      id
      driver_id
      promo_id
      status
      begin_time
      end_time
      promo {
        id
        promo_template_key
        status
        eligibility_algorithm_key
        completion_algorithm_key
        parameters
        scope_region_id
        scope_organization_id
        scope_customer_id
        begin_time
        end_time
      }
    }
  }
`;
var GET_PROMO_BY_ID = gql4`
  query getPromoById($id: bigint!) {
    promos_by_pk(id: $id) {
      id
      promo_template_key
      status
      eligibility_algorithm_key
      completion_algorithm_key
      parameters
      requires_acceptance
      max_acceptances
      accepted_count
      scope_region_id
      scope_organization_id
      scope_customer_id
      begin_time
      end_time
    }
  }
`;
var GET_DRIVERS_IN_REGIONS = gql4`
  query getDriversInRegions($regionIds: [bigint!]!) {
    drivers(
      where: {
        region_id: { _in: $regionIds }
        ${DISPATCHABLE_DRIVER_WHERE_CLAUSES}
      }
    ) {
      id
      status
      created_at
      max_drive_radius_mi
      region_id
      region {
        timezone
      }
      user {
        id
        display_name
      }
      certifications {
        certification_key
      }
      home_location {
        latitude
        longitude
      }
      businesshours {
        is_open
        day_of_week
        year
        month
        day
        open_time
        closed_time
      }
    }
  }
`;
var GET_DRIVERS_IN_REGION = gql4`
  query getDriversInRegion($regionId: bigint!) {
    drivers(
      where: {
        region_id: { _eq: $regionId }
        ${DISPATCHABLE_DRIVER_WHERE_CLAUSES}
      }
    ) {
      id
      status
      created_at
      max_drive_radius_mi
      region_id
      region {
        timezone
      }
      user {
        id
        display_name
      }
      certifications {
        certification_key
      }
      home_location {
        latitude
        longitude
      }
      businesshours {
        is_open
        day_of_week
        year
        month
        day
        open_time
        closed_time
      }
    }
  }
`;
var GET_REGION_ID_FOR_CUSTOMER = gql4`
  query getRegionIdForCustomer($customerId: bigint!) {
    customers_by_pk(id: $customerId) {
      id
      location {
        id
        region_id
      }
    }
  }
`;
var GET_REGION_IDS_FOR_ORGANIZATION = gql4`
  query getRegionIdsForOrganization($organizationId: bigint!) {
    customers(where: { organization_id: { _eq: $organizationId } }) {
      id
      location {
        region_id
      }
    }
  }
`;
var GET_BUNDLE_LINKED_PROMOS = gql4`
  query getLinkedPromosForBundle($bundleId: bigint!, $activeStatus: String!) {
    bundlestopromos(
      where: { bundle_id: { _eq: $bundleId }, promo: { status: { _eq: $activeStatus } } }
    ) {
      promo {
        id
        promo_template_key
        status
        eligibility_algorithm_key
        completion_algorithm_key
        parameters
        requires_acceptance
        max_acceptances
        accepted_count
        scope_region_id
        scope_organization_id
        scope_customer_id
        begin_time
        end_time
      }
    }
  }
`;
async function getActiveDriverstopromosForCandidates(client, driverIds, now = /* @__PURE__ */ new Date()) {
  if (!client) throw new Error("GraphQL client is required");
  if (!Array.isArray(driverIds) || driverIds.length === 0) return [];
  try {
    const variables = {
      driverIds,
      active: DRIVERSTOPROMOS_STATUS.ACTIVE,
      now: now.toISOString()
    };
    const res = await client.request(GET_ACTIVE_DRIVERSTOPROMOS, variables);
    if (gqlErrors(res)) {
      log("getActiveDriverstopromosForCandidates", "errors:", { errors: gqlErrors(res) });
      throw new Error("Error fetching active driverstopromos");
    }
    return res?.driverstopromos ?? [];
  } catch (error) {
    throw new Error(
      `Failed to get active driverstopromos: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}
async function getLinkedPromosForBundle(client, bundleId) {
  if (!client) throw new Error("GraphQL client is required");
  if (!bundleId) return [];
  try {
    const variables = { bundleId, activeStatus: PROMO_STATUS.ACTIVE };
    const res = await client.request(GET_BUNDLE_LINKED_PROMOS, variables);
    if (gqlErrors(res)) {
      log("getLinkedPromosForBundle", "errors:", { errors: gqlErrors(res) });
      throw new Error("Error fetching linked promos");
    }
    return (res?.bundlestopromos ?? []).map((row) => row.promo).filter(Boolean);
  } catch (error) {
    throw new Error(
      `Failed to get linked promos: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}
async function getPromoById(client, promoId) {
  if (!client) throw new Error("GraphQL client is required");
  if (!promoId) return null;
  try {
    const res = await client.request(GET_PROMO_BY_ID, { id: promoId });
    if (gqlErrors(res)) {
      log("getPromoById", "errors:", { errors: gqlErrors(res) });
      throw new Error("Error fetching promo");
    }
    return res?.promos_by_pk ?? null;
  } catch (error) {
    throw new Error(
      `Failed to get promo: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}
async function getDriversInRegion(client, regionId) {
  if (!client) throw new Error("GraphQL client is required");
  if (!regionId) return [];
  try {
    const res = await client.request(GET_DRIVERS_IN_REGION, { regionId });
    if (gqlErrors(res)) {
      log("getDriversInRegion", "errors:", { errors: gqlErrors(res) });
      throw new Error("Error fetching drivers in region");
    }
    return res?.drivers ?? [];
  } catch (error) {
    throw new Error(
      `Failed to get drivers in region: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}
async function getRegionIdForCustomer(client, customerId) {
  if (!client) throw new Error("GraphQL client is required");
  if (!customerId) return null;
  try {
    const res = await client.request(GET_REGION_ID_FOR_CUSTOMER, { customerId });
    if (gqlErrors(res)) {
      log("getRegionIdForCustomer", "errors:", { errors: gqlErrors(res) });
      throw new Error("Error fetching customer region");
    }
    return res?.customers_by_pk?.location?.region_id ?? null;
  } catch (error) {
    throw new Error(
      `Failed to get region for customer: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}
async function getRegionIdsForOrganization(client, organizationId) {
  if (!client) throw new Error("GraphQL client is required");
  if (!organizationId) return [];
  try {
    const res = await client.request(GET_REGION_IDS_FOR_ORGANIZATION, { organizationId });
    if (gqlErrors(res)) {
      log("getRegionIdsForOrganization", "errors:", { errors: gqlErrors(res) });
      throw new Error("Error fetching organization regions");
    }
    const customers = res?.customers ?? [];
    const ids = /* @__PURE__ */ new Set();
    for (const c of customers) {
      const rid = c?.location?.region_id;
      if (rid != null) ids.add(rid);
    }
    return Array.from(ids);
  } catch (error) {
    throw new Error(
      `Failed to get regions for organization: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}
async function getDriversInRegions(client, regionIds) {
  if (!client) throw new Error("GraphQL client is required");
  if (!Array.isArray(regionIds) || regionIds.length === 0) return [];
  try {
    const res = await client.request(GET_DRIVERS_IN_REGIONS, { regionIds });
    if (gqlErrors(res)) {
      log("getDriversInRegions", "errors:", { errors: gqlErrors(res) });
      throw new Error("Error fetching drivers in regions");
    }
    return res?.drivers ?? [];
  } catch (error) {
    throw new Error(
      `Failed to get drivers in regions: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}
var GET_DRIVER_WITH_ELIGIBILITY_FIELDS = gql4`
  query getDriverWithEligibilityFields($id: bigint!) {
    drivers_by_pk(id: $id) {
      id
      tax_class
      certifications {
        certification_key
      }
    }
  }
`;
async function getDriverWithEligibilityFields(client, driverId) {
  if (!client) throw new Error("GraphQL client is required");
  if (!driverId) throw new Error("driverId is required");
  try {
    const res = await client.request(GET_DRIVER_WITH_ELIGIBILITY_FIELDS, { id: driverId });
    return res?.drivers_by_pk ?? null;
  } catch (error) {
    log(
      "getDriverWithEligibilityFields",
      `Error fetching driver ${driverId}: ${error instanceof Error ? error.message : "Unknown error"}`
    );
    throw new Error(
      `Failed to fetch driver for eligibility check: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}
function groupByDriverId(rows) {
  const out = {};
  for (const row of rows ?? []) {
    if (row?.driver_id == null) continue;
    if (!out[row.driver_id]) out[row.driver_id] = [];
    out[row.driver_id].push(row);
  }
  return out;
}

// src/offers/promos/boost.ts
import { resolveBoostWeight } from "@hopdrive/sdk-promos";
var PROMO_COMMITMENT_MATCH_KEY = "promo_commitment_match";
var PROMO_COMMITMENT_BOOSTED_KEY = "promo_commitment_boosted";
var SCOPE_LEVELS = ["customer", "organization", "region"];
var promoIdField = (level) => `scope_${level}_id`;
var scopeIdSetField = (level) => `${level}_ids`;
var matchesAtLevel = (promo, scope, level) => {
  const id = promo[promoIdField(level)];
  if (id == null) return false;
  const set = scope[scopeIdSetField(level)];
  return Array.isArray(set) && set.includes(id);
};
function promoMatchesScope(promo, moveScope) {
  if (!promo || !moveScope) return false;
  return SCOPE_LEVELS.some((level) => matchesAtLevel(promo, moveScope, level));
}
function largestMatchingBoostWeight(instances, moveScope) {
  let weight = 0;
  let matched = false;
  for (const instance of instances) {
    const promo = instance?.promo;
    if (!promoMatchesScope(promo, moveScope)) continue;
    matched = true;
    const candidate = resolveBoostWeight(promo);
    if (candidate > weight) weight = candidate;
  }
  return { weight, matched };
}
function applyPromoCommitmentBoost(scoredDrivers, ctx) {
  if (!Array.isArray(scoredDrivers) || scoredDrivers.length === 0) return [];
  const instancesByDriver = ctx?.activeInstancesByDriver ?? {};
  const moveScope = ctx?.moveScope ?? null;
  let boostedAny = false;
  const next = scoredDrivers.map((driver) => {
    const instances = instancesByDriver[driver.driverId] ?? [];
    const { weight, matched } = largestMatchingBoostWeight(instances, moveScope);
    if (!matched) return driver;
    const factors = { ...driver.factors ?? {}, [PROMO_COMMITMENT_MATCH_KEY]: true };
    if (weight <= 0) return { ...driver, factors };
    boostedAny = true;
    return {
      ...driver,
      finalWeightedScore: Math.round((driver.finalWeightedScore + weight) * 100) / 100,
      factors: { ...factors, [PROMO_COMMITMENT_BOOSTED_KEY]: true }
    };
  });
  return boostedAny ? next.sort((a, b) => b.finalWeightedScore - a.finalWeightedScore) : next;
}

// src/offers/promos/index.ts
import { runEligibility as runEligibility2 } from "@hopdrive/sdk-promos";

// src/offers/promos/filterByPromoEligibility.ts
import { runEligibility } from "@hopdrive/sdk-promos";
var LOG_TAG = "filterByPromoEligibility";
var VALID_ELIGIBILITY_REASONS = new Set(Object.values(ELIGIBILITY_REASON));
function buildDriverContext(driver) {
  const certifications = Array.isArray(driver?.certifications) ? driver.certifications.map((c) => c?.certification_key).filter(Boolean) : [];
  return {
    id: driver?.id,
    certifications,
    tax_class: driver?.tax_class ?? null
  };
}
function normalizeReason(reason) {
  return reason && VALID_ELIGIBILITY_REASONS.has(reason) ? reason : ELIGIBILITY_REASON.UNKNOWN;
}
async function evaluatePromoEligibility(client, promos, candidates) {
  if (!Array.isArray(candidates) || candidates.length === 0) {
    return { eligible: [], ineligible: [] };
  }
  if (!Array.isArray(promos) || promos.length === 0) {
    return { eligible: [...candidates], ineligible: [] };
  }
  const settled = await Promise.allSettled(
    candidates.map(async (candidate) => {
      const driver = buildDriverContext(candidate);
      for (const promo of promos) {
        const result = await runEligibility(promo, { driver, client });
        if (!result?.eligible) {
          const reason = normalizeReason(result?.reason);
          log(LOG_TAG, `Driver ${candidate?.id} excluded by promo ${promo?.id}: ${reason}`);
          return { candidate, eligible: false, reason, promoId: promo?.id };
        }
      }
      return { candidate, eligible: true };
    })
  );
  const eligible = [];
  const ineligible = [];
  for (let index = 0; index < settled.length; index += 1) {
    const outcome = settled[index];
    let verdict;
    if (outcome.status === "fulfilled") {
      verdict = outcome.value;
    } else {
      const candidate = candidates[index];
      log(
        LOG_TAG,
        `Driver ${candidate?.id} eligibility evaluation failed: ${outcome.reason?.message ?? outcome.reason}`
      );
      verdict = {
        candidate,
        eligible: false,
        reason: ELIGIBILITY_REASON.EVALUATION_FAILED,
        promoId: null
      };
    }
    if (verdict.eligible) eligible.push(verdict.candidate);
    else ineligible.push({ candidate: verdict.candidate, reason: verdict.reason, promoId: verdict.promoId });
  }
  return { eligible, ineligible };
}
async function filterByPromoEligibility(client, bundleId, candidates) {
  if (!Array.isArray(candidates) || candidates.length === 0) {
    return { eligible: [], ineligibleVerdicts: [] };
  }
  if (!bundleId) return { eligible: candidates, ineligibleVerdicts: [] };
  const promos = await getLinkedPromosForBundle(client, bundleId);
  if (promos.length === 0) {
    log(LOG_TAG, `Bundle ${bundleId} has no linked promos \u2014 pass-through`);
    return { eligible: candidates, ineligibleVerdicts: [] };
  }
  const { eligible, ineligible } = await evaluatePromoEligibility(client, promos, candidates);
  log(
    LOG_TAG,
    `Bundle ${bundleId}: ${eligible.length}/${candidates.length} drivers passed eligibility for ${promos.length} linked promo(s)`
  );
  return { eligible, ineligibleVerdicts: ineligible };
}

// src/offers/promos/boostContext.ts
async function buildPromoBoostContext(client, candidateIds, moveScope) {
  if (!Array.isArray(candidateIds) || candidateIds.length === 0) return null;
  const rows = await getActiveDriverstopromosForCandidates(client, candidateIds);
  return {
    activeInstancesByDriver: groupByDriverId(rows),
    moveScope: moveScope ?? null
  };
}

// src/offers/promos/offerPayload.ts
var PAYLOAD_FIELD = Object.freeze({
  LINKED_PROMOS: "linked_promos"
});
function toLinkedPromoSummary(promo) {
  return {
    id: promo?.id ?? null,
    template_key: promo?.promo_template_key ?? null,
    parameters: promo?.parameters ?? null,
    begin_time: promo?.begin_time ?? null,
    end_time: promo?.end_time ?? null
  };
}
function extendPayloadDataWithPromos(payloadData, promos) {
  const base = payloadData ?? {};
  if (!Array.isArray(promos) || promos.length === 0) return base;
  return {
    ...base,
    [PAYLOAD_FIELD.LINKED_PROMOS]: JSON.stringify(promos.map(toLinkedPromoSummary))
  };
}
async function extendOfferPayloadWithPromo(client, offer, payload) {
  if (!client || !offer?.bundle_id || !payload) return payload;
  const promos = await getLinkedPromosForBundle(client, offer.bundle_id);
  if (promos.length === 0) return payload;
  return {
    ...payload,
    data: extendPayloadDataWithPromos(payload.data, promos)
  };
}

// src/offers/offerManagement.ts
var GET_OFFER_BY_ID = gql5`
  query getOfferById($offerId: bigint!) {
    offers_by_pk(id: $offerId) {
      id
      bundle_id
      driver_id
      status
      expiration_timestamp
      createdby
      createdat
      updatedby
      updatedat
      driver {
        id
        user {
          display_name
        }
      }
    }
  }
`;
var GET_OFFERS_FOR_BUNDLE = gql5`
  query getOffersForBundle($bundleId: bigint!) {
    offers(where: { bundle_id: { _eq: $bundleId } }) {
      id
      bundle_id
      driver_id
      expiration_timestamp
      status
      createdat
      updatedat
    }
  }
`;
var UPDATE_OFFER_STATUS = gql5`
  mutation updateOfferStatus($id: bigint!, $status: String!, $user: String!) {
    update_offers_by_pk(
      pk_columns: { id: $id }
      _set: { status: $status, updatedat: "now()", updatedby: $user }
    ) {
      id
      status
      updatedat
      updatedby
      driver {
        id
        user {
          display_name
        }
      }
    }
  }
`;
var UPDATE_ALL_OFFER_STATUSES_FOR_BUNDLE = gql5`
  mutation updateAllOfferStatusesForBundle($bundleId: bigint!, $status: String!, $user: String!) {
    update_offers(
      where: { bundle_id: { _eq: $bundleId } }
      _set: { status: $status, updatedat: "now()", updatedby: $user }
    ) {
      affected_rows
      returning {
        id
        status
        updatedat
        updatedby
        driver {
          id
          user {
            display_name
          }
        }
      }
    }
  }
`;
var EXPIRE_OLD_OFFERS_OF_BUNDLE = gql5`
  mutation expireOldOffersOfBundle(
    $bundleId: bigint!
    $currentTime: timestamptz!
    $status: String!
    $statusesToNotExpire: [String!]!
    $user: String!
  ) {
    update_offers(
      where: {
        expiration_timestamp: { _lte: $currentTime }
        bundle_id: { _eq: $bundleId }
        status: { _nin: $statusesToNotExpire }
      }
      _set: { status: $status, updatedby: $user, updatedat: "now()" }
    ) {
      affected_rows
      returning {
        id
        bundle_id
        expiration_timestamp
        status
        updatedat
        updatedby
      }
    }
  }
`;
var ASSIGN_OFFER_TO_BUNDLE = gql5`
  mutation assignOfferToBundle(
    $bundleId: bigint!
    $offerId: bigint!
    $user: String!
  ) {
    update_bundles_by_pk(
      pk_columns: {id: $bundleId},
      _set: {
        assigned_offer_id: $offerId,
        status: "${BUNDLE_STATUSES.DISPATCHED}",
        updatedat: "now()",
        updatedby: $user
      }
    ) {
      id
      assigned_offer_id
      status
      updatedat
      updatedby
    }
  }
`;
async function getOfferById(client, offerId) {
  if (!client) {
    throw new Error("GraphQL client is required to get an offer");
  }
  if (!offerId) {
    throw new Error("Offer ID is required");
  }
  try {
    const variables = { offerId };
    const result = await client.request(GET_OFFER_BY_ID, variables);
    return result.offers_by_pk;
  } catch (error) {
    log("getOfferById", `Error getting offer ${offerId}: ${error.message}`);
    throw new Error(`Failed to get offer: ${error.message}`);
  }
}
async function getOffersForBundle(client, bundleId) {
  if (!client) {
    throw new Error("GraphQL client is required to get offers");
  }
  if (!bundleId) {
    throw new Error("Bundle ID is required");
  }
  try {
    const variables = { bundleId };
    const result = await client.request(GET_OFFERS_FOR_BUNDLE, variables);
    return result.offers || [];
  } catch (error) {
    log("getOffersForBundle", `Error getting offers for bundle ${bundleId}: ${error.message}`);
    throw new Error(`Failed to get offers: ${error.message}`);
  }
}
async function updateOfferStatus2(client, offerId, status, options = {}) {
  const { user = "system" } = options;
  if (!client) {
    throw new Error("GraphQL client is required to update offer status");
  }
  if (!offerId) {
    throw new Error("Offer ID is required");
  }
  if (!VALID_OFFER_STATUSES.includes(status)) {
    throw new Error(`Invalid offer status. Must be one of: ${VALID_OFFER_STATUSES.join(", ")}`);
  }
  log("updateOfferStatus", `Updating offer ${offerId} status to ${status}`);
  try {
    const variables = {
      id: offerId,
      status,
      user
    };
    const result = await client.request(UPDATE_OFFER_STATUS, variables);
    if (!result?.update_offers_by_pk?.id) {
      throw new Error(`Failed to update offer status`);
    }
    return result.update_offers_by_pk;
  } catch (error) {
    log("updateOfferStatus", `Error updating offer ${offerId} status: ${error.message}`);
    throw new Error(`Failed to update offer status: ${error.message}`);
  }
}
async function assignOfferToBundle(client, bundleId, offerId, options = {}) {
  const { user = "system" } = options;
  if (!client) {
    throw new Error("GraphQL client is required to assign offer to bundle");
  }
  if (!bundleId) {
    throw new Error("Bundle ID is required");
  }
  if (!offerId) {
    throw new Error("Offer ID is required");
  }
  log("assignOfferToBundle", `Assigning offer ${offerId} to bundle ${bundleId}`);
  try {
    const currentOffer = await getOfferById(client, offerId);
    if (!currentOffer) {
      throw new Error(`Offer ${offerId} not found`);
    }
    const validStatesForAssignment = [OFFER_STATUSES.ACCEPTED];
    if (!validStatesForAssignment.includes(currentOffer.status)) {
      throw new Error(`Offer ${offerId} is in status '${currentOffer.status}' and cannot be assigned`);
    }
    const variables = {
      bundleId,
      offerId,
      user
    };
    const result = await client.request(ASSIGN_OFFER_TO_BUNDLE, variables);
    if (!result?.update_bundles_by_pk?.id) {
      throw new Error(`Failed to assign offer to bundle`);
    }
    if (result.update_bundles_by_pk.assigned_offer_id === offerId) {
      log("assignOfferToBundle", `Successfully assigned offer ${offerId} to bundle ${bundleId}`);
      await updateOfferStatus2(client, offerId, OFFER_STATUSES.ASSIGNED, { user });
      await rescindOtherOffers(client, bundleId, offerId, { user });
      return result.update_bundles_by_pk;
    } else {
      throw new OfferAlreadyAcceptedError(
        `Bundle ${bundleId} already has an assigned offer`,
        bundleId,
        offerId
      );
    }
  } catch (error) {
    if (error.message && error.message.includes("OFFER_ALREADY_ASSIGNED")) {
      throw new OfferAlreadyAcceptedError(
        `Bundle ${bundleId} already has an assigned offer`,
        bundleId,
        offerId
      );
    }
    log(
      "assignOfferToBundle",
      `Error assigning offer ${offerId} to bundle ${bundleId}: ${error.message}`
    );
    throw error;
  }
}
async function rescindOtherOffers(client, bundleId, acceptedOfferId, options = {}) {
  const { user = "system" } = options;
  if (!client) {
    throw new Error("GraphQL client is required to rescind offers");
  }
  if (!bundleId) {
    throw new Error("Bundle ID is required");
  }
  if (!acceptedOfferId) {
    throw new Error("Accepted offer ID is required");
  }
  try {
    const result = await client.request(GET_OFFERS_FOR_BUNDLE, { bundleId });
    const offers = result.offers || [];
    const offersToRescind = offers.filter(
      (offer) => offer.id !== acceptedOfferId && [OFFER_STATUSES.NEW, OFFER_STATUSES.OFFERED, OFFER_STATUSES.ACCEPTED].includes(offer.status)
    );
    log(
      "rescindOtherOffers",
      `Rescinding ${offersToRescind.length} other offers for bundle ${bundleId}`
    );
    let rescindedCount = 0;
    for (const offer of offersToRescind) {
      try {
        const result2 = await client.request(UPDATE_OFFER_STATUS, {
          id: offer.id,
          status: OFFER_STATUSES.RESCINDED,
          user
        });
        if (result2?.update_offers_by_pk?.id) {
          rescindedCount++;
        }
      } catch (err) {
        const error = err;
        log("rescindOtherOffers", `Failed to rescind offer ${offer.id}: ${error.message}`);
      }
    }
    return rescindedCount;
  } catch (error) {
    log("rescindOtherOffers", `Error: ${error.message}`);
    return 0;
  }
}
async function rescindAllOffers(client, bundleId, options = {}) {
  const { user = "system" } = options;
  if (!client) {
    throw new Error("GraphQL client is required to rescind offers");
  }
  if (!bundleId) {
    throw new Error("Bundle ID is required");
  }
  try {
    const result = await client.request(GET_OFFERS_FOR_BUNDLE, { bundleId });
    const offers = result.offers || [];
    log("rescindAllOffers", `Rescinding ${offers.length} offers for bundle ${bundleId}`);
    let rescindedCount = 0;
    try {
      const result2 = await client.request(UPDATE_ALL_OFFER_STATUSES_FOR_BUNDLE, {
        bundleId,
        status: OFFER_STATUSES.RESCINDED,
        user
      });
      if (result2?.update_offers?.affected_rows) {
        rescindedCount = result2.update_offers.affected_rows;
      }
    } catch (err) {
      const error = err;
      log("rescindAllOffers", `Failed to rescind bundle ${bundleId}: ${error.message}`);
    }
    return rescindedCount;
  } catch (error) {
    log("rescindAllOffers", `Error: ${error.message}`);
    return 0;
  }
}
async function acceptOffer(client, offerId, options = {}) {
  const { user = "system" } = options;
  return updateOfferStatus2(client, offerId, OFFER_STATUSES.ACCEPTED, { user });
}
async function declineOffer(client, offerId, options = {}) {
  const { user = "system" } = options;
  return updateOfferStatus2(client, offerId, OFFER_STATUSES.DECLINED, { user });
}
async function expireOffer(client, offerId, options = {}) {
  const { user = "system" } = options;
  return updateOfferStatus2(client, offerId, OFFER_STATUSES.EXPIRED, { user });
}
async function expireOldOffers(client, bundleId, currentTime = "now()", options = { user: "system" }) {
  if (!bundleId) {
    throw new Error("Bundle ID is required to expire old offers");
  }
  const { user = "system" } = options;
  return client.request(EXPIRE_OLD_OFFERS_OF_BUNDLE, {
    bundleId,
    currentTime,
    status: OFFER_STATUSES.EXPIRED,
    statusesToNotExpire: [
      OFFER_STATUSES.ASSIGNED,
      OFFER_STATUSES.ACCEPTED,
      OFFER_STATUSES.RESCINDED,
      OFFER_STATUSES.DECLINED
    ],
    user
  });
}
async function rescindOffer(client, offerId, options = {}) {
  const { user = "system" } = options;
  return updateOfferStatus2(client, offerId, OFFER_STATUSES.RESCINDED, { user });
}
async function areAllOffersResolved(client, bundleId) {
  if (!client) {
    throw new Error("GraphQL client is required to check offer resolution");
  }
  if (!bundleId) {
    throw new Error("Bundle ID is required");
  }
  try {
    const pendingStatuses = [OFFER_STATUSES.NEW, OFFER_STATUSES.OFFERED];
    const PENDING_OFFERS_QUERY = gql5`
      query pendingOffers($bundleId: bigint!, $pendingStatuses: [String!]) {
        offers(where: { bundle_id: { _eq: $bundleId }, status: { _in: $pendingStatuses } }) {
          id
          status
        }
      }
    `;
    const variables = {
      bundleId,
      pendingStatuses
    };
    const result = await client.request(PENDING_OFFERS_QUERY, variables);
    return !result.offers || result.offers.length === 0;
  } catch (error) {
    log("areAllOffersResolved", `Error checking pending offers: ${error.message}`);
    throw new Error(`Failed to check if all offers are resolved: ${error.message}`);
  }
}
function calculateBundleTotals(bundle, offerStatus, offerId) {
  let totalAP = 0;
  let totalDistance = 0;
  let totalRides = 0;
  let totalDrives = 0;
  let startLocation = null;
  let endLocation = null;
  let startLatLong = null;
  let endLatLong = null;
  let startTime = null;
  if (bundle?.offers?.[0]?.status) {
    offerStatus = bundle?.offers?.[0]?.status;
  }
  const scenarios = Array.isArray(bundle?.scenarios) ? bundle.scenarios : [];
  const allMoves = [];
  for (const scenarioObj of scenarios) {
    if (!scenarioObj?.scenario?.moves) continue;
    for (const moveObj of scenarioObj.scenario.moves) {
      if (moveObj?.move && moveObj.move.active === 1) {
        allMoves.push(moveObj.move);
      }
    }
  }
  allMoves.sort((a, b) => {
    const timeA = new Date(a.pickup_time || 0);
    const timeB = new Date(b.pickup_time || 0);
    return timeA.getTime() - timeB.getTime();
  });
  for (const move of allMoves) {
    for (const appayment of move?.appayments || []) {
      if (appayment?.amount) {
        totalAP += appayment.amount;
      }
    }
    if (move?.move_type === "ride") {
      totalRides++;
    } else if (move?.move_type === "drive") {
      totalDrives++;
    }
    if (move?.lane?.distance_miles) {
      totalDistance += move.lane.distance_miles;
    }
  }
  if (allMoves.length > 0 && allMoves[0]?.lane?.pickup?.address) {
    startLocation = allMoves[0].lane.pickup.address;
  }
  if (allMoves.length > 0 && allMoves[0]?.lane?.pickup?.latitude && allMoves[0]?.lane?.pickup?.longitude) {
    startLatLong = {
      latitude: allMoves[0].lane.pickup.latitude,
      longitude: allMoves[0].lane.pickup.longitude
    };
  }
  if (allMoves.length > 0 && allMoves[allMoves.length - 1]?.lane?.delivery?.address) {
    endLocation = allMoves[allMoves.length - 1].lane.delivery.address;
  }
  if (allMoves.length > 0 && allMoves[allMoves.length - 1]?.lane?.delivery?.latitude && allMoves[allMoves.length - 1]?.lane?.delivery?.longitude) {
    endLatLong = {
      latitude: allMoves[allMoves.length - 1].lane.delivery.latitude,
      longitude: allMoves[allMoves.length - 1].lane.delivery.longitude
    };
  }
  if (allMoves.length > 0 && allMoves[0]?.pickup_time) {
    startTime = allMoves[0].pickup_time;
  }
  return {
    totalAp: totalAP?.toFixed(2).toString(),
    totalDistance: totalDistance?.toFixed(2).toString(),
    totalRides: totalRides?.toString(),
    totalDrives: totalDrives?.toString(),
    startLocation,
    endLocation,
    startLat: startLatLong?.latitude?.toString(),
    startLong: startLatLong?.longitude?.toString(),
    endLat: endLatLong?.latitude?.toString(),
    endLong: endLatLong?.longitude?.toString(),
    startTime: startTime?.toString(),
    offerStatus: offerStatus || "",
    deeplink: `hopdrive://offers/${offerId}/offer`,
    handler: `bundleOffer`
  };
}
var buildOfferNotificationPayload = async (client, offer) => {
  const { bundle_id, driver_id, status, id } = offer;
  let bundle = null;
  let deviceToken = null;
  try {
    bundle = await getBundleWithMoveDetails(client, bundle_id);
  } catch (error) {
    logger.info(`[buildOfferNotificationPayload] Error getting bundle: ${error.message}`);
    throw new Error(`Failed to get bundle for offer notification: ${error.message}`);
  }
  try {
    const driver = await getDriverById(client, driver_id);
    deviceToken = driver.device_token;
  } catch (error) {
    log("buildOfferNotificationPayload", `Error getting device token: ${error.message}`);
    throw new Error(`Failed to get device token for offer notification: ${error.message}`);
  }
  const totals = calculateBundleTotals(bundle, status, id);
  const notificationBody = `Tap for details`;
  const notificationTitle = `New Offer`;
  const basePayload = {
    notificationBody,
    notificationTitle,
    deviceToken,
    data: totals
  };
  const payload = await extendOfferPayloadWithPromo(client, offer, basePayload);
  const hasLiveScenario = Array.isArray(bundle?.scenarios) && bundle.scenarios.length > 0;
  const hasLinkedPromo = payload?.data?.linked_promos != null;
  const isHusk = !hasLiveScenario && !hasLinkedPromo;
  logger.info("offer notification payload", payload);
  return { ...payload, isHusk };
};

// src/offers/flow/queries.ts
import { gql as gql6 } from "graphql-request";
async function getOffersForBundle2(client, bundleId) {
  if (!client) {
    throw new Error("GraphQL client is required to get offers for bundle");
  }
  if (!bundleId) {
    throw new Error("Bundle ID is required");
  }
  try {
    const query = gql6`
      query getBundleOfferStatuses($bundleId: bigint!) {
        offers(where: { bundle_id: { _eq: $bundleId } }) {
          id
          driver_id
          status
          createdat
        }
      }
    `;
    const variables = { bundleId };
    const res = await client.request(query, variables);
    if (gqlErrors(res)) {
      log("getOffersForBundle", `Error getting offers for bundle: ${JSON.stringify(gqlErrors(res) ?? null)}`);
      throw new Error("Error getting offers for bundle");
    }
    return res?.offers || [];
  } catch (error) {
    if (error instanceof Error) {
      log("getOffersForBundle", `Error: ${error.message}`);
      throw new Error(`Error getting offers for bundle: ${error.message}`);
    }
    throw error;
  }
}
async function getScenariosByIds(client, scenarioIds) {
  if (!client) {
    throw new Error("GraphQL client is required to get scenarios by IDs");
  }
  if (!scenarioIds || !Array.isArray(scenarioIds) || scenarioIds.length === 0) {
    throw new Error("Array of scenario IDs is required");
  }
  try {
    const query = gql6`
      query getScenariosByIds($scenarioIds: [bigint!]!) {
        scenarios(where: { id: { _in: $scenarioIds } }) {
          id
          scenario_key
          status
          config
          moves {
            move_id
            cardinality
            move {
              id
              active
              move_type
              pickup_time
              manual_flag
              usecase_key
              vehicle_gross_weight_lbs
              customer_id
              customer {
                organization_id
                tier
              }
              lane {
                origin_location_id
                pickup {
                  latitude
                  longitude
                  nickname
                }
                duration_sec
                destination_location_id
                delivery {
                  address
                  latitude
                  longitude
                  nickname
                }
              }
              scenariotomoves {
                scenario {
                  type {
                    key
                  }
                }
              }
            }
          }
        }
      }
    `;
    const variables = { scenarioIds };
    const res = await client.request(query, variables);
    if (gqlErrors(res)) {
      log("getScenariosByIds", `Error getting scenarios by IDs: ${JSON.stringify(gqlErrors(res) ?? null)}`);
      throw new Error("Error getting scenarios by IDs");
    }
    const scenarios = res.scenarios || [];
    return scenarios.map((scenario) => ({
      scenario_id: scenario.id,
      scenario
    }));
  } catch (error) {
    log("getScenariosByIds", `Error: ${error instanceof Error ? error.message : String(error)}`);
    throw new Error(
      `Error getting scenarios by IDs: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}

// src/offers/utils/drivers.ts
import { gql as gql7 } from "graphql-request";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc.js";
import timezone from "dayjs/plugin/timezone.js";
import { getDistance } from "geolib";
dayjs.extend(utc);
dayjs.extend(timezone);
function toDriverIdName(driver) {
  return { id: driver?.id, name: driver?.user?.display_name || "" };
}
async function getAllDriversInRadius(client, originLocation, radiusMiles) {
  if (!client) {
    throw new Error("GraphQL client is required");
  }
  if (!originLocation?.latitude || !originLocation?.longitude) {
    throw new Error("Origin location with valid latitude and longitude is required");
  }
  if (typeof radiusMiles !== "number" || radiusMiles <= 0) {
    throw new Error("Radius must be a positive number");
  }
  try {
    const driversInRadius = await findDriversInRadiusByStartingAndHomeLocation(
      client,
      originLocation,
      radiusMiles
    );
    return [...driversInRadius];
  } catch (error) {
    throw new Error(`Failed to get drivers in radius: ${error.message}`);
  }
}
async function findDriversInRadiusByStartingAndHomeLocation(client, location, radiusMiles, _options = {}) {
  if (!client) {
    throw new Error("GraphQL client is required to find drivers in radius");
  }
  if (!location || !location.latitude || !location.longitude) {
    throw new Error("Location with latitude and longitude is required");
  }
  if (!radiusMiles) {
    throw new Error("Radius in miles is required");
  }
  try {
    const query = gql7`
      query getDriversInRadiusByStartingLocation($centerPoint: geography!, $radius: Float!) {
        drivers(
          where: {
            _or: [
              { starting_location: { _st_d_within: { distance: $radius, from: $centerPoint } } }
              {
                home_location: {
                  geopoint: { _st_d_within: { distance: $radius, from: $centerPoint } }
                }
              }
            ]
            ${DISPATCHABLE_DRIVER_WHERE_CLAUSES}
          }
        ) {
          created_at
          id
          status
          tax_class
          max_drive_radius_mi
          region {
            timezone
          }
          user {
            id
            display_name
          }
          certifications {
            certification_key
          }
          home_location {
            latitude
            longitude
          }
          businesshours {
            is_open
            day_of_week
            year
            month
            day
            open_time
            closed_time
          }
        }
      }
    `;
    const variables = {
      centerPoint: {
        type: "Point",
        coordinates: [location.longitude, location.latitude]
      },
      radius: radiusMiles * 1609.344
      // Convert miles to meters
    };
    const res = await client.request(query, variables);
    log("getDriversByStartingLocation", "response", { res });
    const drivers = res.drivers;
    if (drivers.length === 0) {
      log(
        "findDriversInRadiusByStartingAndHomeLocation",
        "No drivers found in radius by starting and home location"
      );
      return [];
    }
    if (gqlErrors(res)) {
      log("findDriversInRadiusByStartingAndHomeLocation", "GraphQL errors", { errors: gqlErrors(res) });
      throw new Error("Error finding drivers in radius by starting location");
    }
    return drivers || [];
  } catch (error) {
    if (error instanceof Error) {
      log("findDriversInRadiusByStartingAndHomeLocation", `Error: ${error.message}`);
      throw new Error(
        `Error finding drivers in radius by starting and home location: ${error.message}`
      );
    }
    throw error;
  }
}
async function findDriversLatestLocation(client, driverIds, _options = {}) {
  if (!client) {
    throw new Error("GraphQL client is required to find drivers in radius");
  }
  if (!driverIds || !Array.isArray(driverIds) || driverIds.length === 0) {
    throw new Error("Driver IDs are required");
  }
  try {
    const query = gql7`
      query getDriversLatestLocation(
        $driverIds: [bigint!]!
        $startTime: timestamptz!
        $endTime: timestamptz!
      ) {
        driverlocations(
          where: { driver_id: { _in: $driverIds }, time: { _gte: $startTime, _lte: $endTime } }
          distinct_on: driver_id
          order_by: { driver_id: asc, time: desc }
        ) {
          time
          location
          driver_id
        }
      }
    `;
    const variables = {
      driverIds,
      startTime: dayjs().startOf("day").format(),
      endTime: dayjs().endOf("day").format()
    };
    const res = await client.request(query, variables);
    return res.driverlocations || [];
  } catch (error) {
    if (error instanceof Error) {
      log("findDriversLatestLocation", `Error: ${error.message}`);
      throw new Error(`Error finding drivers latest location: ${error.message}`);
    }
    throw error;
  }
}
async function getDriversWithNoConflictingMoves(client, drivers, startTime, totalDuration) {
  logger.info(
    `\u{1F50D} [getDriversWithNoConflictingMoves] Checking ${drivers.length} drivers for conflicts`
  );
  const bundleDate = dayjs(startTime).format("YYYY-MM-DD");
  const driverIds = drivers.map((driver) => driver.id);
  logger.info(`\u{1F4C5} [getDriversWithNoConflictingMoves] Checking plans for date: ${bundleDate}`);
  const existingPlans = await getExistingPlans(client, driverIds, bundleDate);
  logger.info(`\u{1F4CB} [getDriversWithNoConflictingMoves] Found ${existingPlans.length} existing plans`);
  const bundleStart = dayjs(startTime);
  const bundleEnd = bundleStart.add(totalDuration, "minutes");
  logger.info(
    `\u23F0 [getDriversWithNoConflictingMoves] Bundle window: ${bundleStart.format()} to ${bundleEnd.format()}`
  );
  const moveTimeWindow = `${bundleStart.format("YYYY-MM-DD HH:mm:ss")} to ${bundleEnd.format("YYYY-MM-DD HH:mm:ss")}`;
  const driverPlansMap = /* @__PURE__ */ new Map();
  existingPlans.forEach((plan) => {
    driverPlansMap.set(Number(plan.driver_id), plan);
  });
  logger.info(`\u{1F5C2}\uFE0F [getDriversWithNoConflictingMoves] Driver plans map:`, {
    driverPlansMap: Object.fromEntries(driverPlansMap)
  });
  const availableDrivers = [];
  const conflicts = [];
  drivers.forEach((driver) => {
    const driverPlan = existingPlans.find((plan) => plan.driver_id === driver.id);
    if (!driverPlan?.moves?.length) {
      availableDrivers.push(driver);
      return;
    }
    const conflictingMoves = [];
    const hasConflict = driverPlan.moves.some((move) => {
      const moveStart = dayjs(move.pickup_time);
      const moveEnd = moveStart.add(Math.floor((move.lane?.duration_sec || 0) / 60), "minutes");
      const conflict = !(bundleEnd.isBefore(moveStart) || moveEnd.isBefore(bundleStart));
      if (conflict) {
        conflictingMoves.push({
          moveId: move.id,
          moveStart: moveStart.format("YYYY-MM-DD HH:mm:ss"),
          moveEnd: moveEnd.format("YYYY-MM-DD HH:mm:ss")
        });
        logger.info(
          `\u26A0\uFE0F [getDriversWithNoConflictingMoves] Driver ${driver.id} has conflict: move ${moveStart.format()} to ${moveEnd.format()}`
        );
      }
      return conflict;
    });
    if (hasConflict) {
      const conflictDetails = conflictingMoves.map((cm) => `move ${cm.moveId} (${cm.moveStart}-${cm.moveEnd})`).join(", ");
      conflicts.push(
        `Driver ${driver.user?.display_name || driver.id} has conflicting ${conflictingMoves.length > 1 ? "moves" : "move"}: ${conflictDetails}`
      );
    } else {
      availableDrivers.push(driver);
    }
  });
  logger.info(
    `\u2705 [getDriversWithNoConflictingMoves] ${availableDrivers.length} drivers available (${drivers.length - availableDrivers.length} had conflicts)`
  );
  return {
    availableDrivers,
    driverPlansMap,
    moveTimeWindow,
    conflicts
  };
}
async function getDriversProjectedLocation(client, drivers, pickupTime, driverPlansMap) {
  if (!client) {
    throw new Error("GraphQL client is required");
  }
  if (!Array.isArray(drivers) || drivers.length === 0) {
    return [];
  }
  if (!pickupTime) {
    throw new Error("Pickup time is required");
  }
  try {
    const now = /* @__PURE__ */ new Date();
    const pickupDateTime = new Date(pickupTime);
    const oneHourFromNow = new Date(now.getTime() + 60 * 60 * 1e3);
    const oneHourBeforePickup = new Date(pickupDateTime.getTime() - 60 * 60 * 1e3);
    const driverIds = drivers.map((driver) => driver.id);
    let driverLocations = [];
    try {
      driverLocations = await findDriversLatestLocation(client, driverIds);
    } catch (error) {
      logger.warn("[getDriversProjectedLocation] GPS lookup failed; projecting without recent pings", {
        error: error?.message
      });
    }
    const locationMap = /* @__PURE__ */ new Map();
    driverLocations.forEach((loc) => {
      locationMap.set(loc.driver_id, {
        latitude: loc.location?.coordinates?.[1],
        longitude: loc.location?.coordinates?.[0],
        time: new Date(loc.time)
      });
    });
    const driverDetailsMap = /* @__PURE__ */ new Map();
    drivers.forEach((driver) => {
      driverDetailsMap.set(driver.id, driver);
    });
    const projectedLocations = [];
    for (const driver of drivers) {
      const driverId = driver.id;
      let projectedLocation = null;
      let method = "unknown";
      if (pickupDateTime <= oneHourFromNow) {
        const driverLocation = locationMap.get(driverId);
        if (driverLocation && driverLocation.time >= new Date(now.getTime() - 60 * 60 * 1e3)) {
          projectedLocation = {
            latitude: driverLocation.latitude,
            longitude: driverLocation.longitude
          };
          method = "recent_gps_ping";
        }
      }
      if (!projectedLocation) {
        const driverPlan = driverPlansMap.get(driverId);
        if (driverPlan?.moves?.length) {
          const relevantMoves = driverPlan.moves.filter((move) => {
            if (!move.pickup_time || !move.lane?.duration_sec) return false;
            const moveStart = new Date(move.pickup_time);
            const moveEnd = new Date(moveStart.getTime() + move.lane.duration_sec * 1e3);
            return moveEnd >= oneHourBeforePickup && moveEnd <= pickupDateTime;
          }).sort((a, b) => {
            const aEnd = new Date(new Date(a.pickup_time).getTime() + a.lane.duration_sec * 1e3);
            const bEnd = new Date(new Date(b.pickup_time).getTime() + b.lane.duration_sec * 1e3);
            return bEnd.getTime() - aEnd.getTime();
          });
          if (relevantMoves.length > 0) {
            const lastMove = relevantMoves[0];
            if (lastMove.lane?.delivery?.latitude && lastMove.lane?.delivery?.longitude) {
              projectedLocation = {
                latitude: lastMove.lane.delivery.latitude,
                longitude: lastMove.lane.delivery.longitude
              };
              method = "planned_move_delivery";
            }
          }
        }
      }
      if (!projectedLocation) {
        if (driver?.starting_location?.coordinates?.length && driver?.starting_location?.coordinates?.length >= 2) {
          projectedLocation = {
            latitude: driver.starting_location.coordinates[1],
            longitude: driver.starting_location.coordinates[0]
          };
          method = "starting_location";
        } else if (driver.home_location?.latitude && driver.home_location?.longitude) {
          projectedLocation = {
            latitude: driver.home_location.latitude,
            longitude: driver.home_location.longitude
          };
          method = "home_location";
        }
      }
      if (!projectedLocation) {
        projectedLocation = {
          latitude: 0,
          longitude: 0
        };
        method = "default_fallback";
      }
      projectedLocations.push({
        driverId,
        projectedLocation,
        method
      });
    }
    return projectedLocations;
  } catch (error) {
    logger.error("Error calculating projected locations:", error);
    return drivers.map((driver) => ({
      driverId: driver.id,
      projectedLocation: { latitude: 0, longitude: 0 },
      method: "error_fallback"
    }));
  }
}
function getDriversWithScheduledAvailability(drivers, startTime, totalDuration) {
  const conflicts = [];
  const availableDrivers = [];
  const startTimeFormatted = dayjs(startTime).format("YYYY-MM-DD HH:mm:ss");
  const endTimeFormatted = dayjs(startTime).add(totalDuration, "minutes").format("YYYY-MM-DD HH:mm:ss");
  const moveTime = `${startTimeFormatted} to ${endTimeFormatted}`;
  drivers.forEach((driver) => {
    if (!driver.businesshours || !Array.isArray(driver.businesshours)) {
      conflicts.push(
        `Driver ${driver.user?.display_name || driver.id} has no business hours configured`
      );
      return;
    }
    const driverTimezone = driver.region?.timezone;
    if (!driverTimezone) {
      conflicts.push(`Driver ${driver.user?.display_name || driver.id} has no timezone configured`);
      return;
    }
    const startDayOfWeek = dayjs(startTime).tz(driverTimezone).day();
    const bhYear = dayjs(startTime).tz(driverTimezone).year();
    const bhMonth = dayjs(startTime).tz(driverTimezone).month() + 1;
    const bhDay = dayjs(startTime).tz(driverTimezone).date();
    const dayBusinessHours = driver.businesshours.filter(
      (bh) => bh.is_open === true && (bh.day_of_week === startDayOfWeek || bh.year === bhYear && bh.month === bhMonth && bh.day === bhDay)
    );
    if (!dayBusinessHours || dayBusinessHours.length === 0) {
      conflicts.push(
        `Driver ${driver.user?.display_name || driver.id} is not available on ${dayjs(startTime).tz(driverTimezone).format("YYYY-MM-DD")}`
      );
      return;
    }
    const startTimeInTz = dayjs(startTime).tz(driverTimezone);
    const endTimeInTz = startTimeInTz.add(totalDuration, "minutes");
    const startTimeOnly = startTimeInTz.format("HH:mm:ss");
    const endTimeOnly = endTimeInTz.format("HH:mm:ss");
    const isAvailable = dayBusinessHours.some((businessHour) => {
      const openTime = businessHour.open_time || "00:00:00";
      const closeTime = businessHour.closed_time || "23:59:59";
      return startTimeOnly >= openTime && endTimeOnly <= closeTime;
    });
    if (isAvailable) {
      availableDrivers.push(driver);
    } else {
      const conflictDetails = dayBusinessHours.map((bh) => {
        const openTime = bh.open_time || "00:00:00";
        const closeTime = bh.closed_time || "23:59:59";
        if (startTimeOnly < openTime) {
          return `opens at ${openTime}`;
        } else if (endTimeOnly > closeTime) {
          return `closes at ${closeTime}`;
        } else {
          return `available ${openTime}-${closeTime}`;
        }
      }).join(", ");
      conflicts.push(
        `Driver ${driver.user?.display_name || driver.id} ${conflictDetails} (${driverTimezone})`
      );
    }
  });
  return {
    availableDrivers,
    moveTime,
    conflicts
  };
}
async function getExistingPlans(client, driverIds, bundleDate) {
  const query = gql7`
    query getExistingPlans($driverIds: [bigint!]!, $bundleDate: date!) {
      plans(where: { driver_id: { _in: $driverIds }, plan_date: { _eq: $bundleDate } }) {
        id
        driver_id
        plan_date
        moves(where: { active: { _eq: 1 } }) {
          id
          pickup_time
          lane {
            duration_sec
            pickup {
              latitude
              longitude
            }
            delivery {
              latitude
              longitude
            }
          }
          appayments {
            amount
            driver_id
          }
        }
      }
    }
  `;
  const variables = {
    driverIds,
    bundleDate
  };
  const res = await client.request(query, variables);
  return res.plans || [];
}
function filterDriversByDistance(projectedLocations, originLocation, maxDistanceMiles = 50) {
  if (!Array.isArray(projectedLocations) || projectedLocations.length === 0) {
    return [];
  }
  if (!originLocation || !originLocation.latitude || !originLocation.longitude) {
    throw new Error("Origin location with valid latitude and longitude is required");
  }
  if (typeof maxDistanceMiles !== "number" || maxDistanceMiles <= 0) {
    throw new Error("Maximum distance must be a positive number");
  }
  return projectedLocations.map((driverLocation) => {
    const { driverId, projectedLocation } = driverLocation;
    const distanceInMeters = getDistance(
      { latitude: originLocation.latitude, longitude: originLocation.longitude },
      { latitude: projectedLocation.latitude, longitude: projectedLocation.longitude }
    );
    const distanceToPickupMiles = distanceInMeters / 1609.344;
    return {
      driverId,
      projectedLocation,
      method: driverLocation.method,
      distanceToPickup: Math.round(distanceToPickupMiles * 100) / 100
      // Round to 2 decimal places
    };
  }).filter((driver) => driver.distanceToPickup <= maxDistanceMiles);
}

// src/offers/utils/requirements.ts
import { gql as gql8 } from "graphql-request";
async function getRequiredCertifications(client, usecaseKeys, customerIds, organizationIds, locationIds, scenarioKeys) {
  const query = gql8`
    query getRequiredCertifications(
      $usecaseKeys: [String!]!
      $customerIds: [bigint!]!
      $organizationIds: [bigint!]!
      $locationIds: [bigint!]!
      $scenarioKeys: [String!]!
    ) {
      usecasetocertifications(where: { usecase_key: { _in: $usecaseKeys } }) {
        certification_key
      }
      customerstocertifications(where: { customer_id: { _in: $customerIds } }) {
        certification_key
      }
      organizationstocertifications(where: { organization_id: { _in: $organizationIds } }) {
        certification_key
      }
      locationstocertifications(where: { location_id: { _in: $locationIds } }) {
        certification_key
      }
      scenariotypestocertifications(where: { scenario_key: { _in: $scenarioKeys } }) {
        certification_key
      }
    }
  `;
  const variables = {
    usecaseKeys,
    customerIds,
    organizationIds,
    locationIds,
    scenarioKeys
  };
  const res = await client.request(query, variables);
  if (gqlErrors(res)) {
    log("getRequiredCertifications", "Error getting required certifications:", { errors: gqlErrors(res) });
    throw new Error("Error getting required certifications");
  }
  const usecaseCerts = res?.usecasetocertifications?.map((cert) => cert?.certification_key) || [];
  const customerCerts = res?.customerstocertifications?.map((cert) => cert?.certification_key) || [];
  const organizationCerts = res?.organizationstocertifications?.map((cert) => cert?.certification_key) || [];
  const locationCerts = res?.locationstocertifications?.map((cert) => cert?.certification_key) || [];
  const scenarioCerts = res?.scenariotypestocertifications?.map((cert) => cert?.certification_key) || [];
  const requiredCertificationKeys = [
    .../* @__PURE__ */ new Set([
      ...usecaseCerts,
      ...customerCerts,
      ...organizationCerts,
      ...locationCerts,
      ...scenarioCerts
    ])
  ].filter(Boolean);
  return requiredCertificationKeys;
}
async function getDriverRequirements(client, allScenarioMoves, moveUsecaseKeys, moveCustomerIds, moveOrganizationIds, moveLocationIds, moveScenarioTypeKeys) {
  if (!client) {
    throw new Error("GraphQL client is required for getDriverRequirements");
  }
  const moves = Array.isArray(allScenarioMoves) ? allScenarioMoves : [];
  const usecaseKeys = Array.isArray(moveUsecaseKeys) ? moveUsecaseKeys : [];
  const customerIds = Array.isArray(moveCustomerIds) ? moveCustomerIds : [];
  const orgIds = Array.isArray(moveOrganizationIds) ? moveOrganizationIds : [];
  const locationIds = Array.isArray(moveLocationIds) ? moveLocationIds : [];
  const scenarioKeys = Array.isArray(moveScenarioTypeKeys) ? moveScenarioTypeKeys : [];
  try {
    const requiredCertifications = await getRequiredCertifications(
      client,
      usecaseKeys,
      customerIds,
      orgIds,
      locationIds,
      scenarioKeys
    );
    const certifications = Array.isArray(requiredCertifications) ? requiredCertifications : [];
    moves.forEach((move) => {
      if (!move) return;
      if (typeof move.vehicle_gross_weight_lbs === "number" && move.vehicle_gross_weight_lbs >= 19500 && !certifications.includes("dot-certified")) {
        certifications.push("dot-certified");
      }
      if (move.manual_flag === true && !certifications.includes("manual-certification")) {
        certifications.push("manual-certification");
      }
    });
    return certifications;
  } catch (error) {
    throw new Error(`Failed to get driver requirements: ${error.message}`);
  }
}
async function getBlacklistByCustomerAndOrganization(client, customerIds, organizationIds) {
  if (!client) {
    throw new Error("GraphQL client is required");
  }
  const validCustomerIds = Array.isArray(customerIds) ? customerIds.filter((id) => id != null) : [];
  const validOrganizationIds = Array.isArray(organizationIds) ? organizationIds.filter((id) => id != null) : [];
  if (validCustomerIds.length === 0 && validOrganizationIds.length === 0) {
    return [];
  }
  const query = gql8`
    query getConfigs($customerIds: [bigint!]!, $organizationIds: [bigint!]!) {
      customers(where: { id: { _in: $customerIds } }) {
        id
        config
      }
      organizations(where: { id: { _in: $organizationIds } }) {
        id
        config
      }
    }
  `;
  const variables = {
    customerIds: validCustomerIds,
    organizationIds: validOrganizationIds
  };
  try {
    const res = await client.request(query, variables);
    if (gqlErrors(res)) {
      log("getCustomerAndOrgConfigs", "Error getting configs:", { errors: gqlErrors(res) });
      throw new Error("Error getting customer and organization configs");
    }
    let blacklistedDriverIds = [];
    if (res.customers && Array.isArray(res.customers)) {
      res.customers.forEach((customer) => {
        if (customer?.config?.driver_blacklist && Array.isArray(customer.config.driver_blacklist)) {
          blacklistedDriverIds.push(...customer.config.driver_blacklist);
        }
      });
    }
    if (res.organizations && Array.isArray(res.organizations)) {
      res.organizations.forEach((organization) => {
        if (organization?.config?.driver_blacklist && Array.isArray(organization.config.driver_blacklist)) {
          blacklistedDriverIds.push(...organization.config.driver_blacklist);
        }
      });
    }
    blacklistedDriverIds = [...new Set(blacklistedDriverIds)];
    return blacklistedDriverIds;
  } catch (error) {
    throw new Error(`Failed to get blacklist by customer and organization: ${error.message}`);
  }
}
async function getBlacklistByLocation(client, locationPoint) {
  if (!client) {
    throw new Error("GraphQL client is required to find drivers in radius");
  }
  if (!locationPoint || !locationPoint.latitude || !locationPoint.longitude) {
    throw new Error("Location with latitude and longitude is required");
  }
  try {
    const query = gql8`
      query getDriverBlacklistByLocations($centerPoint: geography!, $radius: Float!) {
        driverblacklist(
          where: { point: { _st_d_within: { distance: $radius, from: $centerPoint } } }
        ) {
          driver_id
        }
      }
    `;
    const variables = {
      centerPoint: {
        type: "Point",
        coordinates: [locationPoint.longitude, locationPoint.latitude]
      },
      radius: 300
    };
    const res = await client.request(query, variables);
    if (gqlErrors(res)) {
      log("getBlacklistByLocation", "Error finding blacklisted drivers by location:", { errors: gqlErrors(res) });
      throw new Error("Error finding blacklisted drivers by location");
    }
    const drivers = res.driverblacklist || [];
    if (drivers.length === 0) {
      log("getBlacklistByLocation", "No blacklisted drivers found at location");
      return [];
    }
    const driverIds = drivers.map((driver) => driver.driver_id);
    return driverIds;
  } catch (error) {
    if (error instanceof Error) {
      log("getBlacklistByLocation", `Error: ${error.message}`);
      throw new Error(`Error finding blacklisted drivers by location: ${error.message}`);
    }
    throw error;
  }
}
async function getFullBlacklist(client, customerIds, organizationIds, allLocations) {
  const blacklistByCustomerAndOrganization = await getBlacklistByCustomerAndOrganization(
    client,
    customerIds,
    organizationIds
  );
  const blacklistByLocation = await Promise.all(
    allLocations.map((location) => getBlacklistByLocation(client, location))
  );
  return [...blacklistByCustomerAndOrganization, ...blacklistByLocation.flat()];
}

// src/offers/utils/scoring.ts
import { getDistance as getDistance2 } from "geolib";
function calculateProjectedEarnings(driverPlansMap) {
  const driverAppayments = [];
  driverPlansMap.forEach((plan, driverId) => {
    const numericDriverId = Number(driverId);
    const totalAppayments = plan?.moves?.reduce((sum, move) => {
      const moveAppayments = move.appayments?.reduce((moveSum, payment) => {
        return moveSum + (payment.amount || 0);
      }, 0) || 0;
      return sum + moveAppayments;
    }, 0) || 0;
    driverAppayments.push({
      driverId: numericDriverId,
      totalAppayments
    });
  });
  return driverAppayments;
}
function getEarningsScore(driverEarnings) {
  if (!Array.isArray(driverEarnings) || driverEarnings.length === 0) {
    return [];
  }
  if (driverEarnings.length === 1) {
    return [
      {
        driverId: driverEarnings[0].driverId,
        earningsScore: 10
      }
    ];
  }
  const sortedEarnings = [...driverEarnings].sort((a, b) => a.totalAppayments - b.totalAppayments);
  const allEarningsEqual = sortedEarnings.every(
    (driver) => driver.totalAppayments === sortedEarnings[0].totalAppayments
  );
  if (allEarningsEqual) {
    return driverEarnings.map((driver) => ({
      driverId: driver.driverId,
      earningsScore: 10
    }));
  }
  const uniqueEarnings = [...new Set(sortedEarnings.map((d) => d.totalAppayments))].sort(
    (a, b) => a - b
  );
  const earningsToRank = /* @__PURE__ */ new Map();
  uniqueEarnings.forEach((earnings, index) => {
    earningsToRank.set(earnings, index + 1);
  });
  const maxRank = uniqueEarnings.length;
  return driverEarnings.map((driver) => {
    const rank = earningsToRank.get(driver.totalAppayments);
    const score = maxRank === 1 ? 10 : 10 - (rank - 1) / (maxRank - 1) * 10;
    return {
      driverId: driver.driverId,
      earningsScore: Math.round(score * 100) / 100
      // Round to 2 decimal places
    };
  });
}
function getLocationsScores(projectedLocations, originLocation) {
  if (!Array.isArray(projectedLocations) || projectedLocations.length === 0) {
    return [];
  }
  if (!originLocation || !originLocation.latitude || !originLocation.longitude) {
    throw new Error("Origin location with valid latitude and longitude is required");
  }
  return projectedLocations.map((driverLocation) => {
    const { driverId, projectedLocation } = driverLocation;
    const distanceInMeters = getDistance2(
      { latitude: originLocation.latitude, longitude: originLocation.longitude },
      { latitude: projectedLocation.latitude, longitude: projectedLocation.longitude }
    );
    const distanceToPickupMiles = distanceInMeters / 1609.344;
    const rawScore = 10 - distanceToPickupMiles * (10 / DRIVER_SELECTION_ALGORITHM_VARIABLES[DRIVER_SELECTION_ALGORITHMS.V1_0_0].DISTANCE_CLIFF_MILES);
    const clampedScore = Math.max(0, Math.min(10, rawScore));
    const projectedLocationScore = Math.round(clampedScore);
    return {
      driverId,
      projectedLocationScore,
      distanceToPickup: Math.round(distanceToPickupMiles * 100) / 100
      // Round to 2 decimal places
    };
  });
}
function getIsAtPickupScore(projectedLocations, originLocation) {
  if (!Array.isArray(projectedLocations) || projectedLocations.length === 0) {
    return [];
  }
  if (!originLocation || !originLocation.latitude || !originLocation.longitude) {
    throw new Error("Origin location with valid latitude and longitude is required");
  }
  const minMeters = 0.1 * 1609.344;
  return projectedLocations.map((driverLocation) => {
    const { driverId, projectedLocation } = driverLocation;
    const distanceInMeters = getDistance2(
      { latitude: originLocation.latitude, longitude: originLocation.longitude },
      { latitude: projectedLocation.latitude, longitude: projectedLocation.longitude }
    );
    const isAtPickup = distanceInMeters <= minMeters;
    const isAtPickupScore = isAtPickup ? 10 : 0;
    return {
      driverId,
      isAtPickupScore,
      isAtPickup,
      distanceToPickup: Math.round(distanceInMeters * 100) / 100
      // Round to 2 decimal places
    };
  });
}
async function scoreDrivers_v1_0_0(client, drivers, pickupTime, originLocation, driverPlansMap) {
  if (!Array.isArray(drivers) || drivers.length === 0) {
    return [];
  }
  const driverEarnings = calculateProjectedEarnings(driverPlansMap);
  const allDriverEarnings = drivers.map((driver) => {
    const existingEarnings = driverEarnings.find((e) => Number(e.driverId) === Number(driver.id));
    return existingEarnings || { driverId: driver.id, totalAppayments: 0 };
  });
  const earningsScores = getEarningsScore(allDriverEarnings);
  const earningsScoreMap = /* @__PURE__ */ new Map();
  earningsScores.forEach((score) => {
    earningsScoreMap.set(Number(score.driverId), score.earningsScore);
  });
  const projectedLocations = await getDriversProjectedLocation(
    client,
    drivers,
    pickupTime,
    driverPlansMap
  );
  const projectedLocationScores = getLocationsScores(projectedLocations, originLocation);
  const isAtPickupScores = getIsAtPickupScore(projectedLocations, originLocation);
  const projectedLocationScoreMap = /* @__PURE__ */ new Map();
  projectedLocationScores.forEach((score) => {
    projectedLocationScoreMap.set(Number(score.driverId), score.projectedLocationScore);
  });
  const isAtPickupScoreMap = /* @__PURE__ */ new Map();
  isAtPickupScores.forEach((score) => {
    isAtPickupScoreMap.set(Number(score.driverId), score.isAtPickupScore);
  });
  return drivers.map((driver) => {
    const earningsScore = earningsScoreMap.get(Number(driver.id)) || 0;
    const projectedLocationScore = projectedLocationScoreMap.get(Number(driver.id)) || 0;
    const isAtPickupScore = isAtPickupScoreMap.get(Number(driver.id)) || 0;
    const factors = {
      earningsScore,
      projectedLocationScore,
      isAtPickupScore
    };
    const totalScore = earningsScore + projectedLocationScore + isAtPickupScore;
    return {
      driver,
      score: totalScore,
      factors
    };
  }).sort((a, b) => b.score - a.score);
}
async function getDriverWeightedScores(client, drivers, pickupTime, originLocation, driverPlansMap, driverSelectionAlgorithm, promoContext) {
  if (!drivers || !Array.isArray(drivers) || drivers.length === 0) {
    return {
      scoredDrivers: [],
      scoringDetails: []
    };
  }
  if (!pickupTime) {
    throw new Error("Pickup time is required to weigh driver scores");
  }
  if (!originLocation || !originLocation.latitude || !originLocation.longitude) {
    throw new Error(
      "Origin location with valid latitude and longitude is required to weigh driver scores. Promo-only bundles must call getDriverWeightedScoresWithoutOrigin instead."
    );
  }
  if (!driverPlansMap) {
    throw new Error("Driver plans are required to weigh driver scores");
  }
  const driverEarnings = calculateProjectedEarnings(driverPlansMap);
  const projectedLocations = await getDriversProjectedLocation(
    client,
    drivers,
    pickupTime,
    driverPlansMap
  );
  const projectedLocationScores = getLocationsScores(projectedLocations, originLocation);
  const isAtPickupScores = getIsAtPickupScore(projectedLocations, originLocation);
  const earningsMap = new Map(driverEarnings.map((d) => [d.driverId, d.totalAppayments]));
  const locationScoreMap = new Map(
    projectedLocationScores.map((d) => [
      d.driverId,
      { score: d.projectedLocationScore, distance: d.distanceToPickup }
    ])
  );
  const locationMethodMap = new Map(projectedLocations.map((d) => [d.driverId, d.method]));
  const pickupScoreMap = new Map(
    isAtPickupScores.map((d) => [d.driverId, { score: d.isAtPickupScore, isAtPickup: d.isAtPickup }])
  );
  let driversWithScoresWeighted;
  if (!driverSelectionAlgorithm || driverSelectionAlgorithm === "v1_0_0") {
    const driversWithScores = await scoreDrivers_v1_0_0(
      client,
      drivers,
      pickupTime,
      originLocation,
      driverPlansMap
    );
    driversWithScoresWeighted = weighDriverScores_v1_0_0(driversWithScores);
  } else {
    const driversWithScores = await scoreDrivers_v1_0_0(
      client,
      drivers,
      pickupTime,
      originLocation,
      driverPlansMap
    );
    driversWithScoresWeighted = weighDriverScores_v1_0_0(driversWithScores);
  }
  if (promoContext?.activeInstancesByDriver) {
    driversWithScoresWeighted = applyPromoCommitmentBoost(driversWithScoresWeighted, {
      activeInstancesByDriver: promoContext.activeInstancesByDriver,
      moveScope: promoContext.moveScope ?? null
    });
  }
  const scoringDetails = driversWithScoresWeighted.map((driver) => {
    const totalEarnings = earningsMap.get(driver.driverId) || 0;
    const locationData = locationScoreMap.get(driver.driverId) || { score: 0, distance: 0 };
    const pickupData = pickupScoreMap.get(driver.driverId) || { score: 0, isAtPickup: false };
    return {
      driverId: driver.driverId,
      driverName: driver.driverName,
      totalDayEarnings: totalEarnings,
      earningsScore: driver.earningsScore,
      distanceToPickupLocation: locationData.distance,
      /** 'default_fallback' / 'error_fallback' mean the distance above is not real. */
      projectedLocationMethod: locationMethodMap.get(driver.driverId) ?? "unknown",
      distanceScore: driver.projectedLocationScore,
      isAtPickupLocation: pickupData.isAtPickup,
      atPickupLocationScore: driver.isAtPickupScore,
      finalScore: driver.finalWeightedScore
    };
  });
  return {
    scoredDrivers: driversWithScoresWeighted,
    scoringDetails
  };
}
async function getDriverWeightedScoresWithoutOrigin(client, drivers, pickupTime, driverPlansMap, _driverSelectionAlgorithm) {
  if (!drivers || !Array.isArray(drivers) || drivers.length === 0) {
    return { scoredDrivers: [], scoringDetails: [] };
  }
  if (!pickupTime) {
    throw new Error("Pickup time is required to weigh driver scores");
  }
  if (!driverPlansMap) {
    throw new Error("Driver plans are required to weigh driver scores");
  }
  const driverEarnings = calculateProjectedEarnings(driverPlansMap);
  const allDriverEarnings = drivers.map((driver) => {
    const driverId = Number(driver.id);
    const existingEarnings = driverEarnings.find((e) => Number(e.driverId) === driverId);
    return existingEarnings || { driverId, totalAppayments: 0 };
  });
  const earningsScores = getEarningsScore(allDriverEarnings);
  const earningsMap = new Map(driverEarnings.map((d) => [d.driverId, d.totalAppayments]));
  const earningsScoreMap = new Map(earningsScores.map((d) => [d.driverId, d.earningsScore]));
  const driversWithScores = drivers.map((driver) => {
    const earningsScore = earningsScoreMap.get(Number(driver.id)) || 0;
    return {
      driver,
      score: earningsScore,
      factors: {
        earningsScore,
        projectedLocationScore: 0,
        isAtPickupScore: 0
      }
    };
  }).sort((a, b) => b.score - a.score);
  const driversWithScoresWeighted = weighDriverScores_v1_0_0(driversWithScores);
  const scoringDetails = driversWithScoresWeighted.map((driver) => {
    const totalEarnings = earningsMap.get(driver.driverId) || 0;
    return {
      driverId: driver.driverId,
      driverName: driver.driverName,
      totalDayEarnings: totalEarnings,
      earningsScore: driver.earningsScore,
      distanceToPickupLocation: 0,
      distanceScore: 0,
      isAtPickupLocation: false,
      atPickupLocationScore: 0,
      finalScore: driver.finalWeightedScore
    };
  });
  return {
    scoredDrivers: driversWithScoresWeighted,
    scoringDetails
  };
}
function weighDriverScores_v1_0_0(driversWithScores) {
  if (!Array.isArray(driversWithScores) || driversWithScores.length === 0) {
    return [];
  }
  const {
    PROJECTED_EARNINGS_WEIGHT,
    PROJECTED_DISTANCE_TO_PICKUP_WEIGHT,
    PROJECTED_IS_AT_PICKUP_WEIGHT
  } = DRIVER_SELECTION_ALGORITHM_VARIABLES[DRIVER_SELECTION_ALGORITHMS.V1_0_0];
  const totalWeight = PROJECTED_EARNINGS_WEIGHT + PROJECTED_DISTANCE_TO_PICKUP_WEIGHT + PROJECTED_IS_AT_PICKUP_WEIGHT;
  const weightedDrivers = driversWithScores.map((driverData) => {
    const { driver, factors } = driverData;
    const earningsScore = factors.earningsScore || 0;
    const projectedLocationScore = factors.projectedLocationScore || 0;
    const isAtPickupScore = factors.isAtPickupScore || 0;
    const weightedEarningsScore = earningsScore * PROJECTED_EARNINGS_WEIGHT;
    const weightedLocationScore = projectedLocationScore * PROJECTED_DISTANCE_TO_PICKUP_WEIGHT;
    const weightedIsAtPickupScore = isAtPickupScore * PROJECTED_IS_AT_PICKUP_WEIGHT;
    const finalWeightedScore = (weightedEarningsScore + weightedLocationScore + weightedIsAtPickupScore) / totalWeight;
    return {
      driverId: driver.id,
      driverName: driver.user?.display_name || `Driver ${driver.id}`,
      earningsScore: Math.round(earningsScore * 100) / 100,
      // Round to 2 decimal places
      projectedLocationScore: Math.round(projectedLocationScore * 100) / 100,
      isAtPickupScore: Math.round(isAtPickupScore * 100) / 100,
      weightedEarningsScore: Math.round(weightedEarningsScore * 100) / 100,
      weightedLocationScore: Math.round(weightedLocationScore * 100) / 100,
      weightedIsAtPickupScore: Math.round(weightedIsAtPickupScore * 100) / 100,
      finalWeightedScore: Math.round(finalWeightedScore * 100) / 100
    };
  });
  return weightedDrivers.sort((a, b) => b.finalWeightedScore - a.finalWeightedScore);
}

// src/offers/driverPool/shared.ts
function getTotalDurationOfMoves(moves) {
  const totalDuration = moves.reduce((sum, move) => sum + (move?.lane?.duration_sec || 0), 0);
  return Math.floor(totalDuration / 60);
}
function buildEmptyDriverPoolResult(reasoningOverrides = {}) {
  return {
    driverPool: [],
    reasoning: {
      inRadius: [],
      certifications: { requiredCertifications: [], drivers: [] },
      blacklist: { blacklistedDrivers: [], nonBlacklistedDrivers: [] },
      promoEligibility: { eligibleDrivers: [], ineligibleDrivers: [] },
      scheduleAvailability: { availableDrivers: [], unavailableDrivers: [] },
      planAvailability: { availableDrivers: [], unavailableDrivers: [] },
      withinMaxDistance: { availableDrivers: [], unavailableDrivers: [] },
      driversWithScoresWeighted: [],
      scoringDetails: [],
      ...reasoningOverrides
    }
  };
}

// src/offers/driverPool/fromScenarios.ts
var isOfferableMove = (move) => move != null && move.active === 1 && move.move_type === MOVE_TYPES.DRIVE;
function getMoveLocationsAndOriginInfoFromBundle(bundle) {
  try {
    if (!bundle || !bundle.scenarios || !bundle.scenarios.length) {
      return { originLocation: null, allLocations: [], startTime: null };
    }
    const allLocations = [];
    for (const scenarioObj of bundle.scenarios) {
      const scenario = scenarioObj.scenario;
      if (!scenario || !scenario.moves || !scenario.moves.length) continue;
      for (const moveObj of scenario.moves) {
        const move = moveObj.move;
        if (!isOfferableMove(move) || !move.lane) continue;
        if (move.lane.pickup && move.lane.pickup.latitude && move.lane.pickup.longitude) {
          allLocations.push({
            type: "pickup",
            moveId: move.id,
            latitude: move.lane.pickup.latitude,
            longitude: move.lane.pickup.longitude
          });
        }
        if (move.lane.delivery && move.lane.delivery.latitude && move.lane.delivery.longitude) {
          allLocations.push({
            type: "delivery",
            moveId: move.id,
            latitude: move.lane.delivery.latitude,
            longitude: move.lane.delivery.longitude
          });
        }
      }
    }
    let originLocation = null;
    let startTime = null;
    for (const scenarioObj of bundle.scenarios) {
      const offerableMoves = (scenarioObj?.scenario?.moves || []).map((m) => m?.move).filter(isOfferableMove);
      const firstMove = offerableMoves[0];
      if (firstMove?.lane?.pickup?.latitude != null && firstMove?.lane?.pickup?.longitude != null) {
        originLocation = {
          latitude: firstMove.lane.pickup.latitude,
          longitude: firstMove.lane.pickup.longitude
        };
        startTime = firstMove.pickup_time;
        break;
      }
    }
    return { originLocation, allLocations, startTime };
  } catch (error) {
    log(
      "getMoveLocationsAndOriginInfoFromBundle",
      `Error: ${error instanceof Error ? error.message : String(error)}`
    );
    return { originLocation: null, allLocations: [], startTime: null };
  }
}
async function processDriverPoolFromScenarios(client, scenarios, bundleConfig, round, driverSelectionAlgorithm = "v1_0_0", bundleId = null) {
  if (!client) throw new Error("GraphQL client is required to get driver pool");
  if (!scenarios || !Array.isArray(scenarios)) {
    throw new Error("Scenarios array is required to get driver pool");
  }
  if (!bundleConfig) throw new Error("Bundle config is required to get driver pool");
  if (!round) throw new Error("Round is required to get driver pool");
  logger.info(
    `[processDriverPoolFromScenarios] Starting driver pool selection with ${scenarios.length} scenarios`
  );
  const offerStrategy = getOfferStrategy(bundleConfig);
  const strategyParams = DEFAULT_STRATEGY_PARAMS[offerStrategy];
  const radiusMiles = round?.radiusMiles || (strategyParams && "radiusMiles" in strategyParams ? strategyParams.radiusMiles : 10);
  logger.info(`[processDriverPoolFromScenarios] Search radius: ${radiusMiles} miles`);
  try {
    const allScenarioMoves = scenarios.flatMap((scenario) => scenario?.scenario?.moves || []).map((moveObj) => moveObj?.move).filter(isOfferableMove);
    logger.info(
      `[processDriverPoolFromScenarios] Found ${allScenarioMoves.length} moves in scenarios`
    );
    const moveScenarioTypeKeys = allScenarioMoves.map((move) => move?.scenariotomoves?.[0]?.scenario?.type?.key).filter(Boolean) || [];
    const moveLocationsAndOriginInfo = getMoveLocationsAndOriginInfoFromBundle({ scenarios });
    const moveUsecasekeys = allScenarioMoves.map((move) => move?.usecase_key).filter(Boolean) || [];
    const moveCustomerIds = allScenarioMoves.map((move) => move?.customer_id).filter(Boolean) || [];
    const moveLocationIds = [
      ...new Set(
        [
          ...allScenarioMoves.map((move) => move?.lane?.origin_location_id),
          ...allScenarioMoves.map((move) => move?.lane?.destination_location_id)
        ].filter(Boolean)
      )
    ];
    const moveOrganizationIds = allScenarioMoves.map((move) => move?.customer?.organization_id).filter(Boolean) || [];
    const originLocation = moveLocationsAndOriginInfo.originLocation;
    const allLocations = moveLocationsAndOriginInfo.allLocations;
    const startTime = moveLocationsAndOriginInfo.startTime;
    const totalDuration = getTotalDurationOfMoves(allScenarioMoves);
    logger.info(
      `[processDriverPoolFromScenarios] Bundle start time: ${startTime}, duration: ${totalDuration} minutes`
    );
    if (!originLocation) {
      throw new Error("Could not determine origin location for scenarios");
    }
    logger.info(`[processDriverPoolFromScenarios] Step 1: Finding drivers in radius...`);
    const driversInRadius = await getAllDriversInRadius(client, originLocation, radiusMiles);
    logger.info(
      `[processDriverPoolFromScenarios] Found ${driversInRadius.length} drivers in radius`
    );
    if (driversInRadius.length === 0) {
      return buildEmptyDriverPoolResult();
    }
    const inRadiusReasoning = driversInRadius.map(toDriverIdName);
    logger.info(`[processDriverPoolFromScenarios] Step 2: Checking certifications...`);
    const requiredCertifications = await getDriverRequirements(
      client,
      allScenarioMoves,
      moveUsecasekeys,
      moveCustomerIds,
      moveOrganizationIds,
      moveLocationIds,
      moveScenarioTypeKeys
    );
    logger.info(
      `[processDriverPoolFromScenarios] Required certifications:`,
      { requiredCertifications }
    );
    const driversWithRequiredCertifications = driversInRadius.filter((driver) => {
      if (!driver.certifications || !Array.isArray(driver.certifications)) {
        return false;
      }
      return requiredCertifications.every(
        (cert) => driver.certifications.some((c) => c?.certification_key === cert)
      );
    });
    const certifiedDriverIds = new Set(driversWithRequiredCertifications.map((d) => d.id));
    const driversWithoutRequiredCertifications = driversInRadius.filter(
      (driver) => !certifiedDriverIds.has(driver.id)
    );
    logger.info(
      `[processDriverPoolFromScenarios] ${driversWithRequiredCertifications.length} drivers have required certifications`
    );
    if (driversWithRequiredCertifications.length === 0) {
      return buildEmptyDriverPoolResult({
        inRadius: inRadiusReasoning,
        certifications: {
          requiredCertifications,
          drivers: [],
          driversWithoutRequiredCertifications: driversWithoutRequiredCertifications.map(toDriverIdName)
        }
      });
    }
    const certifiedReasoning = driversWithRequiredCertifications.map(toDriverIdName);
    logger.info(`[processDriverPoolFromScenarios] Step 3: Checking blacklists...`);
    const fullBlacklist = await getFullBlacklist(
      client,
      moveCustomerIds,
      moveOrganizationIds,
      allLocations
    );
    const blacklistSet = new Set(fullBlacklist);
    const nonBlacklistedDrivers = driversWithRequiredCertifications.filter(
      (driver) => !blacklistSet.has(driver.id)
    );
    const blacklistedDrivers = driversWithRequiredCertifications.filter(
      (driver) => blacklistSet.has(driver.id)
    );
    logger.info(
      `[processDriverPoolFromScenarios] ${nonBlacklistedDrivers.length} drivers not blacklisted`
    );
    const blacklistedReasoning = blacklistedDrivers.map(toDriverIdName);
    const nonBlacklistedReasoning = nonBlacklistedDrivers.map(toDriverIdName);
    if (nonBlacklistedDrivers.length === 0) {
      return buildEmptyDriverPoolResult({
        inRadius: inRadiusReasoning,
        certifications: { requiredCertifications, drivers: certifiedReasoning },
        blacklist: { blacklistedDrivers: blacklistedReasoning, nonBlacklistedDrivers: [] }
      });
    }
    logger.info(
      `[processDriverPoolFromScenarios] Step 3.5: Filtering by linked-promo eligibility...`
    );
    const { eligible: promoEligibleDrivers, ineligibleVerdicts: promoIneligibleVerdicts } = bundleId ? await filterByPromoEligibility(client, bundleId, nonBlacklistedDrivers) : { eligible: nonBlacklistedDrivers, ineligibleVerdicts: [] };
    logger.info(
      `[processDriverPoolFromScenarios] ${promoEligibleDrivers.length}/${nonBlacklistedDrivers.length} drivers passed promo eligibility`
    );
    const promoEligibilityReasoning = {
      eligibleDrivers: promoEligibleDrivers.map(toDriverIdName),
      ineligibleDrivers: promoIneligibleVerdicts.map((v) => ({
        ...toDriverIdName(v.candidate),
        reason: v.reason
      }))
    };
    logger.info(
      `[processDriverPoolFromScenarios] Step 4: Checking drivers' business hours availability...`
    );
    const scheduleAvailabilityResults = startTime ? getDriversWithScheduledAvailability(
      promoEligibleDrivers,
      new Date(startTime),
      totalDuration
    ) : { availableDrivers: promoEligibleDrivers, moveTime: "", conflicts: [] };
    const driversWithScheduledAvailability = scheduleAvailabilityResults?.availableDrivers ?? [];
    const scheduledAvailableIds = new Set(driversWithScheduledAvailability.map((d) => d.id));
    const driversWithoutScheduledAvailability = promoEligibleDrivers.filter(
      (driver) => !scheduledAvailableIds.has(driver.id)
    );
    logger.info(
      `[processDriverPoolFromScenarios] ${driversWithScheduledAvailability.length} drivers with scheduled availability`
    );
    const scheduleAvailableReasoning = driversWithScheduledAvailability.map(toDriverIdName);
    const scheduleUnavailableReasoning = driversWithoutScheduledAvailability.map(toDriverIdName);
    if (driversWithScheduledAvailability.length === 0) {
      return buildEmptyDriverPoolResult({
        inRadius: inRadiusReasoning,
        certifications: { requiredCertifications, drivers: certifiedReasoning },
        blacklist: {
          blacklistedDrivers: blacklistedReasoning,
          nonBlacklistedDrivers: nonBlacklistedReasoning
        },
        promoEligibility: promoEligibilityReasoning,
        scheduleAvailability: {
          availableDrivers: [],
          unavailableDrivers: scheduleUnavailableReasoning
        }
      });
    }
    logger.info(
      `[processDriverPoolFromScenarios] Step 5: Checking for scheduling conflicts...`
    );
    const planConflictResults = await getDriversWithNoConflictingMoves(
      client,
      driversWithScheduledAvailability,
      startTime || "",
      totalDuration
    );
    const driversWithNoConflictingMoves = planConflictResults && planConflictResults?.availableDrivers ? planConflictResults : { availableDrivers: driversWithScheduledAvailability, driverPlansMap: /* @__PURE__ */ new Map() };
    const noConflictAvailableIds = new Set(
      driversWithNoConflictingMoves.availableDrivers.map((d) => d.id)
    );
    const driversWithoutConflictingMoves = driversWithScheduledAvailability.filter(
      (driver) => !noConflictAvailableIds.has(driver.id)
    );
    logger.info(
      `[processDriverPoolFromScenarios] Eligible pool: ${driversWithNoConflictingMoves.availableDrivers.length} drivers with no conflicts`
    );
    const planAvailableReasoning = driversWithNoConflictingMoves.availableDrivers.map(toDriverIdName);
    const planUnavailableReasoning = driversWithoutConflictingMoves.map(toDriverIdName);
    if (driversWithNoConflictingMoves.availableDrivers.length === 0) {
      return buildEmptyDriverPoolResult({
        inRadius: inRadiusReasoning,
        certifications: { requiredCertifications, drivers: certifiedReasoning },
        blacklist: {
          blacklistedDrivers: blacklistedReasoning,
          nonBlacklistedDrivers: nonBlacklistedReasoning
        },
        promoEligibility: promoEligibilityReasoning,
        scheduleAvailability: {
          availableDrivers: scheduleAvailableReasoning,
          unavailableDrivers: scheduleUnavailableReasoning
        },
        planAvailability: {
          availableDrivers: [],
          unavailableDrivers: planUnavailableReasoning
        }
      });
    }
    logger.info(
      `[processDriverPoolFromScenarios] Step 6: Filtering drivers by distance from pickup...`
    );
    const projectedLocations = await getDriversProjectedLocation(
      client,
      driversWithNoConflictingMoves.availableDrivers,
      startTime || "",
      driversWithNoConflictingMoves.driverPlansMap
    );
    const maxDistanceMiles = DEFAULT_STRATEGY_PARAMS[offerStrategy]?.maxOfferRadiusMiles || 50;
    const driversWithinDistance = filterDriversByDistance(
      projectedLocations,
      originLocation,
      maxDistanceMiles
    );
    const driversWithinDistanceIds = new Set(driversWithinDistance.map((d) => d.driverId));
    const driversOutsideDistance = projectedLocations.filter(
      (driver) => !driversWithinDistanceIds.has(driver.driverId)
    );
    logger.info(
      `[processDriverPoolFromScenarios] ${driversWithinDistance.length} drivers within ${maxDistanceMiles} miles of pickup`
    );
    const driverById = new Map(
      driversWithNoConflictingMoves.availableDrivers.map((d) => [d.id, d])
    );
    const projectedToIdName = (projected) => ({
      id: projected.driverId,
      name: driverById.get(projected.driverId)?.user?.display_name || ""
    });
    const withinDistanceReasoning = driversWithinDistance.map(projectedToIdName);
    const outsideDistanceReasoning = driversOutsideDistance.map(projectedToIdName);
    if (driversWithinDistance.length === 0) {
      return buildEmptyDriverPoolResult({
        inRadius: inRadiusReasoning,
        certifications: { requiredCertifications, drivers: certifiedReasoning },
        blacklist: {
          blacklistedDrivers: blacklistedReasoning,
          nonBlacklistedDrivers: nonBlacklistedReasoning
        },
        promoEligibility: promoEligibilityReasoning,
        scheduleAvailability: {
          availableDrivers: scheduleAvailableReasoning,
          unavailableDrivers: scheduleUnavailableReasoning
        },
        planAvailability: {
          availableDrivers: planAvailableReasoning,
          unavailableDrivers: planUnavailableReasoning
        },
        withinMaxDistance: {
          availableDrivers: withinDistanceReasoning,
          unavailableDrivers: outsideDistanceReasoning
        }
      });
    }
    const filteredDrivers = driversWithNoConflictingMoves.availableDrivers.filter(
      (driver) => driversWithinDistanceIds.has(driver.id)
    );
    logger.info(
      `[processDriverPoolFromScenarios] Step 7: Checking for duplicate final weighted scores...`
    );
    const promoScopeableMoves = allScenarioMoves.filter(
      (move) => move?.customer?.tier !== CUSTOMER_TIER.LITE
    );
    const uniqueIds = (key) => Array.from(
      new Set(promoScopeableMoves.map((move) => key(move)).filter((id) => id != null))
    );
    const moveScope = {
      customer_ids: uniqueIds((move) => move?.customer_id),
      organization_ids: uniqueIds((move) => move?.customer?.organization_id),
      region_ids: uniqueIds((move) => move?.lane?.pickup?.region_id)
    };
    const promoBoostContext = await buildPromoBoostContext(
      client,
      filteredDrivers.map((d) => d.id),
      moveScope
    );
    const scoringResults = await getDriverWeightedScores(
      client,
      filteredDrivers,
      startTime || "",
      originLocation,
      driversWithNoConflictingMoves.driverPlansMap,
      driverSelectionAlgorithm,
      promoBoostContext
    );
    const driversWithScoresWeighted = scoringResults.scoredDrivers;
    const scoringDetails = scoringResults.scoringDetails;
    const uniqueFinalScores = new Set(
      driversWithScoresWeighted.map((driver) => driver.finalWeightedScore)
    );
    if (uniqueFinalScores.size !== driversWithScoresWeighted.length) {
      logger.info(
        `[processDriverPoolFromScenarios] Duplicate final weighted scores detected: Using tenure tiebreaker`
      );
      driversWithScoresWeighted.sort((a, b) => {
        if (b.finalWeightedScore !== a.finalWeightedScore) {
          return b.finalWeightedScore - a.finalWeightedScore;
        }
        const driverA = driverById.get(a.driverId);
        const driverB = driverById.get(b.driverId);
        if (driverA?.created_at && driverB?.created_at) {
          return new Date(driverA.created_at).getTime() - new Date(driverB.created_at).getTime();
        }
        return 0;
      });
    }
    logger.info(
      `[processDriverPoolFromScenarios] Final sorted driver array:`,
      { driversWithScoresWeighted }
    );
    const sortedDrivers = driversWithScoresWeighted.map((weightedDriver) => driverById.get(weightedDriver.driverId)).filter((driver) => driver != null);
    return {
      driverPool: sortedDrivers,
      reasoning: {
        inRadius: inRadiusReasoning,
        certifications: { requiredCertifications, drivers: certifiedReasoning },
        blacklist: {
          blacklistedDrivers: blacklistedReasoning,
          nonBlacklistedDrivers: nonBlacklistedReasoning
        },
        promoEligibility: promoEligibilityReasoning,
        scheduleAvailability: {
          availableDrivers: scheduleAvailableReasoning,
          unavailableDrivers: scheduleUnavailableReasoning
        },
        planAvailability: {
          availableDrivers: planAvailableReasoning,
          unavailableDrivers: planUnavailableReasoning
        },
        withinMaxDistance: {
          availableDrivers: withinDistanceReasoning,
          unavailableDrivers: outsideDistanceReasoning
        },
        driversWithScoresWeighted: driversWithScoresWeighted.map((driver) => ({
          id: driver.driverId,
          name: driver.driverName || "",
          finalScore: driver.finalWeightedScore
        })),
        scoringDetails
      }
    };
  } catch (error) {
    logger.error(
      `[processDriverPoolFromScenarios] Error:`,
      { error: error instanceof Error ? error.message : String(error) }
    );
    throw new Error(
      `Failed to get driver pool: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}

// src/offers/driverPool/scope.ts
async function resolveScopeToRegionIds(client, scope) {
  if (!client) throw new Error("GraphQL client is required to resolve scope");
  if (!scope) return [];
  if (scope.scope_region_id != null) {
    return [scope.scope_region_id];
  }
  if (scope.scope_customer_id != null) {
    const regionId = await getRegionIdForCustomer(client, scope.scope_customer_id);
    return regionId ? [regionId] : [];
  }
  if (scope.scope_organization_id != null) {
    return getRegionIdsForOrganization(client, scope.scope_organization_id);
  }
  return [];
}

// src/offers/driverPool/fromPromo.ts
async function sourceCandidatesForPromo(client, promo) {
  if (promo.scope_region_id == null && promo.scope_customer_id == null && promo.scope_organization_id == null) {
    throw new Error(
      `Promo ${promo.id ?? "?"} has no scope set \u2014 every promo must be region-, customer-, or organization-scoped.`
    );
  }
  const regionIds = await resolveScopeToRegionIds(client, promo);
  if (regionIds.length === 0) {
    if (promo.scope_customer_id != null) {
      throw new Error(
        `Customer ${promo.scope_customer_id} has no rooftop location with a region \u2014 cannot derive driver pool.`
      );
    }
    if (promo.scope_organization_id != null) {
      throw new Error(
        `Organization ${promo.scope_organization_id} has no customers with rooftop regions \u2014 cannot derive driver pool. Required certifications still apply once candidates are sourced.`
      );
    }
    return [];
  }
  logger.info(
    `[getDriverPoolForPromo] Sourcing candidates from regions [${regionIds.join(", ")}]`
  );
  return regionIds.length === 1 ? getDriversInRegion(client, regionIds[0]) : getDriversInRegions(client, regionIds);
}
async function getDriverPoolForPromo(client, promoId, bundleConfig, round, driverSelectionAlgorithm = "v1_0_0") {
  logger.info(`[getDriverPoolForPromo] Starting promo-only driver pool selection for promo ${promoId}`);
  if (!client) throw new Error("GraphQL client is required to get driver pool for promo");
  if (!promoId) throw new Error("Promo ID is required to get driver pool for promo");
  if (!bundleConfig) throw new Error("Bundle config is required to get driver pool for promo");
  if (!round) throw new Error("Round is required to get driver pool for promo");
  try {
    const promo = await getPromoById(client, promoId);
    if (!promo) throw new Error(`Promo ${promoId} not found`);
    const candidates = await sourceCandidatesForPromo(client, promo);
    logger.info(`[getDriverPoolForPromo] ${candidates.length} candidates sourced`);
    const inRadiusReasoning = candidates.map(toDriverIdName);
    const buildResult = (reasoningOverrides = {}) => buildEmptyDriverPoolResult({
      inRadius: inRadiusReasoning,
      promoEligibility: { eligibleDrivers: [], ineligibleDrivers: [] },
      ...reasoningOverrides
    });
    if (candidates.length === 0) {
      return buildResult();
    }
    logger.info(`[getDriverPoolForPromo] Filtering by promo eligibility (${promo.eligibility_algorithm_key})...`);
    const { eligible: eligibleDrivers, ineligible: ineligibleVerdicts } = await evaluatePromoEligibility(client, [promo], candidates);
    logger.info(`[getDriverPoolForPromo] ${eligibleDrivers.length}/${candidates.length} drivers passed eligibility`);
    const promoEligibilityReasoning = {
      eligibleDrivers: eligibleDrivers.map(toDriverIdName),
      ineligibleDrivers: ineligibleVerdicts.map((v) => ({
        ...toDriverIdName(v.candidate),
        reason: v.reason
      }))
    };
    if (eligibleDrivers.length === 0) {
      return buildResult({ promoEligibility: promoEligibilityReasoning });
    }
    const fullBlacklist = await getFullBlacklist(client, [], [], []);
    const blacklistSet = new Set(fullBlacklist);
    const nonBlacklistedDrivers = eligibleDrivers.filter((d) => !blacklistSet.has(d.id));
    const blacklistedDrivers = eligibleDrivers.filter((d) => blacklistSet.has(d.id));
    logger.info(`[getDriverPoolForPromo] ${nonBlacklistedDrivers.length} drivers not blacklisted`);
    const blacklistReasoning = {
      blacklistedDrivers: blacklistedDrivers.map(toDriverIdName),
      nonBlacklistedDrivers: nonBlacklistedDrivers.map(toDriverIdName)
    };
    if (nonBlacklistedDrivers.length === 0) {
      return buildResult({
        promoEligibility: promoEligibilityReasoning,
        blacklist: blacklistReasoning
      });
    }
    const blockStart = promo.begin_time;
    const blockEnd = promo.end_time;
    const totalDuration = blockStart && blockEnd ? Math.max(0, Math.round((new Date(blockEnd).getTime() - new Date(blockStart).getTime()) / 6e4)) : 60;
    const scheduleAvailabilityResults = blockStart ? getDriversWithScheduledAvailability(nonBlacklistedDrivers, new Date(blockStart), totalDuration) : { availableDrivers: nonBlacklistedDrivers, moveTime: "", conflicts: [] };
    const driversWithScheduledAvailability = scheduleAvailabilityResults?.availableDrivers ?? [];
    const scheduledAvailableIds = new Set(driversWithScheduledAvailability.map((d) => d.id));
    const driversWithoutScheduledAvailability = nonBlacklistedDrivers.filter(
      (driver) => !scheduledAvailableIds.has(driver.id)
    );
    logger.info(`[getDriverPoolForPromo] ${driversWithScheduledAvailability.length} drivers with scheduled availability`);
    const scheduleReasoning = {
      availableDrivers: driversWithScheduledAvailability.map(toDriverIdName),
      unavailableDrivers: driversWithoutScheduledAvailability.map(toDriverIdName)
    };
    if (driversWithScheduledAvailability.length === 0) {
      return buildResult({
        promoEligibility: promoEligibilityReasoning,
        blacklist: blacklistReasoning,
        scheduleAvailability: scheduleReasoning
      });
    }
    const planConflictResults = await getDriversWithNoConflictingMoves(
      client,
      driversWithScheduledAvailability,
      blockStart || "",
      totalDuration
    );
    const driversWithNoConflictingMoves = planConflictResults && planConflictResults.availableDrivers ? planConflictResults : { availableDrivers: driversWithScheduledAvailability, driverPlansMap: /* @__PURE__ */ new Map() };
    const noConflictAvailableIds = new Set(
      driversWithNoConflictingMoves.availableDrivers.map((d) => d.id)
    );
    const driversWithoutConflictingMoves = driversWithScheduledAvailability.filter(
      (driver) => !noConflictAvailableIds.has(driver.id)
    );
    logger.info(`[getDriverPoolForPromo] ${driversWithNoConflictingMoves.availableDrivers.length} drivers with no plan conflicts`);
    const planReasoning = {
      availableDrivers: driversWithNoConflictingMoves.availableDrivers.map(toDriverIdName),
      unavailableDrivers: driversWithoutConflictingMoves.map(toDriverIdName)
    };
    if (driversWithNoConflictingMoves.availableDrivers.length === 0) {
      return buildResult({
        promoEligibility: promoEligibilityReasoning,
        blacklist: blacklistReasoning,
        scheduleAvailability: scheduleReasoning,
        planAvailability: planReasoning
      });
    }
    const scoringResults = await getDriverWeightedScoresWithoutOrigin(
      client,
      driversWithNoConflictingMoves.availableDrivers,
      blockStart || (/* @__PURE__ */ new Date()).toISOString(),
      driversWithNoConflictingMoves.driverPlansMap,
      driverSelectionAlgorithm
    );
    const driversWithScoresWeighted = scoringResults.scoredDrivers;
    const scoringDetails = scoringResults.scoringDetails;
    const driverById = new Map(driversWithNoConflictingMoves.availableDrivers.map((d) => [d.id, d]));
    const sortedDrivers = driversWithScoresWeighted.map((weighted) => driverById.get(weighted.driverId)).filter(Boolean);
    return {
      driverPool: sortedDrivers,
      reasoning: {
        inRadius: inRadiusReasoning,
        certifications: { requiredCertifications: [], drivers: [], driversWithoutRequiredCertifications: [] },
        promoEligibility: promoEligibilityReasoning,
        blacklist: blacklistReasoning,
        scheduleAvailability: scheduleReasoning,
        planAvailability: planReasoning,
        withinMaxDistance: { availableDrivers: [], unavailableDrivers: [] },
        driversWithScoresWeighted: driversWithScoresWeighted.map((d) => ({
          id: d.driverId,
          name: d.driverName || "",
          finalScore: d.finalWeightedScore
        })),
        scoringDetails
      }
    };
  } catch (error) {
    logger.error(
      `[getDriverPoolForPromo] Error:`,
      { error: error instanceof Error ? error.message : String(error) }
    );
    throw new Error(
      `Failed to get driver pool for promo: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}

// src/offers/driverPool/driverPoolFilters.ts
function buildScopedDriversWhere({
  regionIds,
  search = "",
  excludedTaxClasses = [],
  excludedStatuses = [],
  requireActiveUser = true,
  requireDispatchableStatus = true
} = {}) {
  const filters = [];
  if (requireActiveUser) {
    filters.push({ user: { active: { _eq: true } } });
  }
  if (requireDispatchableStatus) {
    filters.push({ status: { _in: [...DISPATCHABLE_DRIVER_STATUSES] } });
  }
  if (Array.isArray(regionIds) && regionIds.length > 0) {
    filters.push({ region_id: { _in: regionIds } });
  }
  if (Array.isArray(excludedTaxClasses) && excludedTaxClasses.length > 0) {
    filters.push({ tax_class: { _nin: excludedTaxClasses } });
  }
  if (Array.isArray(excludedStatuses) && excludedStatuses.length > 0) {
    filters.push({ status: { _nin: excludedStatuses } });
  }
  const trimmedSearch = typeof search === "string" ? search.trim() : "";
  if (trimmedSearch.length > 0) {
    const like = `%${trimmedSearch}%`;
    const orClauses = [{ user: { display_name: { _ilike: like } } }];
    const asInt = Number.parseInt(trimmedSearch, 10);
    if (Number.isFinite(asInt)) {
      orClauses.push({ id: { _eq: asInt } });
    }
    filters.push({ _or: orClauses });
  }
  return filters.length === 0 ? {} : { _and: filters };
}
function applyRequiredCerts(drivers, requiredCerts) {
  if (!Array.isArray(drivers)) return [];
  if (!Array.isArray(requiredCerts) || requiredCerts.length === 0) return drivers;
  return drivers.filter((driver) => {
    const have = new Set(
      (driver.certifications ?? []).map((c) => c?.certification_key).filter((key) => typeof key === "string" && key.length > 0)
    );
    return requiredCerts.every((key) => have.has(key));
  });
}

// src/offers/driverPool/fromScope.ts
import { gql as gql9 } from "graphql-request";
var DEFAULT_PAGE_LIMIT = 100;
var GET_DRIVERS_PAGE = gql9`
  query getDriverPoolPage($where: drivers_bool_exp!, $limit: Int!, $offset: Int!) {
    drivers(
      where: $where
      limit: $limit
      offset: $offset
      order_by: { id: desc }
    ) {
      id
      status
      region_id
      tax_class
      region {
        id
        name
      }
      user {
        id
        display_name
        email
        avatar_url
        phone
        active
      }
      certifications {
        certification_key
      }
    }
  }
`;
async function getDriverPoolPage(client, input = {}) {
  if (!client) throw new Error("GraphQL client is required to get driver pool page");
  const {
    scope = null,
    regionIds: regionIdsInput = null,
    search = "",
    limit = DEFAULT_PAGE_LIMIT,
    offset = 0,
    excludedTaxClasses = [],
    requiredCerts = [],
    requireActiveUser = true
  } = input;
  const regionIds = Array.isArray(regionIdsInput) ? regionIdsInput : await resolveScopeToRegionIds(client, scope);
  if (regionIds.length === 0) {
    return { drivers: [], regionIds: [], hasMore: false };
  }
  const where = buildScopedDriversWhere({
    regionIds,
    search,
    excludedTaxClasses,
    requireActiveUser
  });
  try {
    const res = await client.request(GET_DRIVERS_PAGE, { where, limit, offset });
    const raw = res?.drivers ?? [];
    const filtered = applyRequiredCerts(raw, requiredCerts);
    return {
      drivers: filtered,
      regionIds,
      hasMore: raw.length === limit
    };
  } catch (error) {
    logger.error(`[getDriverPoolPage] Error:`, {
      error: error instanceof Error ? error.message : String(error)
    });
    throw new Error(
      `Failed to get driver pool page: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}

// src/offers/driverPool/index.ts
async function getDriverPool(client, bundleId, bundleConfig, round, driverSelectionAlgorithm = "v1_0_0") {
  logger.info(`[getDriverPool] Starting driver pool selection for bundle ${bundleId}`);
  if (!client) throw new Error("GraphQL client is required to get driver pool");
  if (!bundleId) throw new Error("Bundle ID is required to get driver pool");
  if (!bundleConfig) throw new Error("Bundle config is required to get driver pool");
  if (!round) throw new Error("Round is required to get driver pool");
  try {
    const bundleDetails = await getBundleWithMoveDetails(client, bundleId);
    const scenarios = bundleDetails?.scenarios || [];
    const bundleSetType = bundleDetails?.bundleset?.type ?? null;
    if (bundleSetType === BUNDLE_SET_TYPES.PROMO_ONLY) {
      const linkedPromos = await getLinkedPromosForBundle(client, bundleId);
      const primaryPromo = linkedPromos?.[0];
      if (!primaryPromo?.id) {
        throw new Error(
          `Bundle ${bundleId} is PROMO_ONLY but has no linked promo \u2014 cannot build a driver pool`
        );
      }
      logger.info(
        `[getDriverPool] Bundle ${bundleId} is PROMO_ONLY; routing to getDriverPoolForPromo for promo ${primaryPromo.id}`
      );
      return await getDriverPoolForPromo(
        client,
        primaryPromo.id,
        bundleConfig,
        round,
        driverSelectionAlgorithm
      );
    }
    return await processDriverPoolFromScenarios(
      client,
      scenarios,
      bundleConfig,
      round,
      driverSelectionAlgorithm,
      bundleId
    );
  } catch (error) {
    logger.error(
      `[getDriverPool] Error:`,
      { error: error instanceof Error ? error.message : String(error) }
    );
    throw new Error(
      `Failed to get driver pool: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}
async function getDriverPoolForScenarios(client, scenarioIds, bundleConfig, round, driverSelectionAlgorithm = "v1_0_0") {
  logger.info(
    `[getDriverPoolForScenarios] Starting driver pool selection for scenarios ${scenarioIds}`
  );
  if (!client) throw new Error("GraphQL client is required to get driver pool for scenarios");
  if (!scenarioIds || !Array.isArray(scenarioIds) || scenarioIds.length === 0) {
    throw new Error("Array of scenario IDs is required to get driver pool");
  }
  if (!bundleConfig) throw new Error("Bundle config is required to get driver pool");
  if (!round) throw new Error("Round is required to get driver pool");
  try {
    const scenarios = await getScenariosByIds(client, scenarioIds);
    return await processDriverPoolFromScenarios(
      client,
      scenarios,
      bundleConfig,
      round,
      driverSelectionAlgorithm
    );
  } catch (error) {
    logger.error(
      `[getDriverPoolForScenarios] Error:`,
      { error: error instanceof Error ? error.message : String(error) }
    );
    throw new Error(
      `Failed to get driver pool for scenarios: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}

// src/offers/flow/strategies.ts
async function processTargetedOffer(client, bundleId, config, round, options = {}) {
  const { user = "system" } = options;
  const driverId = config.driverIdOverride;
  if (!driverId) {
    throw new Error("No driver ID specified for targeted offer");
  }
  const driver = await getDriverWithEligibilityFields(client, driverId);
  if (!driver) {
    throw new Error(`Targeted driver ${driverId} not found`);
  }
  const { ineligibleVerdicts } = await filterByPromoEligibility(client, bundleId, [driver]);
  if (ineligibleVerdicts.length > 0) {
    const reasons = ineligibleVerdicts.map((v) => `${v.reason} (promo ${v.promoId})`).join(", ");
    throw new Error(
      `Driver ${driverId} is not eligible for the promo(s) attached to bundle ${bundleId}: ${reasons}`
    );
  }
  const expirationMinutes = round.timeoutMinutes || DEFAULT_STRATEGY_PARAMS[OFFER_STRATEGIES.TARGETED].timeoutMinutes;
  const expirationTimestamp = new Date(Date.now() + expirationMinutes * 6e4).toISOString();
  const offer = await createOffer(client, bundleId, driverId, expirationTimestamp, { user });
  if (!offer) {
    throw new Error("Failed to create offer");
  }
  if (config.source === OFFER_SOURCES.AUTO || config.autoSendOffer === true) {
    await updateOfferStatus(client, offer.id, OFFER_STATUSES.OFFERED, { user });
  }
  return {
    success: true,
    message: `Created targeted offer for driver ${driverId} for bundle ${bundleId}`,
    offerId: offer.id,
    driverId
  };
}
async function processCascadingOffer(client, bundleId, config, round, options = {}, driverSelectionAlgorithm = "v1_0_0") {
  const { user = "system" } = options;
  const existingOffers = await getOffersForBundle2(client, bundleId);
  const alreadyOfferedDriverIds = existingOffers.map((offer2) => offer2.driver_id);
  if (!round.offeredDrivers) {
    round.offeredDrivers = [];
  }
  const maxDriversForRound = round.maxDrivers || DEFAULT_STRATEGY_PARAMS[OFFER_STRATEGIES.CASCADING].maxDriversPerRound || 1;
  if (round.offeredDrivers.length >= maxDriversForRound) {
    log(
      "processCascadingOffer",
      `Round ${round.round} has reached maxDrivers limit of ${maxDriversForRound}`
    );
    const currentRoundOffers = existingOffers.filter(
      (offer2) => round.offeredDrivers.includes(offer2.driver_id)
    );
    const hasUnresolvedOffers = currentRoundOffers.some(
      (offer2) => [OFFER_STATUSES.NEW, OFFER_STATUSES.OFFERED].includes(offer2.status)
    );
    if (hasUnresolvedOffers) {
      log(
        "processCascadingOffer",
        `Round ${round.round} has unresolved offers, waiting for resolution`
      );
      return {
        success: true,
        message: `Round ${round.round} has reached maxDrivers but waiting for offer resolutions`
      };
    }
    const hasAcceptedOffer = currentRoundOffers.some(
      (offer2) => offer2.status === OFFER_STATUSES.ACCEPTED
    );
    if (!hasAcceptedOffer) {
      return await prepareNextRound(client, bundleId, config, round, { user });
    }
    return {
      success: true,
      message: `Round ${round.round} has completed with an accepted offer`
    };
  }
  const radiusMiles = round.radiusMiles || DEFAULT_STRATEGY_PARAMS[OFFER_STRATEGIES.CASCADING].initialRadiusMiles;
  const bundleDetails = await getBundleWithMoveDetails(client, bundleId);
  const moveLocations = getMoveLocationsAndOriginInfoFromBundle(bundleDetails);
  const originLocation = moveLocations.originLocation;
  if (!originLocation) {
    throw new Error("Could not determine origin location for bundle");
  }
  const driverPoolResult = await getDriverPool(
    client,
    bundleId,
    config,
    round,
    driverSelectionAlgorithm
  );
  let drivers = driverPoolResult?.driverPool ?? [];
  if (drivers.length === 0) {
    log("processCascadingOffer", `No available drivers found within ${radiusMiles} miles`);
    return await prepareNextRound(client, bundleId, config, round, { user });
  }
  drivers = drivers.filter((driver2) => !alreadyOfferedDriverIds.includes(driver2.id));
  drivers = drivers.filter((driver2) => !round.offeredDrivers.includes(driver2.id));
  if (drivers.length === 0) {
    log(
      "processCascadingOffer",
      `All available drivers within ${radiusMiles} miles have already been offered`
    );
    return await prepareNextRound(client, bundleId, config, round, { user });
  }
  const driver = drivers[0];
  if (!driver) {
    throw new Error("No driver found for cascading offer");
  }
  const expirationMinutes = round.timeoutMinutes || DEFAULT_STRATEGY_PARAMS[OFFER_STRATEGIES.CASCADING].timeoutMinutes;
  const expirationTimestamp = new Date(Date.now() + expirationMinutes * 6e4).toISOString();
  const offer = await createOffer(client, bundleId, driver.id, expirationTimestamp, { user });
  round.offeredDrivers.push(driver.id);
  const updatedConfig = { ...config };
  updatedConfig.offerRounds = updatedConfig.offerRounds.map((r) => {
    if (r.round === round.round) {
      return { ...r, offeredDrivers: round.offeredDrivers };
    }
    return r;
  });
  await updateBundleConfig(client, bundleId, updatedConfig, { user });
  await updateOfferStatus(client, offer.id, OFFER_STATUSES.OFFERED, { user });
  log("processCascadingOffer", `Created offer for driver ${driver.id} in round ${round.round}`);
  return {
    success: true,
    message: `Created cascading offer for driver ${driver.id} for bundle ${bundleId}`,
    offerId: offer.id,
    driverId: driver.id
  };
}
async function processBroadcastOffer(client, bundleId, drivers, config, round, user = "system") {
  logger.info(
    `[processBroadcastOffer] Starting with ${drivers?.length || 0} drivers for round ${round?.round}`
  );
  const maxDrivers = round?.maxDrivers || DEFAULT_STRATEGY_PARAMS[OFFER_STRATEGIES.BROADCAST]?.maxDrivers || 10;
  logger.info(`[processBroadcastOffer] Max drivers for round: ${maxDrivers}`);
  const existingOffers = await getOffersForBundle2(client, bundleId);
  const alreadyOfferedDriverIds = existingOffers.map((offer) => offer.driver_id);
  logger.info(
    `[processBroadcastOffer] Existing offers: ${existingOffers.length}, Already offered to: ${alreadyOfferedDriverIds.length} drivers`
  );
  if (!round?.offeredDrivers) {
    round.offeredDrivers = [];
  }
  logger.info(
    `[processBroadcastOffer] Round ${round?.round} has offered to ${round?.offeredDrivers?.length || 0} drivers so far`
  );
  if (round?.offeredDrivers?.length >= maxDrivers) {
    logger.info(
      `[processBroadcastOffer] Already reached max drivers (${maxDrivers}) for round ${round.round}`
    );
    const updatedConfig2 = { ...config };
    updatedConfig2.offerRounds = updatedConfig2?.offerRounds?.map((r) => {
      if (r?.round === round?.round) {
        return { ...r, completed: true };
      }
      return r;
    });
    await updateBundleConfig(client, bundleId, updatedConfig2, { user });
    const hasAcceptedOffer = existingOffers.some((offer) => offer.status === OFFER_STATUSES.ACCEPTED);
    if (!hasAcceptedOffer) {
      logger.info(`[processBroadcastOffer] No accepted offers, advancing to next strategy`);
      return await advanceToNextStrategy(client, bundleId, updatedConfig2, { user });
    }
    return {
      success: true,
      message: `Round ${round?.round} completed with max drivers reached`
    };
  }
  const remainingSlots = maxDrivers - round?.offeredDrivers?.length;
  logger.info(`[processBroadcastOffer] Remaining slots: ${remainingSlots}`);
  if (remainingSlots <= 0) {
    logger.info(`[processBroadcastOffer] No remaining slots for round ${round?.round}`);
    const updatedConfig2 = { ...config };
    updatedConfig2.offerRounds = updatedConfig2.offerRounds.map((r) => {
      if (r?.round === round?.round) {
        return { ...r, completed: true };
      }
      return r;
    });
    await updateBundleConfig(client, bundleId, updatedConfig2, { user });
    return await advanceToNextStrategy(client, bundleId, updatedConfig2, { user });
  }
  if (drivers.length === 0) {
    logger.info(`[processBroadcastOffer] No available drivers found!`);
    const updatedConfig2 = { ...config };
    updatedConfig2.offerRounds = updatedConfig2.offerRounds.map((r) => {
      if (r?.round === round?.round) {
        return { ...r, completed: true };
      }
      return r;
    });
    await updateBundleConfig(client, bundleId, updatedConfig2, { user });
    return await advanceToNextStrategy(client, bundleId, updatedConfig2, { user });
  }
  const uniqueAvailableDrivers = drivers.filter(
    (driver) => !alreadyOfferedDriverIds.includes(driver.id) && !round.offeredDrivers.includes(driver.id)
  ).slice(0, remainingSlots);
  logger.info(
    `[processBroadcastOffer] Unique available drivers after filtering: ${uniqueAvailableDrivers.length}`
  );
  if (uniqueAvailableDrivers.length === 0) {
    logger.info(`[processBroadcastOffer] No new drivers available, marking round complete`);
    const updatedConfig2 = { ...config };
    updatedConfig2.offerRounds = updatedConfig2?.offerRounds?.map((r) => {
      if (r?.round === round?.round) {
        return { ...r, completed: true };
      }
      return r;
    });
    await updateBundleConfig(client, bundleId, updatedConfig2, { user });
    return await advanceToNextStrategy(client, bundleId, updatedConfig2, { user });
  }
  const expirationMinutes = round?.timeoutMinutes || DEFAULT_STRATEGY_PARAMS[OFFER_STRATEGIES.BROADCAST].timeoutMinutes;
  const expirationTimestamp = new Date(Date.now() + expirationMinutes * 6e4).toISOString();
  logger.info(
    `[processBroadcastOffer] Creating offers with ${expirationMinutes} minute timeout`
  );
  const offerPromises = uniqueAvailableDrivers.map(
    (driver) => createOffer(client, bundleId, driver.id, expirationTimestamp, { user })
  );
  const results = await Promise.allSettled(offerPromises);
  const offers = results.filter((result) => result.status === "fulfilled").map((result) => result.value);
  logger.info(
    `[processBroadcastOffer] Created ${offers.length} offers successfully out of ${offerPromises.length} attempts`
  );
  log("processFlow", `Created ${offers.length} offers for bundle ${bundleId}`);
  const newDriverIds = uniqueAvailableDrivers.map((d) => d.id);
  const existingDrivers = round?.offeredDrivers || [];
  const updatedOfferedDrivers = [.../* @__PURE__ */ new Set([...existingDrivers, ...newDriverIds])];
  logger.info(
    `[processBroadcastOffer] Updated offered drivers count: ${updatedOfferedDrivers.length}`
  );
  const updatedConfig = { ...config };
  updatedConfig.offerRounds = updatedConfig?.offerRounds?.map((r) => {
    if (r?.round === round?.round) {
      return { ...r, offeredDrivers: updatedOfferedDrivers };
    }
    return r;
  });
  await updateBundleConfig(client, bundleId, updatedConfig, { user });
  logger.info(`[processBroadcastOffer] Updating ${offers.length} offers to OFFERED status`);
  const updatePromises = offers.map(
    (offer) => updateOfferStatus(client, offer.id, OFFER_STATUSES.OFFERED, { user })
  );
  await Promise.all(updatePromises);
  logger.info(`[processBroadcastOffer] Successfully completed broadcast round ${round?.round}`);
  return {
    success: true,
    message: `Created ${offers.length} broadcast offers for bundle ${bundleId}`
  };
}
async function processRollingBroadcastOffer(client, bundleId, drivers, config, round, options = { user: "system" }) {
  try {
    const { user = "system" } = options;
    logger.info(`[processRollingBroadcastOffer] Starting...`);
    if (!drivers || drivers.length === 0) {
      logger.info(`[processRollingBroadcastOffer] No driver pool!`);
      return {
        success: false,
        message: `No driver pool available for rolling broadcast offer`
      };
    }
    const expirationMinutes = round.timeoutMinutes || DEFAULT_STRATEGY_PARAMS[OFFER_STRATEGIES.TARGETED].timeoutMinutes;
    const expirationTimestamp = new Date(Date.now() + expirationMinutes * 6e4).toISOString();
    if (round.round <= 4) {
      const unOfferedDrivers = drivers.filter((d) => !(round?.offeredDrivers || []).includes(d.id));
      const driver = unOfferedDrivers[round.round - 1];
      if (!driver) {
        logger.info(
          `[processRollingBroadcastOffer] No driver found for round ${round.round}. marking round as complete`
        );
        const updatedConfig3 = { ...config };
        updatedConfig3.offerRounds = updatedConfig3?.offerRounds?.map((r) => {
          if (r?.round === round?.round) {
            return { ...r, offeredDrivers: [] };
          }
          return r;
        });
        await updateBundleConfig(client, bundleId, updatedConfig3, { user });
        return {
          success: true,
          message: `No driver found for round ${round.round}, skipping offer for round`
        };
      }
      logger.info(`[processRollingBroadcastOffer] Creating offer for driver ${driver.id}`);
      const offer = await createOffer(client, bundleId, driver.id, expirationTimestamp, { user });
      const updatedConfig2 = { ...config };
      updatedConfig2.offerRounds = updatedConfig2?.offerRounds?.map((r) => {
        if (r?.round === round?.round) {
          return { ...r, offeredDrivers: [driver.id] };
        }
        return r;
      });
      await updateBundleConfig(client, bundleId, updatedConfig2, { user });
      logger.info(`[processRollingBroadcastOffer] Updating ${offer.id} to OFFERED status`);
      await updateOfferStatus(client, offer.id, OFFER_STATUSES.OFFERED, { user });
      logger.info(
        `[processRollingBroadcastOffer] Successfully completed rolling broadcast round ${round?.round}`
      );
      return {
        success: true,
        message: `Created rolling broadcast offer for driver ${driver.id} for bundle ${bundleId}`,
        offerId: offer.id,
        driverId: driver.id
      };
    }
    logger.info(
      `[processRollingBroadcastOffer] Rescinding all existing offers for bundle ${bundleId}`
    );
    const rescindedCount = await rescindAllOffers(client, bundleId, { user });
    logger.info(
      `[processRollingBroadcastOffer] Rescinded ${rescindedCount} offers for bundle ${bundleId}`
    );
    let driversToOffer = drivers;
    if (round.maxDrivers && round.maxDrivers > 0) {
      driversToOffer = drivers.slice(0, round.maxDrivers);
    }
    const offerPromises = driversToOffer.map(
      (driver) => createOffer(client, bundleId, driver.id, expirationTimestamp, { user })
    );
    const results = await Promise.allSettled(offerPromises);
    const offers = results.filter((result) => result.status === "fulfilled").map((result) => result.value);
    logger.info(
      `[processRollingBroadcastOffer] Created ${offers.length} offers successfully out of ${offerPromises.length} attempts`
    );
    log("processFlow", `Created ${offers.length} offers for bundle ${bundleId}`);
    const newDriverIds = driversToOffer.map((d) => d.id);
    const existingDrivers = round?.offeredDrivers || [];
    const updatedOfferedDrivers = [.../* @__PURE__ */ new Set([...existingDrivers, ...newDriverIds])];
    logger.info(
      `[processRollingBroadcastOffer] Updated offered drivers count: ${updatedOfferedDrivers.length}`
    );
    const updatedConfig = { ...config };
    updatedConfig.offerRounds = updatedConfig?.offerRounds?.map((r) => {
      if (r?.round === round?.round) {
        return { ...r, offeredDrivers: updatedOfferedDrivers };
      }
      return r;
    });
    await updateBundleConfig(client, bundleId, updatedConfig, { user });
    logger.info(
      `[processRollingBroadcastOffer] Updating ${offers.length} offers to OFFERED status`
    );
    const updatePromises = offers.map(
      (offer) => updateOfferStatus(client, offer.id, OFFER_STATUSES.OFFERED, { user })
    );
    await Promise.all(updatePromises);
    logger.info(
      `[processRollingBroadcastOffer] Successfully completed broadcast round ${round?.round}`
    );
    return {
      success: true,
      message: `Created ${offers.length} broadcast offers for bundle ${bundleId}`
    };
  } catch (error) {
    logger.error(
      `[processRollingBroadcastOffer] Error: ${error instanceof Error ? error.message : String(error)}`
    );
    return {
      success: false,
      message: `Error creating rolling broadcast offers: ${error instanceof Error ? error.message : String(error)}`
    };
  }
}

// src/offers/flow/offerLifecycle.ts
async function processNextOfferRound(client, bundleId, options = {}, driverSelectionAlgorithm = "v1_0_0") {
  const { user = "system" } = options;
  logger.info(
    `[processNextOfferRound] Starting for bundle ${bundleId} with algorithm ${driverSelectionAlgorithm}`
  );
  if (!client) throw new Error("GraphQL client is required to process next offer round");
  if (!bundleId) throw new Error("Bundle ID is required");
  try {
    const bundle = await getBundleById(client, bundleId);
    logger.info(`[processNextOfferRound] Bundle ${bundleId} status: ${bundle?.status}`);
    if (!bundle) {
      throw new Error(`Bundle ${bundleId} not found`);
    }
    if (bundle.status === BUNDLE_STATUSES.DEPRECATED) {
      logger.info(`[processNextOfferRound] Bundle ${bundleId} is deprecated, not offering`);
      return {
        offerResult: { success: false, message: `Bundle ${bundleId} is deprecated` }
      };
    }
    const isPromoOnly = bundle.bundleset?.type === BUNDLE_SET_TYPES.PROMO_ONLY;
    if (!isPromoOnly && !bundle.scenarios?.length) {
      logger.info(`[processNextOfferRound] Bundle ${bundleId} has no scenarios, deprecating instead of offering`);
      await updateBundleStatus(client, bundleId, BUNDLE_STATUSES.DEPRECATED, { user });
      return {
        offerResult: { success: false, message: `Bundle ${bundleId} has no scenarios, deprecated` }
      };
    }
    if (bundle.status !== BUNDLE_STATUSES.DISPATCHING) {
      logger.info(`[processNextOfferRound] Updating bundle ${bundleId} status to DISPATCHING`);
      await updateBundleStatus(client, bundleId, BUNDLE_STATUSES.DISPATCHING, {
        config: bundle.config,
        user
      });
    }
    const bundleConfig = bundle.config || {
      offerStrategy: OFFER_STRATEGIES.TARGETED,
      offerRounds: []
    };
    const strategy = getOfferStrategy(bundleConfig);
    const nextRound = getNextIncompleteRound(bundleConfig);
    logger.info(
      `[processNextOfferRound] Strategy: ${strategy}, Round: ${nextRound?.round || "none"}`
    );
    if (!nextRound) {
      logger.info(`[processNextOfferRound] No incomplete rounds, advancing to next strategy`);
      const advanceResult = await advanceToNextStrategy(client, bundleId, bundleConfig, { user });
      return {
        offerResult: advanceResult,
        driverPoolResults: {}
      };
    }
    log(
      "processNextOfferRound",
      `Processing round ${nextRound.round} using ${strategy} strategy for bundle ${bundleId}`
    );
    logger.info(
      `[processNextOfferRound] Getting driver pool with ${driverSelectionAlgorithm} algorithm`
    );
    let getDriverPoolResponse = {
      driverPool: [],
      reasoning: {
        inRadius: [],
        certifications: { requiredCertifications: [], drivers: [] },
        blacklist: { blacklistedDrivers: [], nonBlacklistedDrivers: [] },
        promoEligibility: { eligibleDrivers: [], ineligibleDrivers: [] },
        scheduleAvailability: { availableDrivers: [], unavailableDrivers: [] },
        planAvailability: { availableDrivers: [], unavailableDrivers: [] },
        withinMaxDistance: { availableDrivers: [], unavailableDrivers: [] },
        driversWithScoresWeighted: [],
        scoringDetails: []
      }
    };
    if (nextRound.round === 1) {
      switch (strategy) {
        case OFFER_STRATEGIES.BROADCAST:
        case OFFER_STRATEGIES.ROLLING_BROADCAST:
          getDriverPoolResponse = await getDriverPool(
            client,
            bundleId,
            bundleConfig,
            nextRound,
            driverSelectionAlgorithm
          );
          bundleConfig.driverSelectionResults = getDriverPoolResponse;
          await updateBundleConfig(client, bundleId, bundleConfig, { user });
          break;
        case OFFER_STRATEGIES.TARGETED:
        case OFFER_STRATEGIES.CASCADING:
        default:
          break;
      }
    } else {
      switch (strategy) {
        case OFFER_STRATEGIES.BROADCAST:
        case OFFER_STRATEGIES.ROLLING_BROADCAST:
          getDriverPoolResponse.driverPool = bundleConfig?.driverSelectionResults?.reasoning?.driversWithScoresWeighted || [];
          break;
        case OFFER_STRATEGIES.TARGETED:
        case OFFER_STRATEGIES.CASCADING:
        default:
          break;
      }
    }
    let result;
    switch (strategy) {
      case OFFER_STRATEGIES.TARGETED:
        logger.info(`[processNextOfferRound] Executing TARGETED strategy`);
        result = await processTargetedOffer(client, bundleId, bundleConfig, nextRound, { user });
        if (result.success) {
          await markRoundCompleted(client, bundleId, bundleConfig, nextRound.round, { user });
        }
        break;
      case OFFER_STRATEGIES.CASCADING:
        logger.info(`[processNextOfferRound] Executing CASCADING strategy`);
        result = await processCascadingOffer(client, bundleId, bundleConfig, nextRound, { user });
        break;
      case OFFER_STRATEGIES.BROADCAST:
        logger.info(
          `[processNextOfferRound] Executing BROADCAST strategy with ${getDriverPoolResponse?.driverPool?.length || 0} drivers`
        );
        result = await processBroadcastOffer(
          client,
          bundleId,
          getDriverPoolResponse?.driverPool,
          bundleConfig,
          nextRound,
          user
        );
        break;
      case OFFER_STRATEGIES.ROLLING_BROADCAST:
        logger.info(
          `[processNextOfferRound] Executing ROLLING_BROADCAST strategy with ${getDriverPoolResponse?.driverPool?.length || 0} drivers`
        );
        result = await processRollingBroadcastOffer(
          client,
          bundleId,
          getDriverPoolResponse?.driverPool,
          bundleConfig,
          nextRound,
          { user }
        );
        if (result.success) {
          await markRoundCompleted(client, bundleId, bundleConfig, nextRound.round, { user });
          await updateBundleStatus(client, bundleId, BUNDLE_STATUSES.READY, { user });
        } else {
          await updateBundleStatus(client, bundleId, BUNDLE_STATUSES.FAILED, { user });
        }
        break;
      default:
        throw new Error(`Unsupported offer strategy: ${strategy}`);
    }
    logger.info(`[processNextOfferRound] Strategy execution result:`, {
      success: result.success,
      message: result.message
    });
    return {
      offerResult: result,
      reasoning: getDriverPoolResponse?.reasoning
    };
  } catch (error) {
    logger.error(
      `[processNextOfferRound] Error for bundle ${bundleId}:`,
      { error: error instanceof Error ? error.message : String(error) }
    );
    if (error instanceof Error) {
      log(
        "processNextOfferRound",
        `Error processing offers for bundle ${bundleId}: ${error.message}`
      );
      throw new Error(`Error processing offers for bundle ${bundleId}: ${error.message}`);
    }
    throw error;
  }
}
async function handleOfferDeclined(client, offerId, options = {}) {
  const { user = "system" } = options;
  if (!client) throw new Error("GraphQL client is required to handle declined offer");
  if (!offerId) throw new Error("Offer ID is required");
  try {
    log("handleOfferDeclined", `Processing declined offer ${offerId}`);
    const declinedOffer = await getOfferById(client, offerId);
    if (!declinedOffer) {
      throw new Error(`Offer ${offerId} not found`);
    }
    const bundleId = declinedOffer.bundle_id;
    log("handleOfferDeclined", `Offer belongs to bundle ${bundleId}`);
    const bundle = await getBundleById(client, bundleId);
    if (!bundle) {
      throw new Error(`Bundle ${bundleId} not found`);
    }
    if (bundle.status === BUNDLE_STATUSES.DISPATCHED) {
      log(
        "handleOfferDeclined",
        `Bundle ${bundleId} is already dispatched, no further action needed.`
      );
      return {
        success: true,
        message: `Offer ${offerId} declined, but bundle ${bundleId} is already dispatched.`
      };
    }
    const bundleConfig = bundle.config && typeof bundle.config === "object" ? {
      offerStrategy: bundle.config.offerStrategy || OFFER_STRATEGIES.TARGETED,
      offerRounds: bundle.config.offerRounds || [],
      previousStrategy: bundle.config.previousStrategy,
      driverIdOverride: bundle.config.driverIdOverride,
      source: bundle.config.source
    } : {
      offerStrategy: OFFER_STRATEGIES.TARGETED,
      offerRounds: []
    };
    const strategy = getOfferStrategy(bundleConfig);
    log("handleOfferDeclined", `Bundle using strategy: ${strategy}`);
    const isManual = bundleConfig?.source === OFFER_SOURCES.MANUAL;
    if (isManual) {
      log("handleOfferDeclined", `Bundle is manual, marking as failed for dispatcher intervention`);
      await updateBundleStatus(client, bundleId, BUNDLE_STATUSES.FAILED, { user });
      return {
        success: true,
        message: `Bundle marked as failed, requires manual intervention`
      };
    }
    if (strategy === OFFER_STRATEGIES.BROADCAST) {
      const currentRound = getNextIncompleteRound(bundleConfig);
      const existingOffers = await getOffersForBundle2(client, bundleId);
      const cohort = currentRound ?? bundleConfig.offerRounds?.[bundleConfig.offerRounds.length - 1];
      const cohortOffers = existingOffers.filter(
        (offer) => cohort?.offeredDrivers?.includes(offer.driver_id)
      );
      const allOffersResolved = !cohortOffers.some(
        (offer) => offer.status === OFFER_STATUSES.NEW || offer.status === OFFER_STATUSES.OFFERED
      );
      log(
        "handleOfferDeclined",
        `All offers resolved for broadcast round ${cohort?.round ?? "n/a"}? ${allOffersResolved}`
      );
      if (allOffersResolved) {
        if (!currentRound) {
          log("handleOfferDeclined", `No incomplete rounds found, creating new broadcast round`);
          const updatedConfig = { ...bundleConfig };
          const lastRound = bundleConfig.offerRounds[bundleConfig.offerRounds.length - 1];
          const newRound = {
            round: lastRound.round + 1,
            type: OFFER_STRATEGIES.BROADCAST,
            radiusMiles: lastRound.radiusMiles,
            maxDrivers: lastRound.maxDrivers,
            timeoutMinutes: lastRound.timeoutMinutes,
            completed: false,
            offeredDrivers: []
          };
          updatedConfig.offerRounds = [...bundleConfig.offerRounds, newRound];
          await updateBundleConfig(client, bundleId, updatedConfig, { user });
          const processResult = await processNextOfferRound(client, bundleId, { user });
          return processResult.offerResult;
        }
        const currentRoundOffers = existingOffers.filter(
          (offer) => currentRound.offeredDrivers?.includes(offer.driver_id)
        );
        const hasAcceptedOffer = currentRoundOffers.some(
          (offer) => offer.status === OFFER_STATUSES.ACCEPTED
        );
        if (!hasAcceptedOffer) {
          log(
            "handleOfferDeclined",
            `No accepted offers in round ${currentRound.round}, trying another batch`
          );
          const processResult = await processNextOfferRound(client, bundleId, { user });
          return processResult.offerResult;
        }
        log(
          "handleOfferDeclined",
          `Round ${currentRound.round} has an accepted offer, marking as completed`
        );
        await markRoundCompleted(client, bundleId, bundleConfig, currentRound.round, { user });
        return {
          success: true,
          message: `Round ${currentRound.round} completed with an accepted offer`
        };
      }
      log("handleOfferDeclined", `Still waiting for other broadcast offers to be resolved`);
      return {
        success: true,
        message: `Offer ${offerId} declined, but other offers for this bundle are still pending.`
      };
    } else if (strategy === OFFER_STRATEGIES.CASCADING) {
      const currentRound = getNextIncompleteRound(bundleConfig);
      if (!currentRound) {
        log("handleOfferDeclined", `No incomplete rounds found for cascading strategy`);
        return await advanceToNextStrategy(client, bundleId, bundleConfig, { user });
      }
      const existingOffers = await getOffersForBundle2(client, bundleId);
      const currentRoundOffers = existingOffers.filter(
        (offer) => currentRound.offeredDrivers?.includes(offer.driver_id)
      );
      const maxDriversForRound = currentRound.maxDrivers || DEFAULT_STRATEGY_PARAMS[OFFER_STRATEGIES.CASCADING]?.maxDriversPerRound || 1;
      const hasReachedMaxDrivers = currentRound.offeredDrivers?.length >= maxDriversForRound;
      const hasUnresolvedOffers = currentRoundOffers.some(
        (offer) => [OFFER_STATUSES.NEW, OFFER_STATUSES.OFFERED].includes(offer.status)
      );
      if (hasReachedMaxDrivers && !hasUnresolvedOffers) {
        log(
          "handleOfferDeclined",
          `All offers in round ${currentRound.round} are resolved and max drivers reached`
        );
        return await prepareNextRound(client, bundleId, bundleConfig, currentRound, { user });
      }
      log("handleOfferDeclined", `Processing next driver in current round`);
      const processResult = await processNextOfferRound(client, bundleId, { user });
      return processResult.offerResult;
    } else if (strategy === OFFER_STRATEGIES.ROLLING_BROADCAST) {
      const currentRound = getNextIncompleteRound(bundleConfig);
      if (!currentRound) {
        log("handleOfferDeclined", `No incomplete rounds found for rolling broadcast strategy`);
        return {
          success: true,
          message: `No incomplete rounds found for rolling broadcast strategy`
        };
      }
      const existingOffers = await getOffersForBundle2(client, bundleId);
      const currentRoundOffers = existingOffers.filter(
        (offer) => currentRound.offeredDrivers?.includes(offer.driver_id)
      );
      const hasActiveOffers = currentRoundOffers.some(
        (offer) => [OFFER_STATUSES.NEW, OFFER_STATUSES.OFFERED].includes(offer.status)
      );
      if (hasActiveOffers) {
        log("handleOfferDeclined", `Active offers found for rolling broadcast strategy, exiting`);
        return {
          success: true,
          message: `Active offers found for rolling broadcast strategy, exiting`
        };
      }
      const hasCompletedOffers = currentRoundOffers.some(
        (offer) => [OFFER_STATUSES.ACCEPTED, OFFER_STATUSES.ASSIGNED].includes(offer.status)
      );
      if (hasCompletedOffers) {
        log(
          "handleOfferDeclined",
          `Completed offers found for rolling broadcast strategy, exiting`
        );
        return {
          success: true,
          message: `Completed offers found for rolling broadcast strategy, exiting`
        };
      }
      log("handleOfferDeclined", `Processing next offer for rolling broadcast strategy`);
      const processResult = await processNextOfferRound(client, bundleId, { user });
      return processResult.offerResult;
    }
    log("handleOfferDeclined", `Targeted offer declined, marking bundle as failed`);
    await updateBundleStatus(client, bundleId, BUNDLE_STATUSES.FAILED, { user });
    return {
      success: true,
      message: "Targeted offer declined, bundle marked as failed for manual intervention"
    };
  } catch (error) {
    if (error instanceof Error) {
      log("handleOfferDeclined", `Error handling declined offer ${offerId}: ${error.message}`);
      throw new Error(`Error handling declined offer: ${error.message}`);
    }
    throw error;
  }
}
async function advanceToNextStrategy(client, bundleId, config, options = {}) {
  const { user = "system" } = options;
  if (!client) throw new Error("GraphQL client is required to advance to next strategy");
  if (!bundleId) throw new Error("Bundle ID is required");
  if (!config) throw new Error("Bundle config is required");
  const currentStrategy = getOfferStrategy(config);
  let nextStrategy;
  if (currentStrategy === OFFER_STRATEGIES.ROLLING_BROADCAST) {
    nextStrategy = OFFER_STRATEGIES.ROLLING_BROADCAST;
  }
  if (currentStrategy === OFFER_STRATEGIES.TARGETED) {
    nextStrategy = OFFER_STRATEGIES.CASCADING;
  } else if (currentStrategy === OFFER_STRATEGIES.CASCADING) {
    nextStrategy = OFFER_STRATEGIES.BROADCAST;
  } else if (currentStrategy === OFFER_STRATEGIES.BROADCAST) {
    const maxBroadcastRounds = 3;
    const currentRoundCount = config.offerRounds?.filter((r) => r.type === OFFER_STRATEGIES.BROADCAST).length || 0;
    if (currentRoundCount >= maxBroadcastRounds) {
      log(
        "advanceToNextStrategy",
        `Exceeded maximum broadcast rounds (${maxBroadcastRounds}), marking as failed`
      );
      return await updateBundleStatus(client, bundleId, BUNDLE_STATUSES.FAILED, { config, user });
    }
    nextStrategy = OFFER_STRATEGIES.BROADCAST;
  } else {
    return await updateBundleStatus(client, bundleId, BUNDLE_STATUSES.FAILED, { config, user });
  }
  log(
    "advanceToNextStrategy",
    `Advancing from ${currentStrategy} to ${nextStrategy} for bundle ${bundleId}`
  );
  const updatedConfig = {
    ...config,
    offerStrategy: nextStrategy,
    previousStrategy: currentStrategy
  };
  if (currentStrategy === OFFER_STRATEGIES.CASCADING && nextStrategy === OFFER_STRATEGIES.BROADCAST) {
    const newRounds = initializeOfferRounds({ strategy: nextStrategy });
    updatedConfig.offerRounds = [...config.offerRounds || [], ...newRounds];
  } else if (currentStrategy === OFFER_STRATEGIES.BROADCAST && nextStrategy === OFFER_STRATEGIES.BROADCAST) {
    const lastRound = config.offerRounds[config.offerRounds.length - 1];
    const newRound = {
      round: lastRound.round + 1,
      type: OFFER_STRATEGIES.BROADCAST,
      radiusMiles: lastRound.radiusMiles,
      maxDrivers: lastRound.maxDrivers,
      timeoutMinutes: lastRound.timeoutMinutes,
      completed: false,
      offeredDrivers: []
    };
    updatedConfig.offerRounds = [...config.offerRounds, newRound];
  } else {
    updatedConfig.offerRounds = initializeOfferRounds({ strategy: nextStrategy });
  }
  const updateConfigResult = await updateBundleConfig(client, bundleId, updatedConfig, { user });
  if (!updateConfigResult) {
    throw new Error("Failed to update bundle config");
  }
  log("advanceToNextStrategy", "Bundle config updated successfully");
  return await updateBundleStatus(client, bundleId, BUNDLE_STATUSES.READY);
}
async function prepareNextRound(client, bundleId, config, round, options = {}) {
  const { user = "system" } = options;
  if (!client) throw new Error("GraphQL client is required to prepare next round");
  if (!bundleId) throw new Error("Bundle ID is required");
  if (!config) throw new Error("Bundle config is required");
  if (!round) throw new Error("Round config is required");
  const currentRadius = round.radiusMiles || DEFAULT_STRATEGY_PARAMS[OFFER_STRATEGIES.CASCADING]?.initialRadiusMiles || 10;
  const radiusIncrement = DEFAULT_STRATEGY_PARAMS[OFFER_STRATEGIES.CASCADING]?.radiusIncrementMiles || 5;
  const maxRadius = DEFAULT_STRATEGY_PARAMS[OFFER_STRATEGIES.CASCADING]?.maxRadiusMiles || 50;
  const maxRounds = DEFAULT_STRATEGY_PARAMS[OFFER_STRATEGIES.CASCADING]?.maxRounds || 5;
  const nextRadius = currentRadius + radiusIncrement;
  if (nextRadius > maxRadius || round.round >= maxRounds) {
    log("prepareNextRound", "Reached max radius or rounds limit, marking round complete");
    const updatedConfig2 = { ...config };
    updatedConfig2.offerRounds = updatedConfig2.offerRounds.map((r) => {
      if (r.round === round.round) {
        return { ...r, completed: true };
      }
      return r;
    });
    await updateBundleConfig(client, bundleId, updatedConfig2, { user });
    return await advanceToNextStrategy(client, bundleId, updatedConfig2, { user });
  }
  const maxDriversPerRound = DEFAULT_STRATEGY_PARAMS[OFFER_STRATEGIES.CASCADING]?.maxDriversPerRound || 1;
  const nextRound = {
    round: round.round + 1,
    type: OFFER_STRATEGIES.CASCADING,
    radiusMiles: nextRadius,
    maxDrivers: round.maxDrivers || maxDriversPerRound,
    timeoutMinutes: round.timeoutMinutes,
    completed: false,
    offeredDrivers: []
  };
  const updatedConfig = { ...config };
  updatedConfig.offerRounds = updatedConfig.offerRounds.map((r) => {
    if (r.round === round.round) {
      return { ...r, completed: true };
    }
    return r;
  });
  updatedConfig.offerRounds.push(nextRound);
  await updateBundleConfig(client, bundleId, updatedConfig, { user });
  const bundle = await getBundleById(client, bundleId);
  if (!bundle) {
    throw new Error(`Bundle ${bundleId} not found`);
  }
  const freshConfig = bundle.config || {};
  const freshNextRound = freshConfig.offerRounds?.find(
    (r) => r.round === nextRound.round
  );
  if (!freshNextRound || freshNextRound.type !== OFFER_STRATEGIES.CASCADING) {
    log("prepareNextRound", "Next round was not added correctly, aborting");
    return {
      success: false,
      message: `Failed to add next round ${nextRound.round} correctly`
    };
  }
  log("prepareNextRound", `Starting next round ${nextRound.round} with radius ${nextRadius} miles`);
  const processResult = await processNextOfferRound(client, bundleId, { user });
  return processResult.offerResult;
}

// src/scenarios/scenarioCreation.ts
import dayjs2 from "dayjs";

// src/scenarios/gql.ts
import { gql as gql10 } from "graphql-request";
var CREATE_SCENARIO = gql10`
  mutation CreateScenario(
    $scenario_key: String!
    $status: String!
    $config: jsonb!
    $createdat: timestamptz!
    $createdby: String!
    $updatedat: timestamptz!
    $updatedby: String!
    $product_key: String
    $usecase_key: String
    $source_channel: String
    $moveLinks: [scenariotomoves_insert_input!]!
  ) {
    insert_scenarios_one(
      object: {
        scenario_key: $scenario_key
        status: $status
        config: $config
        createdat: $createdat
        createdby: $createdby
        updatedat: $updatedat
        updatedby: $updatedby
        product_key: $product_key
        usecase_key: $usecase_key
        source_channel: $source_channel
        moves: {data: $moveLinks}
      }
    ) {
      id
      scenario_key
      status
      product_key
      usecase_key
      moves {
        id
        scenario_id
        move_id
        cardinality
      }
    }
  }
`;
var GET_MOVES_FOR_SCENARIO_IDENTITY = gql10`
  query GetMovesForScenarioIdentity($moveIds: [bigint!]!) {
    moves(where: { id: { _in: $moveIds } }) {
      id
      move_type
      customer_id
      usecase_key
    }
  }
`;
var LINK_MOVE_TO_SCENARIO = gql10`
  mutation LinkMoveToScenario(
    $move_id: bigint!
    $scenario_id: bigint!
    $cardinality: smallint!
    $createdat: timestamptz!
    $createdby: String!
    $updatedat: timestamptz!
    $updatedby: String!
  ) {
    insert_scenariotomoves_one(
      object: {
        move_id: $move_id
        scenario_id: $scenario_id
        cardinality: $cardinality
        createdat: $createdat
        createdby: $createdby
        updatedat: $updatedat
        updatedby: $updatedby
      }
    ) {
      move_id
      scenario_id
      cardinality
    }
  }
`;
var LINK_SCENARIO_TO_BUNDLE = gql10`
  mutation LinkScenarioToBundle(
    $bundle_id: bigint!
    $scenario_id: bigint!
    $cardinality: smallint!
    $createdat: timestamptz!
    $createdby: String!
    $updatedat: timestamptz!
    $updatedby: String!
  ) {
    insert_bundletoscenarios_one(
      object: {
        bundle_id: $bundle_id
        scenario_id: $scenario_id
        cardinality: $cardinality
        createdat: $createdat
        createdby: $createdby
        updatedat: $updatedat
        updatedby: $updatedby
      }
    ) {
      bundle_id
      scenario_id
      cardinality
    }
  }
`;
var UPSERT_MOVES = gql10`
  mutation upsert_moves($moveObjects: [moves_insert_input!]!) {
    insert_moves(
      objects: $moveObjects
      on_conflict: {
        constraint: moves_pkey
        update_columns: [
          active
          cancel_reason
          cancel_status
          chargeable
          class
          config
          customer_id
          delivery_time
          driver_app_version
          driver_id
          driver_name
          lane_id
          lyft_flag
          lyft_trigger_id
          move_failed
          move_type
          pickup_time
          pinnable
          rate_class_override
          return_ride_id
          ride_type
          status
          sequence
          updatedat
        ]
      }
    ) {
      affected_rows
      returning {
        id
        childMoves {
          id
        }
      }
    }
  }
`;
var UPDATE_MOVE_PICKUP_AND_DELIVERY_TIME = gql10`
  mutation updatePickupAndDeliveryTime($moveId: bigint!, $pickupTime: timestamptz!, $deliveryTime: timestamptz!) {
    update_moves(where: { id: { _eq: $moveId } }, _set: { pickup_time: $pickupTime, delivery_time: $deliveryTime }) {
      returning {
        id
      }
    }
  }
`;
var INSERT_RIDEGROUPS = gql10`
  mutation insert_ridegroups($ridegroupsObjectArray: [ridegroups_insert_input!]!) {
    insert_ridegroups(objects: $ridegroupsObjectArray) {
      returning {
        id
      }
    }
  }
`;
var GET_SCENARIO = gql10`
  query GetScenario($id: bigint!) {
    scenarios_by_pk(id: $id) {
      id
      scenario_key
      config
      moves {
        move_id
      }
    }
  }
`;
var UPDATE_SCENARIO_TYPE = gql10`
  mutation UpdateScenarioType($id: bigint!, $type: String!, $updatedby: String!) {
    update_scenarios_by_pk(
      pk_columns: { id: $id }
      _set: { scenario_key: $type, updatedby: $updatedby, updatedat: "now()" }
    ) {
      id
      scenario_key
    }
  }
`;
var BATCH_LINK_MOVES_TO_SCENARIO = gql10`
  mutation batchLinkMovesToScenario($objects: [scenariotomoves_insert_input!]!) {
    insert_scenariotomoves(objects: $objects) {
      returning {
        move_id
        scenario_id
        cardinality
      }
    }
  }
`;
var UPDATE_SCENARIO_ATOMIC = gql10`
  mutation updateScenarioAtomic(
    $scenarioId: bigint!
    $type: String!
    $updatedby: String!
    $links: [scenariotomoves_insert_input!]!
    $previousMoveIds: [bigint!]!
  ) {
    delete_scenariotomoves(where: { scenario_id: { _eq: $scenarioId } }) {
      affected_rows
    }
    update_scenarios_by_pk(
      pk_columns: { id: $scenarioId }
      _set: { scenario_key: $type, updatedby: $updatedby, updatedat: "now()" }
    ) {
      id
      scenario_key
    }
    insert_scenariotomoves(objects: $links) {
      affected_rows
    }
    update_moves(
      where: {
        id: { _in: $previousMoveIds }
        move_type: { _eq: "ride" }
        active: { _eq: 1 }
        status: { _is_null: true }
        _not: { scenariotomoves: {} }
      }
      _set: { active: 0 }
    ) {
      affected_rows
      returning {
        id
      }
    }
  }
`;

// src/scenarios/utils.ts
var validateMoveScenarioInputs = (client, move, isShell = false) => {
  if (!client) throw new Error("GraphQL client is required!");
  if (!move) throw new Error("Move is required!");
  if (!isShell) {
    if (!move.id) throw new Error("Move is required and must already be persisted!");
  }
};
var validateMoveWithRideScenarioInputs = (client, move, isShell = false) => {
  if (!client) throw new Error("GraphQL client is required!");
  if (!move) throw new Error("Move is required!");
  if (!isShell) {
    if (!move.id) throw new Error("Move is required and must already be persisted!");
  }
};
var validateMoveWithTwoRidesScenarioInputs = (client, move, rideMove1, rideMove2, isShell = false) => {
  if (!client) throw new Error("GraphQL client is required!");
  if (!move) throw new Error("Move is required!");
  if (!rideMove1) throw new Error("First ride move is required!");
  if (!rideMove2) throw new Error("Second ride move is required!");
  if (!isShell) {
    if (!move.id) throw new Error("Move is required and must already be persisted!");
    if (!rideMove1.id)
      throw new Error("First ride move is required and must already be persisted!");
    if (!rideMove2.id)
      throw new Error("Second ride move is required and must already be persisted!");
  }
};
var validatePairedMoveScenarioInputs = (client, parentMove, childMove, isShell = false) => {
  if (!client) throw new Error("GraphQL client is required!");
  if (!parentMove) throw new Error("Parent move is required!");
  if (!childMove) throw new Error("Child move is required!");
  if (!isShell) {
    if (!parentMove.id) throw new Error("Parent move is required and must already be persisted!");
    if (!childMove.id) throw new Error("Child move is required and must already be persisted!");
  }
};
var buildMoveWithDefaults = (move) => {
  return {
    ...move,
    chargeable: move.chargeable ?? true,
    driver_app_version: move.driver_app_version || "3.0.0",
    workflowset_id: move.workflowset_id || 1,
    // company default workflowset
    pickup_workflow_id: move.pickup_workflow_id || 1,
    // company default pickup workflow
    delivery_workflow_id: move.delivery_workflow_id || 2
    // company default delivery workflow
  };
};

// src/scenarios/scenarioCreation.ts
function dedupeMoveIds(moveIds) {
  return [...new Set(moveIds)];
}
function scenarioIdentityOptions(options) {
  const { productKey, usecaseKey, sourceChannel, resolveProductKey } = options ?? {};
  return { productKey, usecaseKey, sourceChannel, resolveProductKey };
}
async function resolveScenarioIdentity(client, moveIds, options) {
  const { productKey = null, usecaseKey = null, resolveProductKey = null } = options ?? {};
  if (usecaseKey && (productKey || !resolveProductKey)) return { productKey, usecaseKey };
  let legs = [];
  try {
    const res = await client.request(GET_MOVES_FOR_SCENARIO_IDENTITY, { moveIds });
    const byId = new Map((res?.moves ?? []).map((leg) => [String(leg.id), leg]));
    legs = moveIds.map((id) => byId.get(String(id))).filter(Boolean);
  } catch (error) {
    log("resolveScenarioIdentity", `Could not read moves ${moveIds.join(", ")}: ${error?.message}`);
  }
  const drives = legs.filter((leg) => leg.move_type === MOVE_TYPES.DRIVE);
  const resolvedUsecaseKey = usecaseKey ?? drives.find((leg) => leg.usecase_key)?.usecase_key ?? null;
  const customerId = (drives.find((leg) => leg.customer_id) ?? legs.find((leg) => leg.customer_id))?.customer_id ?? null;
  let resolvedProductKey = productKey;
  if (!resolvedProductKey && resolveProductKey && customerId && resolvedUsecaseKey) {
    try {
      resolvedProductKey = await resolveProductKey({ customerId, usecaseKey: resolvedUsecaseKey, moveIds }) ?? null;
    } catch (error) {
      log(
        "resolveScenarioIdentity",
        `No product_key for moves ${moveIds.join(", ")} (customer ${customerId}, use case ${resolvedUsecaseKey}): ${error?.message}`
      );
    }
  }
  return { productKey: resolvedProductKey, usecaseKey: resolvedUsecaseKey };
}
var updateMovePickupAndDeliveryTime = async (client, moveId, pickupTime, deliveryTime) => {
  if (pickupTime) {
    try {
      await client.request(UPDATE_MOVE_PICKUP_AND_DELIVERY_TIME, {
        moveId,
        pickupTime,
        deliveryTime
      });
    } catch (error) {
      log(
        "Unable to update pickup time for move. Continuing with scenario creation...",
        `Error: ${error?.message}`
      );
    }
  }
};
var addScenarioToBundle = async (client, bundleId, scenarioId, options = {}) => {
  const { cardinality = 0, user = "system" } = options;
  if (!client) throw new Error("GraphQL client is required!");
  if (!bundleId) throw new Error("Bundle ID is required!");
  if (!scenarioId) throw new Error("Scenario ID is required!");
  try {
    const now = (/* @__PURE__ */ new Date()).toISOString();
    const variables = {
      bundle_id: bundleId,
      scenario_id: scenarioId,
      cardinality,
      createdat: now,
      createdby: user,
      updatedat: now,
      updatedby: user
    };
    log("addScenarioToBundle", `Adding scenario ${scenarioId} to bundle ${bundleId}`);
    const result = await client.request(LINK_SCENARIO_TO_BUNDLE, variables);
    if (!result?.insert_bundletoscenarios_one) throw new Error("Failed to add scenario to bundle");
    return result.insert_bundletoscenarios_one;
  } catch (error) {
    log("Failed to add scenario to bundle.", `Error: ${error?.message}`);
    throw new Error(`Failed to add scenario to bundle: ${error?.message}`);
  }
};
var assignMoveToRideGroup = async (client, moveId, rideGroupId) => {
  const rideGroup = {
    group_id: rideGroupId,
    drive_move_id: moveId
  };
  try {
    const updatedRideGroup = await client.request(INSERT_RIDEGROUPS, {
      ridegroupsObjectArray: [rideGroup]
    });
    return updatedRideGroup?.insert_ridegroups?.returning[0]?.id;
  } catch (error) {
    log("assignMovesToRideGroup", `Error: ${error}`);
    throw new Error(`Failed to assign moves to ride group: ${error?.message}`);
  }
};
var createRide = async (client, driveMove, overrides, config = {}) => {
  const { ridePosition = RIDE_POSITION.AFTER, rideGroupId = null } = config;
  let rideMoveInput = { ...overrides };
  if (!rideMoveInput?.pickup_time) {
    logger.info("createRide debug:", {
      driveMove_pickup_time: driveMove?.pickup_time,
      lane_duration_sec: driveMove?.lane?.duration_sec,
      ridePosition,
      lane_id: driveMove?.lane?.id,
      lane_inverse_id: driveMove?.lane?.inverse?.id
    });
    const durationSec = driveMove?.lane?.duration_sec;
    if (durationSec === null || durationSec === void 0 || isNaN(durationSec)) {
      throw new Error(
        `Invalid or missing lane duration_sec: ${durationSec}. Cannot calculate ride pickup time.`
      );
    }
    if (!driveMove?.pickup_time) {
      throw new Error(`Drive move pickup_time is missing or invalid: ${driveMove?.pickup_time}`);
    }
    rideMoveInput.pickup_time = ridePosition === RIDE_POSITION.AFTER ? dayjs2(driveMove?.pickup_time).add(durationSec, "seconds").format() : dayjs2(driveMove?.pickup_time).subtract(durationSec, "seconds").format();
    logger.info("createRide calculated pickup_time:", rideMoveInput.pickup_time);
  }
  if (!rideMoveInput?.lane_id) {
    rideMoveInput.lane_id = driveMove?.lane?.inverse?.id;
  }
  if (!rideMoveInput?.move_type) rideMoveInput.move_type = MOVE_TYPES.RIDE;
  if (!rideMoveInput?.ride_type) rideMoveInput.ride_type = RIDE_TYPES.AUTO;
  if (!rideMoveInput?.parent_move_id) rideMoveInput.parent_move_id = driveMove?.id;
  if (!rideMoveInput?.customer_id) rideMoveInput.customer_id = driveMove?.customer?.id;
  if (!rideMoveInput?.driver_app_version)
    rideMoveInput.driver_app_version = driveMove?.driver_app_version;
  if (!rideMoveInput?.config) rideMoveInput.config = driveMove?.config;
  try {
    const rideMoveRes = await client.request(UPSERT_MOVES, {
      moveObjects: [rideMoveInput]
    });
    if (gqlErrors(rideMoveRes?.insert_moves) || !rideMoveRes.insert_moves.returning[0]) {
      log("createScenarioWithMoves", "Failed to upsert ride move", { result: rideMoveRes });
      throw new Error("Failed to upsert ride move!");
    }
    if (rideGroupId) {
      try {
        await assignMoveToRideGroup(client, driveMove?.id, rideGroupId);
      } catch (error) {
        log(
          "Unable to assign move to ride group. Continuing with scenario creation...",
          `Error: ${error?.message}`
        );
      }
    }
    const returnRideId = rideMoveRes.insert_moves.returning[0].id;
    return returnRideId;
  } catch (error) {
    log("Unable to create ride. Continuing with scenario creation...", `Error: ${error}`);
    throw new Error(`Failed to create ride: ${error?.message}`);
  }
};
var createScenarioWithMoves = async (client, options) => {
  const {
    type,
    status = SCENARIO_STATUS.NEW,
    config = {},
    moves = [],
    user = "system",
    sourceChannel = null
  } = options;
  if (!client) throw new Error("GraphQL client is required!");
  if (!type) throw new Error("Scenario type is required!");
  if (!Array.isArray(moves)) throw new Error("Moves must be an array!");
  if (moves.length === 0) throw new Error("Must provide at least one move!");
  if (!VALID_SCENARIO_TYPES.includes(type)) {
    throw new Error(`Invalid scenario type! Must be one of: ${VALID_SCENARIO_TYPES.join(", ")}.`);
  }
  if (!VALID_SCENARIO_STATUSES.includes(status)) {
    throw new Error(
      `Invalid scenario status! Must be one of: ${VALID_SCENARIO_STATUSES.join(", ")}.`
    );
  }
  try {
    const { productKey, usecaseKey } = await resolveScenarioIdentity(client, dedupeMoveIds(moves), options);
    const moveLinks = dedupeMoveIds(moves).map((moveId, index) => ({
      move_id: moveId,
      cardinality: index,
      // Use array index as cardinality to maintain order
      createdat: "now()",
      createdby: user,
      updatedat: "now()",
      updatedby: user
    }));
    const scenarioVariables = {
      scenario_key: type,
      status,
      config,
      createdat: "now()",
      createdby: user,
      updatedat: "now()",
      updatedby: user,
      product_key: productKey,
      usecase_key: usecaseKey,
      source_channel: sourceChannel,
      moveLinks
    };
    log("createScenarioWithMoves", `Creating scenario of type ${type}`);
    const scenarioResult = await client.request(CREATE_SCENARIO, scenarioVariables);
    if (!scenarioResult?.insert_scenarios_one?.id) throw new Error("Failed to create scenario");
    const scenarioId = scenarioResult.insert_scenarios_one.id;
    log("createScenarioWithMoves", `Created scenario with ID ${scenarioId}`);
    return {
      scenario: {
        id: scenarioId,
        status,
        scenario_key: type,
        product_key: productKey,
        usecase_key: usecaseKey,
        moves
      }
    };
  } catch (error) {
    log("Failed to create scenario.", `Error: ${error?.message}`);
    throw new Error(`Failed to create scenario: ${error?.message}`);
  }
};
var updateScenario = async (client, scenarioId, options) => {
  const { moves = [], type, user = "system" } = options;
  if (!client) throw new Error("GraphQL client is required!");
  if (!scenarioId) throw new Error("Scenario ID is required!");
  if (!type) throw new Error("Scenario type is required!");
  if (!Array.isArray(moves)) throw new Error("Moves must be an array!");
  if (moves.length === 0) throw new Error("Must provide at least one move!");
  if (!VALID_SCENARIO_TYPES.includes(type)) {
    throw new Error(`Invalid scenario type! Must be one of: ${VALID_SCENARIO_TYPES.join(", ")}.`);
  }
  try {
    const existingScenario = await client.request(GET_SCENARIO, { id: scenarioId });
    if (!existingScenario?.scenarios_by_pk) {
      throw new Error("Could not find existing scenario");
    }
    const dedupedMoves = dedupeMoveIds(moves);
    const links = dedupedMoves.map((moveId, index) => ({
      move_id: moveId,
      scenario_id: scenarioId,
      cardinality: index,
      createdat: "now()",
      createdby: user,
      updatedat: "now()",
      updatedby: user
    }));
    const previousMoveIds = (existingScenario.scenarios_by_pk.moves ?? []).map((link) => link.move_id);
    const result = await client.request(UPDATE_SCENARIO_ATOMIC, {
      scenarioId,
      type,
      updatedby: user,
      links,
      previousMoveIds
    });
    if (!result?.update_scenarios_by_pk?.id) {
      throw new Error("Failed to update scenario type");
    }
    if (!result?.update_moves) {
      throw new Error("the deactivation half of updateScenarioAtomic did not run");
    }
    const held = new Set(previousMoveIds.map((id) => String(id)));
    const strangers = (result.update_moves.returning ?? []).map((move) => String(move.id)).filter((id) => !held.has(id));
    if (strangers.length > 0) {
      throw new Error(
        `deactivated ${strangers.length} move(s) this scenario did not ask about: ${strangers.join(", ")}`
      );
    }
    if (result.update_moves.affected_rows > 0) {
      log(
        "updateScenario",
        `Deactivated ${result.update_moves.affected_rows} ride(s) left with no scenario by the rewrite of ${scenarioId}`
      );
    }
    log(
      "updateScenario",
      `Atomically set scenario ${scenarioId} to type ${type} with ${links.length} move link(s)`
    );
    return {
      id: scenarioId,
      scenario_key: type,
      moves: dedupedMoves
    };
  } catch (error) {
    log("Failed to update scenario.", `Error: ${error?.message}`);
    throw new Error(`Failed to update scenario: ${error?.message}`);
  }
};
var createConciergePickupScenario = async (client, move, rideMove, options) => {
  validateMoveWithRideScenarioInputs(client, move, rideMove);
  const { user } = options;
  try {
    const rideMoveId = await createRide(client, move, rideMove, {
      ridePosition: RIDE_POSITION.BEFORE
    });
    const scenario = await createScenarioWithMoves(client, {
      type: SCENARIO_TYPES.CONCIERGE_PICKUP,
      status: SCENARIO_STATUS.NEW,
      config: {},
      moves: [rideMoveId, move.id],
      user,
      ...scenarioIdentityOptions(options)
    });
    return scenario;
  } catch (err) {
    log("Unable to create concierge pickup scenario.", `Error: ${err?.message}`);
    throw new Error(`Unable to create concierge pickup scenario. Error: ${err?.message}`);
  }
};
var createConciergeReturnScenario = async (client, move, options) => {
  validateMoveWithRideScenarioInputs(client, move);
  const { user } = options;
  try {
    const rideMoveId = await createRide(
      client,
      move,
      {},
      {
        ridePosition: RIDE_POSITION.AFTER
      }
    );
    const scenario = await createScenarioWithMoves(client, {
      type: SCENARIO_TYPES.CONCIERGE_RETURN,
      status: SCENARIO_STATUS.NEW,
      config: {},
      moves: [move.id, rideMoveId],
      user,
      ...scenarioIdentityOptions(options)
    });
    return scenario;
  } catch (err) {
    log("Unable to create concierge return scenario.", `Error: ${err?.message}`);
    throw new Error(`Unable to create concierge return scenario. Error: ${err?.message}`);
  }
};
var createConciergePickupNoRideshareScenario = async (client, move, options) => {
  validateMoveScenarioInputs(client, move);
  const { user } = options;
  try {
    const scenario = await createScenarioWithMoves(client, {
      type: SCENARIO_TYPES.CONCIERGE_PICKUP_NO_RIDESHARE,
      status: SCENARIO_STATUS.NEW,
      config: {},
      moves: [move.id],
      user,
      ...scenarioIdentityOptions(options)
    });
    return scenario;
  } catch (err) {
    log(
      "Unable to create concierge pickup no rideshare scenario.",
      `Error: ${err?.message}`
    );
    throw new Error(
      `Unable to create concierge pickup no rideshare scenario. Error: ${err?.message}`
    );
  }
};
var createConciergeReturnNoRideshareScenario = async (client, move, options) => {
  validateMoveScenarioInputs(client, move);
  const { user } = options;
  try {
    const scenario = await createScenarioWithMoves(client, {
      type: SCENARIO_TYPES.CONCIERGE_RETURN_NO_RIDESHARE,
      status: SCENARIO_STATUS.NEW,
      config: {},
      moves: [move.id],
      user,
      ...scenarioIdentityOptions(options)
    });
    return scenario;
  } catch (err) {
    log(
      "Unable to create concierge return no rideshare scenario.",
      `Error: ${err?.message}`
    );
    throw new Error(
      `Unable to create concierge return no rideshare scenario. Error: ${err?.message}`
    );
  }
};
var createConciergeLoanerPickupScenario = async (client, loanerMove, customerMove, options) => {
  validatePairedMoveScenarioInputs(client, loanerMove, customerMove);
  const { user } = options;
  try {
    const scenario = await createScenarioWithMoves(client, {
      type: SCENARIO_TYPES.CONCIERGE_LOANER_PICKUP,
      status: SCENARIO_STATUS.NEW,
      config: {},
      moves: [loanerMove.id, customerMove.id],
      user,
      ...scenarioIdentityOptions(options)
    });
    return scenario;
  } catch (err) {
    log("Unable to create concierge+loaner pickup scenario.", `Error: ${err?.message}`);
    throw new Error(`Unable to create concierge+loaner pickup scenario. Error: ${err?.message}`);
  }
};
var createConciergeLoanerReturnScenario = async (client, loanerMove, options) => {
  const { user } = options;
  try {
    const scenario = await createScenarioWithMoves(client, {
      type: SCENARIO_TYPES.CONCIERGE_LOANER_RETURN,
      status: SCENARIO_STATUS.NEW,
      config: {},
      moves: [loanerMove.parent_move_id, loanerMove.id],
      user,
      ...scenarioIdentityOptions(options)
    });
    return scenario;
  } catch (err) {
    log("Unable to create concierge+loaner return scenario.", `Error: ${err?.message}`);
    throw new Error(`Unable to create concierge+loaner return scenario. Error: ${err?.message}`);
  }
};
var createStrandedWithRideBeforeScenario = async (client, move, rideMove, options) => {
  validateMoveWithRideScenarioInputs(client, move, rideMove);
  const { user } = options;
  try {
    const rideMoveId = await createRide(client, move, rideMove, {
      ridePosition: RIDE_POSITION.BEFORE
    });
    const scenario = await createScenarioWithMoves(client, {
      type: SCENARIO_TYPES.STRANDED_RIDESHARE_BEFORE,
      status: SCENARIO_STATUS.NEW,
      config: {},
      moves: [rideMoveId, move.id],
      user,
      ...scenarioIdentityOptions(options)
    });
    return scenario;
  } catch (err) {
    log("Unable to create stranded with ride before scenario.", `Error: ${err?.message}`);
    throw new Error(`Unable to create stranded with ride before scenario. Error: ${err?.message}`);
  }
};
var createStrandedWithRideAfterScenario = async (client, move, rideMove, options) => {
  validateMoveWithRideScenarioInputs(client, move, rideMove);
  const { user } = options;
  try {
    const rideMoveId = await createRide(client, move, rideMove, {
      ridePosition: RIDE_POSITION.AFTER
    });
    const scenario = await createScenarioWithMoves(client, {
      type: SCENARIO_TYPES.STRANDED_RIDESHARE_AFTER,
      status: SCENARIO_STATUS.NEW,
      config: {},
      moves: [move.id, rideMoveId],
      user,
      ...scenarioIdentityOptions(options)
    });
    return scenario;
  } catch (err) {
    log("Unable to create stranded with ride after scenario.", `Error: ${err?.message}`);
    throw new Error(`Unable to create stranded with ride after scenario. Error: ${err?.message}`);
  }
};
var createStrandedWithRideBothScenario = async (client, move, beforeRideMove, afterRideMove, options) => {
  validateMoveWithTwoRidesScenarioInputs(client, move, beforeRideMove, afterRideMove);
  const { user } = options;
  try {
    const beforeRideMoveId = await createRide(client, move, beforeRideMove, {
      ridePosition: RIDE_POSITION.BEFORE
    });
    const afterRideMoveId = await createRide(client, move, afterRideMove, {
      ridePosition: RIDE_POSITION.AFTER
    });
    const scenario = await createScenarioWithMoves(client, {
      type: SCENARIO_TYPES.STRANDED_RIDESHARE_BOTH,
      status: SCENARIO_STATUS.NEW,
      config: {},
      moves: [beforeRideMoveId, move.id, afterRideMoveId],
      user,
      ...scenarioIdentityOptions(options)
    });
    return scenario;
  } catch (err) {
    log("Unable to create stranded with rides before & after scenario.", `Error: ${err?.message}`);
    throw new Error(
      `Unable to create stranded with rides before & after scenario. Error: ${err?.message}`
    );
  }
};
var createTriangleScenario = async (client, move, beforeRideMove, afterRideMove, options) => {
  validateMoveWithTwoRidesScenarioInputs(client, move, beforeRideMove, afterRideMove);
  const { user } = options;
  try {
    const beforeRideMoveId = await createRide(client, move, beforeRideMove, {
      ridePosition: RIDE_POSITION.BEFORE
    });
    const afterRideMoveId = await createRide(client, move, afterRideMove, {
      ridePosition: RIDE_POSITION.AFTER
    });
    const scenario = await createScenarioWithMoves(client, {
      type: SCENARIO_TYPES.TRIANGLE,
      status: SCENARIO_STATUS.NEW,
      config: {},
      moves: [beforeRideMoveId, move.id, afterRideMoveId],
      user,
      ...scenarioIdentityOptions(options)
    });
    return scenario;
  } catch (err) {
    log("Unable to create triangle scenario.", `Error: ${err?.message}`);
    throw new Error(`Unable to create triangle scenario. Error: ${err?.message}`);
  }
};
var createStrandedWithShuttleAfterScenario = async (client, move, options) => {
  validateMoveScenarioInputs(client, move);
  const { pickupTime, deliveryTime, rideGroupId, user } = options;
  if (pickupTime || deliveryTime) {
    await updateMovePickupAndDeliveryTime(client, move.id, pickupTime, deliveryTime);
    move.pickup_time = pickupTime || move.pickup_time;
    move.delivery_time = deliveryTime || move.delivery_time;
  }
  try {
    const afterRideId = await createRide(
      client,
      move,
      { ride_type: RIDE_TYPES.SHUTTLE },
      {
        ridePosition: RIDE_POSITION.AFTER,
        rideGroupId
      }
    );
    const scenario = await createScenarioWithMoves(client, {
      type: SCENARIO_TYPES.STRANDED_SHUTTLE_AFTER,
      status: SCENARIO_STATUS.NEW,
      config: {},
      moves: [move.id, afterRideId],
      user,
      ...scenarioIdentityOptions(options)
    });
    return scenario;
  } catch (err) {
    log("Unable to create stranded with shuttle after scenario.", `Error: ${err?.message}`);
    throw new Error(
      `Unable to create stranded with shuttle after scenario. Error: ${err?.message}`
    );
  }
};
var createRoundTripScenario = async (client, parentMove, childMove, options) => {
  validatePairedMoveScenarioInputs(client, parentMove, childMove);
  const { user } = options;
  try {
    const scenario = await createScenarioWithMoves(client, {
      type: SCENARIO_TYPES.ROUND_TRIP,
      status: SCENARIO_STATUS.NEW,
      config: {},
      moves: [parentMove.id, childMove.id],
      user,
      ...scenarioIdentityOptions(options)
    });
    return scenario;
  } catch (err) {
    log("Unable to create round-trip scenario.", `Error: ${err?.message}`);
    throw new Error(`Unable to create round-trip scenario. Error: ${err?.message}`);
  }
};
var createRoundTripFlexScenario = async (client, parentMove, childMove, options) => {
  validatePairedMoveScenarioInputs(client, parentMove, childMove);
  const { user } = options;
  try {
    const scenario = await createScenarioWithMoves(client, {
      type: SCENARIO_TYPES.ROUND_TRIP_FLEX,
      status: SCENARIO_STATUS.NEW,
      config: {},
      moves: [parentMove.id, childMove.id],
      user,
      ...scenarioIdentityOptions(options)
    });
    return scenario;
  } catch (err) {
    log("Unable to create round-trip flex scenario.", `Error: ${err?.message}`);
    throw new Error(`Unable to create round-trip flex scenario. Error: ${err?.message}`);
  }
};
var createShuttleDriveScenario = async (client, parentMove, childMove, options) => {
  validatePairedMoveScenarioInputs(client, parentMove, childMove, true);
  const { user } = options;
  let formattedParentMove = buildMoveWithDefaults(parentMove);
  let formattedChildMove = buildMoveWithDefaults(childMove);
  const insertableMoves = [formattedParentMove];
  insertableMoves[0].childMoves = { data: [formattedChildMove] };
  try {
    const insertResult = await client.request(UPSERT_MOVES, {
      moveObjects: insertableMoves
    });
    if (gqlErrors(insertResult?.insert_moves) || !insertResult?.insert_moves?.returning?.[0]) {
      log("createShuttleDriveScenario", "Failed to insert moves", { result: insertResult });
      throw new Error("Failed to insert moves for shuttle drive scenario!");
    }
    const parentMove2 = insertResult?.insert_moves?.returning?.[0];
    const childMove2 = insertResult?.insert_moves?.returning?.[0]?.childMoves?.[0];
    const scenario = await createScenarioWithMoves(client, {
      type: SCENARIO_TYPES.SHUTTLE_DRIVE,
      status: SCENARIO_STATUS.NEW,
      config: {},
      moves: [parentMove2?.id, childMove2?.id],
      user,
      ...scenarioIdentityOptions(options)
    });
    return scenario;
  } catch (err) {
    log("Unable to create shuttle drive scenario.", `Error: ${err?.message}`);
    throw new Error(`Unable to create shuttle drive scenario. Error: ${err?.message}`);
  }
};
var createShuttleLinkerScenario = async (client, move, options) => {
  validateMoveScenarioInputs(client, move, true);
  const { user } = options;
  let formattedMove = buildMoveWithDefaults(move);
  const insertableMoves = [formattedMove];
  try {
    const insertResult = await client.request(UPSERT_MOVES, {
      moveObjects: insertableMoves
    });
    if (gqlErrors(insertResult?.insert_moves) || !insertResult?.insert_moves?.returning?.[0]) {
      log("createShuttleLinkerScenario", "Failed to insert moves", { result: insertResult });
      throw new Error("Failed to insert moves for shuttle linker scenario!");
    }
    const move2 = insertResult?.insert_moves?.returning?.[0];
    const scenario = await createScenarioWithMoves(client, {
      type: SCENARIO_TYPES.SHUTTLE_LINKER,
      status: SCENARIO_STATUS.NEW,
      config: {},
      moves: [move2?.id],
      user,
      ...scenarioIdentityOptions(options)
    });
    return scenario;
  } catch (err) {
    log("Unable to create shuttle linker scenario.", `Error: ${err?.message}`);
    throw new Error(`Unable to create shuttle linker scenario. Error: ${err?.message}`);
  }
};

// src/scenarios/scenarioManagement.ts
import { gql as gql11 } from "graphql-request";
var GET_SCENARIO_BY_ID = gql11`
  query getScenarioById($scenarioId: bigint!) {
    scenarios_by_pk(id: $scenarioId) {
      id
      scenario_key
      status
      config
      workflow_data
      createdby
      createdat
      updatedby
      updatedat
    }
  }
`;
var GET_SCENARIO_WITH_MOVES = gql11`
  query getScenarioWithMoves($scenarioId: bigint!) {
    scenarios_by_pk(id: $scenarioId) {
      id
      scenario_key
      status
      config
      workflow_data
      createdby
      createdat
      updatedby
      updatedat
      moves {
        move_id
        cardinality
        move {
          id
          type: move_type
          driver_id
          lane {
            origin_location_id
            pickup {
              latitude
              longitude
              nickname
              address
            }
            destination_location_id
            delivery {
              latitude
              longitude
              nickname
              address
            }
          }
        }
      }
    }
  }
`;
var UPDATE_SCENARIO_STATUS = gql11`
  mutation updateScenarioStatus($id: bigint!, $status: String!, $user: String!) {
    update_scenarios_by_pk(
      pk_columns: { id: $id }
      _set: { status: $status, updatedat: "now()", updatedby: $user }
    ) {
      id
      status
      updatedat
      updatedby
    }
  }
`;
var UPDATE_SCENARIO_STATUSES = gql11`
  mutation updateScenarioStatuses($ids: [bigint!]!, $status: String!, $user: String!) {
    update_scenarios(
      where: { id: { _in: $ids } }
      _set: { status: $status, updatedat: "now()", updatedby: $user }
    ) {
      affected_rows
      returning {
        id
        status
        updatedat
        updatedby
      }
    }
  }
`;
var UPDATE_SCENARIO_CONFIG = gql11`
  mutation updateScenarioConfig($id: bigint!, $config: jsonb!, $user: String!) {
    update_scenarios_by_pk(
      pk_columns: { id: $id }
      _set: { config: $config, updatedat: "now()", updatedby: $user }
    ) {
      id
      config
      updatedat
      updatedby
    }
  }
`;
var UPDATE_SCENARIO_WORKFLOW_DATA = gql11`
  mutation updateScenarioWorkflowData($id: bigint!, $workflow_data: jsonb!, $user: String!) {
    update_scenarios_by_pk(
      pk_columns: { id: $id }
      _set: { workflow_data: $workflow_data, updatedat: "now()", updatedby: $user }
    ) {
      id
      workflow_data
      updatedat
      updatedby
    }
  }
`;
var UPDATE_BUNDLE_SCENARIOS_STATUS = gql11`
  mutation updateBundleScenariosStatus($bundleId: bigint!, $status: String!, $user: String!) {
    update_scenarios(
      where: {
        bundletoscenarios: { bundle_id: { _eq: $bundleId } }
        status: { _neq: "deprecated" }
      }
      _set: { status: $status, updatedby: $user }
    ) {
      returning {
        id
        status
      }
    }
  }
`;
var GET_SCENARIO_BUNDLE = gql11`
  query getScenarioBundle($scenarioId: bigint!) {
    bundletoscenarios(where: { scenario_id: { _eq: $scenarioId } }) {
      bundle {
        id
        scenarios {
          scenario {
            id
            status
          }
        }
        assigned_offer_id
        offers {
          id
          bundle_id
          driver_id
          status
        }
      }
    }
  }
`;
async function getScenarioById({ client, scenarioId }) {
  if (!client) {
    throw new Error("GraphQL client is required to get a scenario");
  }
  if (!scenarioId) {
    throw new Error("Scenario ID is required");
  }
  try {
    const variables = { scenarioId };
    const result = await client.request(GET_SCENARIO_BY_ID, variables);
    return result.scenarios_by_pk;
  } catch (error) {
    log("getScenarioById", `Error getting scenario ${scenarioId}: ${error.message}`);
    throw new Error(`Failed to get scenario: ${error.message}`);
  }
}
async function getScenarioWithMoves(client, scenarioId) {
  if (!client) {
    throw new Error("GraphQL client is required to get scenario details");
  }
  if (!scenarioId) {
    throw new Error("Scenario ID is required");
  }
  try {
    const variables = { scenarioId };
    const result = await client.request(GET_SCENARIO_WITH_MOVES, variables);
    return result.scenarios_by_pk;
  } catch (error) {
    log("getScenarioWithMoves", `Error getting scenario details ${scenarioId}: ${error.message}`);
    throw new Error(`Failed to get scenario details: ${error.message}`);
  }
}
async function updateScenarioStatus({ client, scenarioId, status, user = "system" }) {
  if (!client) {
    throw new Error("GraphQL client is required to update scenario status");
  }
  if (!scenarioId) {
    throw new Error("Scenario ID is required");
  }
  if (!VALID_SCENARIO_STATUSES.includes(status)) {
    throw new Error(`Invalid status. Must be one of: ${VALID_SCENARIO_STATUSES.join(", ")}`);
  }
  log("updateScenarioStatus", `Updating scenario ${scenarioId} status to ${status}`);
  try {
    const variables = {
      id: scenarioId,
      status,
      user
    };
    const result = await client.request(UPDATE_SCENARIO_STATUS, variables);
    if (!result?.update_scenarios_by_pk?.id) {
      throw new Error(`Failed to update scenario status`);
    }
    return result.update_scenarios_by_pk;
  } catch (error) {
    log("updateScenarioStatus", `Error updating scenario ${scenarioId} status: ${error.message}`);
    throw new Error(`Failed to update scenario status: ${error.message}`);
  }
}
async function updateScenarioStatuses({ client, scenarioIds, status, user = "system" }) {
  if (!client) {
    throw new Error("GraphQL client is required to update scenario status");
  }
  if (!scenarioIds || scenarioIds.length === 0) {
    throw new Error("Scenario IDs are required");
  }
  if (!VALID_SCENARIO_STATUSES.includes(status)) {
    throw new Error(`Invalid status. Must be one of: ${VALID_SCENARIO_STATUSES.join(", ")}`);
  }
  log("updateScenarioStatus", `Updating scenario statuses to ${status}`);
  try {
    const variables = {
      ids: scenarioIds,
      status,
      user
    };
    const result = await client.request(UPDATE_SCENARIO_STATUSES, variables);
    if (!result?.update_scenarios?.affected_rows) {
      throw new Error(`Failed to update scenario status`);
    }
    return result.update_scenarios.returning;
  } catch (error) {
    log("updateScenarioStatus", `Error updating scenario statuses: ${error.message}`);
    throw new Error(`Failed to update scenario statuses: ${error.message}`);
  }
}
async function updateBundleScenariosStatus({ client, bundleId, status, user = "system" }) {
  if (!client) {
    throw new Error("GraphQL client is required to update bundle scenarios");
  }
  if (!bundleId) {
    throw new Error("Bundle ID is required");
  }
  if (!VALID_SCENARIO_STATUSES.includes(status)) {
    throw new Error(`Invalid status. Must be one of: ${VALID_SCENARIO_STATUSES.join(", ")}`);
  }
  log(
    "updateBundleScenariosStatus",
    `Updating all scenarios in bundle ${bundleId} to status ${status}`
  );
  try {
    const variables = {
      bundleId,
      status,
      user
    };
    const result = await client.request(UPDATE_BUNDLE_SCENARIOS_STATUS, variables);
    const bundleScenarios = result.update_bundletoscenarios;
    if (!bundleScenarios?.affected_rows) {
      log("updateBundleScenariosStatus", `No scenarios found in bundle ${bundleId}`);
      return [];
    }
    const updatedScenarios = bundleScenarios.returning.map((r) => r.scenario);
    log(
      "updateBundleScenariosStatus",
      `Successfully updated ${updatedScenarios.length} scenarios in bundle ${bundleId}`
    );
    return updatedScenarios;
  } catch (error) {
    log(
      "updateBundleScenariosStatus",
      `Error updating scenarios in bundle ${bundleId}: ${error.message}`
    );
    throw new Error(`Failed to update bundle scenarios: ${error.message}`);
  }
}
async function updateScenarioConfig({ client, scenarioId, config, user = "system" }) {
  if (!client) {
    throw new Error("GraphQL client is required to update scenario config");
  }
  if (!scenarioId) {
    throw new Error("Scenario ID is required");
  }
  if (!config) {
    throw new Error("Config object is required");
  }
  log("updateScenarioConfig", `Updating config for scenario ${scenarioId}`);
  try {
    const variables = {
      id: scenarioId,
      config,
      user
    };
    const result = await client.request(UPDATE_SCENARIO_CONFIG, variables);
    if (!result?.update_scenarios_by_pk?.id) {
      throw new Error(`Failed to update scenario config`);
    }
    return result.update_scenarios_by_pk;
  } catch (error) {
    log("updateScenarioConfig", `Error updating scenario ${scenarioId} config: ${error.message}`);
    throw new Error(`Failed to update scenario config: ${error.message}`);
  }
}
async function updateScenarioWorkflowData({
  client,
  scenarioId,
  workflowData,
  user = "system"
}) {
  if (!client) {
    throw new Error("GraphQL client is required to update scenario workflow data");
  }
  if (!scenarioId) {
    throw new Error("Scenario ID is required");
  }
  if (!workflowData) {
    throw new Error("Workflow data object is required");
  }
  log("updateScenarioWorkflowData", `Updating workflow data for scenario ${scenarioId}`);
  try {
    const variables = {
      id: scenarioId,
      workflow_data: workflowData,
      user
    };
    const result = await client.request(UPDATE_SCENARIO_WORKFLOW_DATA, variables);
    if (!result?.update_scenarios_by_pk?.id) {
      throw new Error(`Failed to update scenario workflow data`);
    }
    return result.update_scenarios_by_pk;
  } catch (error) {
    log(
      "updateScenarioWorkflowData",
      `Error updating scenario ${scenarioId} workflow data: ${error.message}`
    );
    throw new Error(`Failed to update scenario workflow data: ${error.message}`);
  }
}
async function getScenarioBundle(client, scenarioId) {
  if (!client) {
    throw new Error("GraphQL client is required to get scenario bundle");
  }
  if (!scenarioId) {
    throw new Error("Scenario ID is required");
  }
  const variables = { scenarioId };
  const result = await client.request(GET_SCENARIO_BUNDLE, variables);
  const foundBundle = result?.bundletoscenarios?.[0]?.bundle;
  if (!foundBundle) {
    throw new Error("Bundle not found for scenario!");
  }
  return foundBundle;
}

// src/scenarios/scenarioConversion.ts
import { gql as gql12 } from "graphql-request";
import dayjs3 from "dayjs";
import utc2 from "dayjs/plugin/utc.js";
dayjs3.extend(utc2);
var conciergeConversions = [
  SCENARIO_TYPES.CONCIERGE_PICKUP,
  SCENARIO_TYPES.CONCIERGE_PICKUP_NO_RIDESHARE,
  SCENARIO_TYPES.CONCIERGE_RETURN,
  SCENARIO_TYPES.CONCIERGE_RETURN_NO_RIDESHARE
];
var conciergeLoanerConversions = [
  SCENARIO_TYPES.CONCIERGE_LOANER_PICKUP,
  SCENARIO_TYPES.CONCIERGE_LOANER_RETURN
];
var oneWayConversions = [
  SCENARIO_TYPES.STRANDED_NO_RIDESHARE,
  SCENARIO_TYPES.STRANDED_RIDESHARE_AFTER,
  SCENARIO_TYPES.STRANDED_RIDESHARE_BEFORE,
  SCENARIO_TYPES.STRANDED_RIDESHARE_BOTH,
  SCENARIO_TYPES.STRANDED_SHUTTLE_AFTER,
  SCENARIO_TYPES.TRIANGLE
];
var roundTripConversions = [
  SCENARIO_TYPES.ROUND_TRIP,
  SCENARIO_TYPES.ROUND_TRIP_RIDESHARE_AFTER,
  SCENARIO_TYPES.ROUND_TRIP_RIDESHARE_BEFORE,
  SCENARIO_TYPES.ROUND_TRIP_RIDESHARE_BOTH,
  SCENARIO_TYPES.ROUND_TRIP_FLEX,
  SCENARIO_TYPES.ROUND_TRIP_FLEX_RIDESHARE_AFTER,
  SCENARIO_TYPES.ROUND_TRIP_FLEX_RIDESHARE_BEFORE,
  SCENARIO_TYPES.ROUND_TRIP_FLEX_RIDESHARE_BOTH
];
var GET_SCENARIO_TO_CONVERT_OR_REPAIR = gql12`
  query getScenarioToConvertOrRepair($scenarioId: bigint!) {
    scenarios_by_pk(id: $scenarioId) {
      id
      config
      scenario_key
      status
      moves(order_by: { cardinality: asc }) {
        cardinality
        move {
          id
          active
          config
          move_type
          pickup_time
          delivery_time
          consumer_at_pickup
          consumer_type
          childMoves(order_by: { pickup_time: asc }) {
            id
            active
            move_type
            pickup_time
            delivery_time
            lane {
              id
              inverse {
                id
              }
            }
          }
          lane {
            id
            duration_sec
            pickup_inspection_sec
            delivery_inspection_sec
            return_ride_wait_sec
            inverse {
              id
            }
          }
        }
      }
    }
  }
`;
var REMOVE_RIDE_MOVES_FROM_SCENARIO = gql12`
  mutation removeRideMovesFromScenario($scenarioId: bigint!, $moveIds: [bigint!]!) {
    delete_scenariotomoves(
      where: { scenario_id: { _eq: $scenarioId }, move_id: { _in: $moveIds } }
    ) {
      affected_rows
    }
    update_moves(
      where: { id: { _in: $moveIds }, _not: { scenariotomoves: {} } }
      _set: { active: 0 }
    ) {
      affected_rows
      returning {
        id
      }
    }
  }
`;
var UPSERT_RIDE_MOVES = gql12`
  mutation upsertRideMoves($moveInputs: [moves_insert_input!]!) {
    insert_moves(
      objects: $moveInputs
      on_conflict: {
        constraint: moves_pkey
        update_columns: [
          active
          delivery_time
          lane_id
          move_type
          parent_move_id
          pickup_time
          ride_type
          updatedat
          updatedBy
        ]
      }
    ) {
      affected_rows
      returning {
        id
        pickup_time
      }
    }
  }
`;
var ADD_RIDE_MOVES = gql12`
  mutation addRideMoves(
    $scenarioId: bigint!
    $moveIds: [bigint!]!
    $linkInputs: [scenariotomoves_insert_input!]!
  ) {
    delete_existing_links: delete_scenariotomoves(
      where: { scenario_id: { _eq: $scenarioId }, move_id: { _in: $moveIds } }
    ) {
      affected_rows
    }
    insert_scenariotomoves(objects: $linkInputs) {
      affected_rows
      returning {
        id
      }
    }
  }
`;
var INVERSE_CONCIERGE = gql12`
  mutation inverseConcierge($moveId: bigint!, $laneId: bigint!, $consumerAtPickup: smallint!) {
    update_moves(
      where: { id: { _eq: $moveId } }
      _set: {
        lane_id: $laneId
        consumer_at_pickup: $consumerAtPickup
        updatedat: "now()"
        updatedBy: "convertOrRepairScenario"
      }
    ) {
      affected_rows
    }
  }
`;
var INVERSE_CONCIERGE_LOANER = gql12`
  mutation inverseConciergeLoaner(
    $moveId1: bigint!
    $moveId2: bigint!
    $pickupTime1: timestamptz!
    $pickupTime2: timestamptz!
    $deliveryTime1: timestamptz!
    $deliveryTime2: timestamptz!
    $laneId1: bigint!
    $laneId2: bigint!
    $consumerAtPickup1: smallint!
    $consumerAtPickup2: smallint!
  ) {
    updateMove1: update_moves(
      where: { id: { _eq: $moveId1 } }
      _set: {
        pickup_time: $pickupTime1
        delivery_time: $deliveryTime1
        lane_id: $laneId1
        consumer_at_pickup: $consumerAtPickup1
        updatedat: "now()"
        updatedBy: "convertOrRepairScenario"
      }
    ) {
      affected_rows
    }

    updateMove2: update_moves(
      where: { id: { _eq: $moveId2 } }
      _set: {
        pickup_time: $pickupTime2
        delivery_time: $deliveryTime2
        lane_id: $laneId2
        consumer_at_pickup: $consumerAtPickup2
        updatedat: "now()"
        updatedBy: "convertOrRepairScenario"
      }
    ) {
      affected_rows
    }
  }
`;
var INVERSE_FLEXIBLE_FLAGS = gql12`
  mutation inverseFlexibleFlags(
    $moveId1: bigint!
    $moveId2: bigint!
    $moveConfig1: jsonb!
    $moveConfig2: jsonb!
  ) {
    updateMove1: update_moves(
      where: { id: { _eq: $moveId1 } }
      _set: { config: $moveConfig1, updatedat: "now()", updatedBy: "convertOrRepairScenario" }
    ) {
      affected_rows
    }

    updateMove2: update_moves(
      where: { id: { _eq: $moveId2 } }
      _set: { config: $moveConfig2, updatedat: "now()", updatedBy: "convertOrRepairScenario" }
    ) {
      affected_rows
    }
  }
`;
async function getScenarioToConvertOrRepair(client, scenarioId) {
  if (!client) throw new Error("GraphQL client is required!");
  if (!scenarioId) throw new Error("Scenario ID is required!");
  try {
    const variables = { scenarioId };
    const result = await client.request(GET_SCENARIO_TO_CONVERT_OR_REPAIR, variables, {
      cachePolicy: "network-only"
    });
    return result.scenarios_by_pk;
  } catch (err) {
    log(
      "getScenarioToConvertOrRepair",
      `Error getting scenario data for scenario ${scenarioId} to convert or repair: ${err.message}`
    );
    throw new Error(
      `Failed to get scenario data for scenario ${scenarioId} to convert or repair: ${err.message}`
    );
  }
}
async function removeRidesFromScenario(client, scenarioId, rideMoveIds) {
  if (!client) throw new Error("GraphQL client is required!");
  if (!scenarioId) throw new Error("Scenario ID is required!");
  if (!rideMoveIds) throw new Error("Ride move IDs are required!");
  try {
    const res = await client.request(
      REMOVE_RIDE_MOVES_FROM_SCENARIO,
      { scenarioId, moveIds: rideMoveIds }
    );
    if (!res?.delete_scenariotomoves?.affected_rows) {
      log("removeRidesFromScenario", `Failed to remove ride move links!`);
      throw new Error("Failed to remove ride move links!");
    }
    if (!res?.update_moves) {
      log("removeRidesFromScenario", `Failed to set ride moves to inactive!`);
      throw new Error("Failed to set ride moves to inactive!");
    }
    const requested = rideMoveIds.map((id) => String(id));
    const deactivated = (res?.update_moves?.returning ?? []).map((move) => String(move?.id));
    const unexpected = deactivated.filter((id) => !requested.includes(id));
    if (unexpected.length > 0) {
      log(
        "removeRidesFromScenario",
        `Deactivated ride moves that were not asked about: ${unexpected.join(", ")}!`
      );
      throw new Error("Deactivated ride moves outside of the requested set!");
    }
    const stillHeld = requested.filter((id) => !deactivated.includes(id));
    if (stillHeld.length > 0) {
      log(
        "removeRidesFromScenario",
        `Left ride moves active, another scenario still holds them: ${stillHeld.join(", ")}`
      );
    }
    return true;
  } catch (err) {
    log("removeRidesFromScenario", `Error removing ride moves: ${err.message}`);
    return false;
  }
}
async function addRidesToScenario(client, scenarioId, rideMoveInputs) {
  if (!client) throw new Error("GraphQL client is required!");
  if (!scenarioId) throw new Error("Scenario ID is required!");
  if (!rideMoveInputs) throw new Error("Ride move inputs are required!");
  try {
    const moveInputs = rideMoveInputs?.map((rm) => {
      let input = {
        active: rm?.active,
        delivery_time: rm?.delivery_time,
        lane_id: rm?.lane_id,
        move_type: rm?.move_type,
        parent_move_id: rm?.parent_move_id,
        pickup_time: rm?.pickup_time,
        ride_type: rm?.ride_type
      };
      if (rm?.id) {
        input.id = rm?.id;
        input.updatedat = "now()";
        input.updatedBy = "convertOrRepairScenario";
      }
      if (!rm?.id) {
        input.createdat = "now()";
        input.createdBy = "convertOrRepairScenario";
        input.updatedat = "now()";
        input.updatedBy = "convertOrRepairScenario";
      }
      return input;
    });
    const upsertRes = await client.request(UPSERT_RIDE_MOVES, {
      moveInputs
    });
    if (!upsertRes?.insert_moves?.affected_rows) {
      log("addRidesToScenario", `Failed to upsert ride moves!`);
      throw new Error("Failed to upsert ride moves!");
    }
    const sortedRideMoveIds = upsertRes?.insert_moves?.returning?.sort((a, b) => dayjs3(a?.pickup_time).diff(dayjs3(b?.pickup_time)))?.map((rm) => rm?.id) || [];
    const linkInputs = rideMoveInputs?.map((rm, index) => ({
      move_id: sortedRideMoveIds?.[index],
      scenario_id: scenarioId,
      cardinality: rm?.cardinality,
      createdat: "now()",
      createdby: "convertOrRepairScenario",
      updatedat: "now()",
      updatedby: "convertOrRepairScenario"
    }));
    const addRes = await client.request(ADD_RIDE_MOVES, {
      scenarioId,
      moveIds: linkInputs.map((l) => l?.move_id).filter((id) => id !== void 0 && id !== null),
      linkInputs
    });
    if (!addRes?.insert_scenariotomoves?.affected_rows) {
      log("addRidesToScenario", `Failed to add ride move links!`);
      throw new Error("Failed to add ride move links!");
    }
    return sortedRideMoveIds;
  } catch (err) {
    log("addRidesToScenario", `Error adding ride moves to scenario: ${err.message}`);
    return [];
  }
}
async function inverseConcierge(client, driveMove) {
  if (!client) throw new Error("GraphQL client is required!");
  if (!driveMove) throw new Error("Drive move is required!");
  try {
    const moveId = driveMove?.id;
    const laneId = driveMove?.lane?.inverse?.id;
    const consumerAtPickup = driveMove?.consumer_at_pickup === 0 ? 1 : 0;
    const res = await client.request(INVERSE_CONCIERGE, {
      moveId,
      laneId,
      consumerAtPickup
    });
    if (!res?.update_moves?.affected_rows) {
      log("inverseConcierge", `Failed to inverse concierge drive move!`);
      throw new Error("Failed to inverse concierge drive move!");
    }
    return true;
  } catch (err) {
    log("inverseConcierge", `Error inverting concierge drive move: ${err.message}`);
    return false;
  }
}
async function inverseConciergeLoaner(client, driveMoves) {
  if (!client) throw new Error("GraphQL client is required!");
  if (!driveMoves) throw new Error("Drive moves are required!");
  if (driveMoves?.length !== 2) throw new Error("Drive moves must be an array of two moves!");
  try {
    const moveId1 = driveMoves?.[1]?.id;
    const moveId2 = driveMoves?.[0]?.id;
    const pickupTime1 = driveMoves?.[0]?.pickup_time;
    const pickupTime2 = driveMoves?.[1]?.pickup_time;
    const deliveryTime1 = driveMoves?.[0]?.delivery_time;
    const deliveryTime2 = driveMoves?.[1]?.delivery_time;
    const laneId1 = driveMoves?.[0]?.lane?.id;
    const laneId2 = driveMoves?.[1]?.lane?.id;
    const consumerAtPickup1 = driveMoves?.[0]?.consumer_at_pickup;
    const consumerAtPickup2 = driveMoves?.[1]?.consumer_at_pickup;
    const res = await client.request(INVERSE_CONCIERGE_LOANER, {
      moveId1,
      moveId2,
      pickupTime1,
      pickupTime2,
      deliveryTime1,
      deliveryTime2,
      laneId1,
      laneId2,
      consumerAtPickup1,
      consumerAtPickup2
    });
    if (!res?.updateMove1?.affected_rows || !res?.updateMove2?.affected_rows) {
      log("inverseConciergeLoaner", `Failed to inverse concierge + loaner drive moves!`);
      throw new Error("Failed to inverse concierge + loaner drive moves!");
    }
    return true;
  } catch (err) {
    log("inverseConciergeLoaner", `Error inverting concierge + loaner drive moves: ${err.message}`);
    return false;
  }
}
async function inverseFlexibleFlags(client, driveMoves, isFlexible) {
  if (!client) throw new Error("GraphQL client is required!");
  if (!driveMoves) throw new Error("Drive moves are required!");
  if (typeof isFlexible !== "boolean") throw new Error("Is flexible flag is required!");
  try {
    const moveId1 = driveMoves?.[0]?.id;
    const moveId2 = driveMoves?.[1]?.id;
    const moveConfig1 = { ...driveMoves?.[0]?.config, flexible: isFlexible };
    const moveConfig2 = { ...driveMoves?.[1]?.config, flexible: isFlexible };
    const res = await client.request(INVERSE_FLEXIBLE_FLAGS, {
      moveId1,
      moveId2,
      moveConfig1,
      moveConfig2
    });
    if (!res?.updateMove1?.affected_rows || !res?.updateMove2?.affected_rows) {
      log("inverseFlexibleFlags", `Failed to inverse flexible flags!`);
      throw new Error("Failed to inverse flexible flags!");
    }
    return true;
  } catch (err) {
    log("inverseFlexibleFlags", `Error inverting flexible flags: ${err.message}`);
    return false;
  }
}
function checkConversionValidity(oldScenarioKey, newScenarioKey) {
  let oldKeyType = void 0;
  if (conciergeConversions?.includes(oldScenarioKey)) oldKeyType = "concierge";
  else if (conciergeLoanerConversions?.includes(oldScenarioKey)) oldKeyType = "concierge+loaner";
  else if (oneWayConversions?.includes(oldScenarioKey)) oldKeyType = "one-way";
  else if (roundTripConversions?.includes(oldScenarioKey)) oldKeyType = "round-trip";
  let newKeyType = void 0;
  if (conciergeConversions?.includes(newScenarioKey)) newKeyType = "concierge";
  else if (conciergeLoanerConversions?.includes(newScenarioKey)) newKeyType = "concierge+loaner";
  else if (oneWayConversions?.includes(newScenarioKey)) newKeyType = "one-way";
  else if (roundTripConversions?.includes(newScenarioKey)) newKeyType = "round-trip";
  return oldKeyType === newKeyType;
}
function determineLayoutOfScenarioObject(scenario) {
  if (!scenario) throw new Error("Scenario is required!");
  const moves = scenario?.moves?.map((moveLink) => moveLink?.move);
  const movesWithIndex = moves?.map((m, i) => ({ ...m, index: i }));
  const driveMovesWithIndex = movesWithIndex?.filter((m) => m?.move_type === "drive");
  const rideMovesWithIndex = movesWithIndex?.filter((m) => m?.move_type === "ride");
  const driveCount = driveMovesWithIndex?.length;
  const rideCount = rideMovesWithIndex?.length;
  let ridePlacement = "none";
  if (rideCount === 2) ridePlacement = "both";
  else if (rideCount === 1) {
    if (rideMovesWithIndex[0]?.index === 0) ridePlacement = "before";
    else ridePlacement = "after";
  }
  const isConciergePickup = driveMovesWithIndex?.[0]?.consumer_type === "customer" && driveMovesWithIndex?.[0]?.consumer_at_pickup === 1;
  const isConciergeLoanerPickup = driveMovesWithIndex?.[0]?.consumer_type === "loaner" && driveMovesWithIndex?.[0]?.consumer_at_pickup === 0 && driveMovesWithIndex?.[1]?.consumer_type === "customer" && driveMovesWithIndex?.[1]?.consumer_at_pickup === 1;
  const isFlexible = driveMovesWithIndex?.some((m) => m?.config?.flexible === true);
  return {
    driveCount,
    rideCount,
    ridePlacement,
    isConciergePickup,
    isConciergeLoanerPickup,
    isFlexible
  };
}
function determineLayoutOfScenarioKey(scenarioKey) {
  if (!scenarioKey) throw new Error("Scenario key is required!");
  if (scenarioKey === SCENARIO_TYPES.CONCIERGE_PICKUP) {
    return {
      driveCount: 1,
      rideCount: 1,
      ridePlacement: "before",
      isConciergePickup: true,
      isConciergeLoanerPickup: false,
      isFlexible: false
    };
  }
  if (scenarioKey === SCENARIO_TYPES.CONCIERGE_PICKUP_NO_RIDESHARE) {
    return {
      driveCount: 1,
      rideCount: 0,
      ridePlacement: "none",
      isConciergePickup: true,
      isConciergeLoanerPickup: false,
      isFlexible: false
    };
  }
  if (scenarioKey === SCENARIO_TYPES.CONCIERGE_RETURN) {
    return {
      driveCount: 1,
      rideCount: 1,
      ridePlacement: "after",
      isConciergePickup: false,
      isConciergeLoanerPickup: false,
      isFlexible: false
    };
  }
  if (scenarioKey === SCENARIO_TYPES.CONCIERGE_RETURN_NO_RIDESHARE) {
    return {
      driveCount: 1,
      rideCount: 0,
      ridePlacement: "none",
      isConciergePickup: false,
      isConciergeLoanerPickup: false,
      isFlexible: false
    };
  }
  if (scenarioKey === SCENARIO_TYPES.CONCIERGE_LOANER_PICKUP) {
    return {
      driveCount: 2,
      rideCount: 0,
      ridePlacement: "none",
      isConciergePickup: false,
      isConciergeLoanerPickup: true,
      isFlexible: false
    };
  }
  if (scenarioKey === SCENARIO_TYPES.CONCIERGE_LOANER_RETURN) {
    return {
      driveCount: 2,
      rideCount: 0,
      ridePlacement: "none",
      isConciergePickup: false,
      isConciergeLoanerPickup: false,
      isFlexible: false
    };
  }
  if (scenarioKey === SCENARIO_TYPES.STRANDED_NO_RIDESHARE) {
    return {
      driveCount: 1,
      rideCount: 0,
      ridePlacement: "none",
      isConciergePickup: false,
      isConciergeLoanerPickup: false,
      isFlexible: false
    };
  }
  if (scenarioKey === SCENARIO_TYPES.STRANDED_RIDESHARE_AFTER) {
    return {
      driveCount: 1,
      rideCount: 1,
      ridePlacement: "after",
      isConciergePickup: false,
      isConciergeLoanerPickup: false,
      isFlexible: false
    };
  }
  if (scenarioKey === SCENARIO_TYPES.STRANDED_RIDESHARE_BEFORE) {
    return {
      driveCount: 1,
      rideCount: 1,
      ridePlacement: "before",
      isConciergePickup: false,
      isConciergeLoanerPickup: false,
      isFlexible: false
    };
  }
  if (scenarioKey === SCENARIO_TYPES.STRANDED_RIDESHARE_BOTH) {
    return {
      driveCount: 1,
      rideCount: 2,
      ridePlacement: "both",
      isConciergePickup: false,
      isConciergeLoanerPickup: false,
      isFlexible: false
    };
  }
  if (scenarioKey === SCENARIO_TYPES.TRIANGLE) {
    return {
      driveCount: 1,
      rideCount: 2,
      ridePlacement: "both",
      isConciergePickup: false,
      isConciergeLoanerPickup: false,
      isFlexible: false
    };
  }
  if (scenarioKey === SCENARIO_TYPES.STRANDED_SHUTTLE_AFTER) {
    return {
      driveCount: 1,
      rideCount: 1,
      ridePlacement: "after",
      isConciergePickup: false,
      isConciergeLoanerPickup: false,
      isFlexible: false
    };
  }
  if (scenarioKey === SCENARIO_TYPES.ROUND_TRIP) {
    return {
      driveCount: 2,
      rideCount: 0,
      ridePlacement: "none",
      isConciergePickup: false,
      isConciergeLoanerPickup: false,
      isFlexible: false
    };
  }
  if (scenarioKey === SCENARIO_TYPES.ROUND_TRIP_RIDESHARE_AFTER) {
    return {
      driveCount: 2,
      rideCount: 1,
      ridePlacement: "after",
      isConciergePickup: false,
      isConciergeLoanerPickup: false,
      isFlexible: false
    };
  }
  if (scenarioKey === SCENARIO_TYPES.ROUND_TRIP_RIDESHARE_BEFORE) {
    return {
      driveCount: 2,
      rideCount: 1,
      ridePlacement: "before",
      isConciergePickup: false,
      isConciergeLoanerPickup: false,
      isFlexible: false
    };
  }
  if (scenarioKey === SCENARIO_TYPES.ROUND_TRIP_RIDESHARE_BOTH) {
    return {
      driveCount: 2,
      rideCount: 2,
      ridePlacement: "both",
      isConciergePickup: false,
      isConciergeLoanerPickup: false,
      isFlexible: false
    };
  }
  if (scenarioKey === SCENARIO_TYPES.ROUND_TRIP_FLEX) {
    return {
      driveCount: 2,
      rideCount: 0,
      ridePlacement: "none",
      isConciergePickup: false,
      isConciergeLoanerPickup: false,
      isFlexible: true
    };
  }
  if (scenarioKey === SCENARIO_TYPES.ROUND_TRIP_FLEX_RIDESHARE_AFTER) {
    return {
      driveCount: 2,
      rideCount: 1,
      ridePlacement: "after",
      isConciergePickup: false,
      isConciergeLoanerPickup: false,
      isFlexible: true
    };
  }
  if (scenarioKey === SCENARIO_TYPES.ROUND_TRIP_FLEX_RIDESHARE_BEFORE) {
    return {
      driveCount: 2,
      rideCount: 1,
      ridePlacement: "before",
      isConciergePickup: false,
      isConciergeLoanerPickup: false,
      isFlexible: true
    };
  }
  if (scenarioKey === SCENARIO_TYPES.ROUND_TRIP_FLEX_RIDESHARE_BOTH) {
    return {
      driveCount: 2,
      rideCount: 2,
      ridePlacement: "both",
      isConciergePickup: false,
      isConciergeLoanerPickup: false,
      isFlexible: true
    };
  }
  return {
    driveCount: 0,
    rideCount: 0,
    ridePlacement: "none",
    isConciergePickup: false,
    isConciergeLoanerPickup: false,
    isFlexible: false
  };
}
function buildConversionInstructions(scenario, currentScenarioKey, newScenarioKey) {
  if (!scenario) throw new Error("Scenario is required!");
  if (!currentScenarioKey) throw new Error("Current scenario key is required!");
  if (!newScenarioKey) throw new Error("New scenario key is required!");
  const currentLayout = determineLayoutOfScenarioObject(scenario);
  const newLayout = determineLayoutOfScenarioKey(newScenarioKey);
  const currentHasRideBefore = currentLayout?.ridePlacement === "before" || currentLayout?.ridePlacement === "both";
  const currentHasRideAfter = currentLayout?.ridePlacement === "after" || currentLayout?.ridePlacement === "both";
  const newHasRideBefore = newLayout?.ridePlacement === "before" || newLayout?.ridePlacement === "both";
  const newHasRideAfter = newLayout?.ridePlacement === "after" || newLayout?.ridePlacement === "both";
  const isConciergeInverse = conciergeConversions?.includes(newScenarioKey) && currentLayout?.isConciergePickup !== newLayout?.isConciergePickup;
  const isConciergeLoanerInverse = conciergeLoanerConversions?.includes(newScenarioKey) && currentLayout?.isConciergeLoanerPickup !== newLayout?.isConciergeLoanerPickup;
  const isFlexibleFlagsInverse = roundTripConversions?.includes(newScenarioKey) && currentLayout?.isFlexible !== newLayout?.isFlexible;
  const instructions = {
    totalMoveCount: newLayout?.driveCount + newLayout?.rideCount,
    removeRideBefore: currentHasRideBefore && !newHasRideBefore,
    removeRideAfter: currentHasRideAfter && !newHasRideAfter,
    addRideBefore: !currentHasRideBefore && newHasRideBefore,
    addRideAfter: !currentHasRideAfter && newHasRideAfter,
    inverseConcierge: isConciergeInverse,
    inverseConciergeLoaner: isConciergeLoanerInverse,
    inverseFlexibleFlags: isFlexibleFlagsInverse,
    isFlexible: newLayout?.isFlexible
  };
  return instructions;
}
function buildRideMoveInput(driveMove, placement = "before", type = "auto", cardinality = 0, existingRideIdsToIgnore = []) {
  if (!driveMove) throw new Error("Drive move is required!");
  if (!placement) throw new Error("Placement is required!");
  if (!type) throw new Error("Type is required!");
  try {
    const existingInactiveRide = driveMove?.childMoves?.find(
      (cm) => cm?.move_type === "ride" && cm?.active === 0 && !existingRideIdsToIgnore?.includes(cm?.id)
    );
    const durationSec = (driveMove?.lane?.duration_sec || 30 * 60) - (driveMove?.lane?.pickup_inspection_sec || 10 * 60) - (driveMove?.lane?.delivery_inspection_sec || 8 * 60) + (driveMove?.lane?.return_ride_wait_sec || 5 * 60);
    const pickupTime = placement === "before" ? dayjs3(driveMove?.pickup_time).subtract(durationSec, "seconds").utc().format() : dayjs3(driveMove?.delivery_time).utc().format();
    const deliveryTime = placement === "before" ? dayjs3(driveMove?.pickup_time).utc().format() : dayjs3(driveMove?.delivery_time).add(durationSec, "seconds").utc().format();
    const rideMoveInput = {
      id: existingInactiveRide?.id,
      parent_move_id: driveMove?.id,
      lane_id: driveMove?.lane?.inverse?.id,
      move_type: "ride",
      ride_type: type,
      pickup_time: pickupTime,
      delivery_time: deliveryTime,
      active: 1,
      cardinality
    };
    return rideMoveInput;
  } catch (err) {
    log("buildRideMoveInput", `Error building ride move input: ${err.message}`);
    return void 0;
  }
}
async function convertOrRepairScenario(client, scenarioId, targetScenarioKey) {
  if (!client) throw new Error("GraphQL client is required!");
  if (!scenarioId) throw new Error("Scenario ID is required!");
  logger.info(
    `Running convertOrRepairScenario with scenarioId: ${scenarioId}, targetScenarioKey: ${targetScenarioKey}`
  );
  try {
    const scenario = await getScenarioToConvertOrRepair(client, scenarioId);
    const currentScenarioKey = scenario?.scenario_key;
    logger.info("scenario:", scenario);
    const isRepair = currentScenarioKey === targetScenarioKey || !targetScenarioKey;
    logger.info("isRepair:", { isRepair });
    if (!isRepair) {
      const isValid = checkConversionValidity(currentScenarioKey, targetScenarioKey);
      if (!isValid)
        throw new Error(
          `Cannot convert scenario key '${currentScenarioKey}' to '${targetScenarioKey}'!`
        );
    }
    const moves = scenario?.moves?.map((moveLink) => moveLink?.move);
    const driveMoves = moves?.filter((move) => move?.move_type === "drive");
    const rideMoves = moves?.filter((move) => move?.move_type === "ride");
    logger.info("driveMoves:", { driveMoves });
    logger.info("rideMoves:", { rideMoves });
    let updatedMoveIds = (moves ?? []).map((move) => move?.id);
    const instructions = buildConversionInstructions(
      scenario,
      currentScenarioKey,
      targetScenarioKey
    );
    logger.info("instructions:", instructions);
    if (instructions?.inverseConcierge === true) {
      logger.info("Attempting to inverse concierge drive move...");
      const inversed = await inverseConcierge(client, driveMoves?.[0]);
      if (!inversed) {
        log("convertOrRepairScenario", `Failed to inverse concierge drive move!`);
        throw new Error("Failed to inverse concierge drive move!");
      }
    }
    if (instructions?.inverseConciergeLoaner === true) {
      logger.info("Attempting to inverse concierge + loaner drive moves...");
      const inversed = await inverseConciergeLoaner(client, driveMoves);
      if (!inversed) {
        log("convertOrRepairScenario", `Failed to inverse concierge + loaner drive moves!`);
        throw new Error("Failed to inverse concierge + loaner drive moves!");
      }
      const driveMoveIndex1 = updatedMoveIds?.indexOf(driveMoves?.[0]?.id);
      const driveMoveIndex2 = updatedMoveIds?.indexOf(driveMoves?.[1]?.id);
      updatedMoveIds[driveMoveIndex1] = driveMoves?.[1]?.id;
      updatedMoveIds[driveMoveIndex2] = driveMoves?.[0]?.id;
    }
    if (instructions?.inverseFlexibleFlags === true) {
      logger.info("Attempting to inverse the flexible flags...");
      const inversed = await inverseFlexibleFlags(client, driveMoves, instructions?.isFlexible);
      if (!inversed) {
        log("convertOrRepairScenario", `Failed to inverse the flexible flags!`);
        throw new Error("Failed to inverse the flexible flags!");
      }
    }
    let rideMoveIdsToRemove = [];
    if (instructions?.removeRideBefore === true) {
      const rideBeforeMove = rideMoves?.[0];
      if (rideBeforeMove) rideMoveIdsToRemove.push(rideBeforeMove?.id);
      logger.info("rideBeforeMove:", rideBeforeMove);
    }
    if (instructions?.removeRideAfter === true) {
      const rideAfterMove = rideMoves?.[rideMoves?.length - 1];
      if (rideAfterMove) rideMoveIdsToRemove.push(rideAfterMove?.id);
      logger.info("rideAfterMove:", rideAfterMove);
    }
    if (rideMoveIdsToRemove?.length > 0) {
      logger.info("Attempting to remove ride moves:", { rideMoveIdsToRemove });
      const removed = await removeRidesFromScenario(client, scenarioId, rideMoveIdsToRemove);
      if (!removed) {
        log("convertOrRepairScenario", `Failed to remove ride moves!`);
        throw new Error("Failed to remove ride moves!");
      }
      updatedMoveIds = updatedMoveIds?.filter((mId) => !rideMoveIdsToRemove?.includes(mId));
    }
    if (instructions?.addRideBefore === true || instructions?.addRideAfter === true) {
      const latestScenario = await getScenarioToConvertOrRepair(client, scenarioId);
      const latestDriveMoves = latestScenario?.moves?.map((moveLink) => moveLink?.move)?.filter((move) => move?.move_type === "drive");
      logger.info("latestScenario:", latestScenario);
      const firstDriveMove = latestDriveMoves?.[0];
      logger.info("firstDriveMove:", firstDriveMove);
      let rideBeforeMoveInput = void 0;
      if (instructions?.addRideBefore === true) {
        rideBeforeMoveInput = buildRideMoveInput(
          firstDriveMove,
          "before",
          targetScenarioKey === SCENARIO_TYPES.STRANDED_SHUTTLE_AFTER ? "shuttle" : "auto",
          0,
          []
        );
        logger.info("rideBeforeMoveInput:", rideBeforeMoveInput);
      }
      const lastDriveMove = latestDriveMoves?.[latestDriveMoves?.length - 1];
      logger.info("lastDriveMove:", lastDriveMove);
      let rideAfterMoveInput = void 0;
      if (instructions?.addRideAfter === true) {
        rideAfterMoveInput = buildRideMoveInput(
          lastDriveMove,
          "after",
          targetScenarioKey === SCENARIO_TYPES.STRANDED_SHUTTLE_AFTER ? "shuttle" : "auto",
          instructions?.totalMoveCount - 1,
          rideBeforeMoveInput?.id ? [rideBeforeMoveInput?.id] : []
        );
        logger.info("rideAfterMoveInput:", rideAfterMoveInput);
      }
      const rideMoveInputs = [];
      if (rideBeforeMoveInput) rideMoveInputs.push(rideBeforeMoveInput);
      if (rideAfterMoveInput) rideMoveInputs.push(rideAfterMoveInput);
      if (rideMoveInputs?.length > 0) {
        logger.info("Attempting to add ride moves...");
        const addedIds = await addRidesToScenario(client, scenarioId, rideMoveInputs);
        logger.info("addedIds:", { addedIds });
        if (!addedIds || !addedIds?.length) {
          log("convertOrRepairScenario", `Failed to add ride moves!`);
          throw new Error("Failed to add ride moves!");
        }
        if (instructions?.addRideBefore === true) updatedMoveIds.unshift(addedIds?.[0]);
        if (instructions?.addRideAfter === true)
          updatedMoveIds.push(addedIds?.[instructions?.addRideBefore === true ? 1 : 0]);
      }
    }
    const updatedScenario = await updateScenario(client, scenarioId, {
      type: targetScenarioKey,
      moves: updatedMoveIds,
      user: "convertOrRepairScenario"
    });
    logger.info("updatedScenario:", updatedScenario);
    return updatedScenario;
  } catch (err) {
    log(
      "convertOrRepairScenario",
      `Error converting or repairing scenario ${scenarioId}: ${err.message}`
    );
    throw new Error(`Failed to convert or repair scenario ${scenarioId}: ${err.message}`);
  }
}

// src/useCases/useCaseChecks.ts
var isConcierge = (
  /** @param {Move} move */
  (move) => Boolean(move.consumer_pickup)
);
var isNotConcierge = (
  /** @param {Move} move */
  (move) => !isConcierge(move)
);
var isPickup = (
  /** @param {Move} move */
  (move) => move.consumer_type !== "loaner" && move.consumer_at_pickup === 1 || move.consumer_type === "loaner" && move.consumer_at_pickup === 0
);
var hasParentMove = (
  /** @param {Move} move */
  (move) => Boolean(move.parent_move_id)
);
var hasUncanceledChildDriveMove = (
  /** @param {Move} move */
  (move) => {
    if (!move?.childMoves) return false;
    return move.childMoves.some((childMove) => childMove?.move_type === "drive" && (childMove?.cancel_status === null || childMove?.cancel_status === "pending"));
  }
);
var hasRelatedDriveMove = (
  /** @param {Move} move */
  (move) => {
    if (hasParentMove(move) || hasUncanceledChildDriveMove(move)) {
      return true;
    }
    return false;
  }
);
var hasNoRelatedDriveMove = (
  /** @param {Move} move */
  (move) => !hasRelatedDriveMove(move)
);
var isNotPickup = (
  /** @param {Move} move */
  (move) => !isPickup(move)
);
var isShuttleDrive = (
  /** @param {Move} move */
  (move) => move?.config?.isShuttle === true
);
var matchesCriteria = (
  /** @param {Move} move @param {MoveCriteria} criteria */
  (move, criteria) => {
    const { workflowsetType, customerId, organizationId } = criteria;
    const matches = [];
    if (workflowsetType !== void 0) matches.push(move?.workflowset?.type === workflowsetType);
    if (customerId !== void 0) matches.push(move?.customer?.id === customerId);
    if (organizationId !== void 0)
      matches.push(move?.customer?.organization_id === organizationId);
    return matches.length > 0 && matches.every((match) => match);
  }
);
var createCheck = (
  /** @param {Array<Function>} conditions @param {string} useCase */
  (conditions, useCase) => (
    /** @param {Move} move */
    (move) => conditions.every((condition) => condition(move)) ? useCase : null
  )
);
var createUseCaseChecks = (
  /** @param {UseCaseConfig} config */
  (config) => {
    const isConciergeHangtag = (
      /** @param {Move} move */
      (move) => {
        const { useCaseIdentification } = config;
        const criteriaList = useCaseIdentification?.conciergeHangtag || [];
        if (criteriaList.length === 0) return false;
        return criteriaList.some(
          /** @param {MoveCriteria} criteria */
          (criteria) => matchesCriteria(move, criteria)
        );
      }
    );
    const isYardHangtag = (
      /** @param {Move} move */
      (move) => {
        const { useCaseIdentification } = config;
        const criteriaList = useCaseIdentification?.yardHangtag || [];
        if (criteriaList.length === 0) return false;
        return criteriaList.some(
          /** @param {MoveCriteria} criteria */
          (criteria) => matchesCriteria(move, criteria)
        );
      }
    );
    const isYardHangtagWithShuttle = (
      /** @param {Move} move */
      (move) => {
        const { useCaseIdentification } = config;
        const criteriaList = useCaseIdentification?.yardHangtagWithShuttle || [];
        if (criteriaList.length === 0) return false;
        return criteriaList.some(
          /** @param {MoveCriteria} criteria */
          (criteria) => matchesCriteria(move, criteria)
        );
      }
    );
    return {
      // Hangtag checks first
      1: createCheck(
        [isConcierge, isConciergeHangtag, hasRelatedDriveMove],
        USE_CASES.CONCIERGE_LOANER_HANGTAG
      ),
      2: createCheck(
        [isConcierge, isConciergeHangtag, hasNoRelatedDriveMove],
        USE_CASES.CONCIERGE_HANGTAG
      ),
      3: createCheck(
        [isNotConcierge, isYardHangtagWithShuttle, hasNoRelatedDriveMove],
        USE_CASES.ONE_WAY_HANGTAG_SHUTTLE
      ),
      4: createCheck(
        [isNotConcierge, isYardHangtag, hasNoRelatedDriveMove],
        USE_CASES.ONE_WAY_HANGTAG
      ),
      // Concierge checks
      5: createCheck([isConcierge, hasNoRelatedDriveMove, isPickup], USE_CASES.CONCIERGE_PICKUP),
      6: createCheck([isConcierge, hasNoRelatedDriveMove, isNotPickup], USE_CASES.CONCIERGE_RETURN),
      7: createCheck([isConcierge, hasRelatedDriveMove, isPickup], USE_CASES.CONCIERGE_LOANER_PICKUP),
      8: createCheck(
        [isConcierge, hasRelatedDriveMove, isNotPickup],
        USE_CASES.CONCIERGE_LOANER_RETURN
      ),
      // Other checks
      // 9: createCheck([isHertzOrg], USE_CASES.ONE_WAY_SHUTTLE), // We are skipping this one for now due to some inticricies with Hertz moves (e.g. they are not always shuttle drives)
      10: createCheck([isShuttleDrive], USE_CASES.INTERNAL),
      // Basic move types last
      11: createCheck([hasRelatedDriveMove], USE_CASES.ROUND_TRIP),
      12: createCheck([hasNoRelatedDriveMove], USE_CASES.ONE_WAY)
    };
  }
);

// src/timeline/planCreation.ts
import { gql as gql13 } from "graphql-request";
var GET_PLAN_BY_DRIVER_ID = gql13`
  query getPlanByDriverId($driverId: bigint!, $planDate: date!) {
    plans(where: { driver_id: { _eq: $driverId }, plan_date: { _eq: $planDate } }) {
      id
      driver_id
      driver_name
      plan_date
      region_id
    }
  }
`;
var CREATE_PLAN_FOR_DRIVER = gql13`
  mutation createPlanForDriver($driverId: bigint!, $driverName: String!, $planDate: date!, $regionId: bigint!) {
    insert_plans(objects: { driver_id: $driverId, driver_name: $driverName, plan_date: $planDate, region_id: $regionId }) {
      returning {
        id
        driver_id
        driver_name
        plan_date
        region_id
      }
    }
  }
`;
var getPlanByDriverId = async (client, driverId, planDate) => {
  if (!client) {
    throw new Error("GraphQL client is required to get a plan");
  }
  if (!driverId) {
    throw new Error("Driver id is required to get a plan");
  }
  if (!planDate) {
    throw new Error("Plan date is required to get a plan");
  }
  const results = await client.request(GET_PLAN_BY_DRIVER_ID, {
    driverId,
    planDate
  });
  log("getPlanByDriverId", "fetched plans", { results });
  if (results?.plans?.length > 0) {
    return results.plans[0];
  }
  return null;
};
var createPlanForDriver = async (client, driverId, driverName, planDate, regionId) => {
  if (!client) {
    throw new Error("GraphQL client is required to create a plan");
  }
  if (!driverId) {
    throw new Error("Driver id is required to create a plan");
  }
  if (!driverName) {
    throw new Error("Driver name is required to create a plan");
  }
  if (!planDate) {
    throw new Error("Plan date is required to create a plan");
  }
  if (!regionId) {
    throw new Error("Region id is required to create a plan");
  }
  try {
    const insert = await client.request(CREATE_PLAN_FOR_DRIVER, {
      driverId,
      driverName,
      planDate,
      regionId
    });
    if (insert.insert_plans.returning.length > 0) {
      log("createPlanForDriver", `Created plan for driver ${driverId}`, { plan: insert.insert_plans.returning[0] });
      return insert.insert_plans.returning[0];
    }
    throw new Error("Failed to create plan");
  } catch (error) {
    log("getOrCreatePlan", `Error creating plan for driver ${driverId}: ${error.message}`);
    throw new Error(`Failed to create plan: ${error.message}`);
  }
};
var getOrCreatePlan = async (client, driverId, driverName, planDate, regionId) => {
  if (!client) {
    throw new Error("GraphQL client is required to get or create a plan");
  }
  if (!driverId || !driverName) {
    throw new Error("Driver id and name is required to get or create a plan");
  }
  if (!planDate) {
    throw new Error("Plan date is required to get or create a plan");
  }
  if (!regionId) {
    throw new Error("Region id is required to get or create a plan");
  }
  try {
    const existingPlan = await getPlanByDriverId(client, driverId, planDate);
    if (existingPlan) {
      log("getOrCreatePlan", `Found existing plan for driver ${driverId}`, { plan: existingPlan });
      return existingPlan;
    }
  } catch (error) {
    log("getOrCreatePlan", `Error getting plan for driver ${driverId}: ${error.message}`);
    throw new Error(`Failed to get plan: ${error.message}`);
  }
  try {
    return await createPlanForDriver(client, driverId, driverName, planDate, regionId);
  } catch (error) {
    log("getOrCreatePlan", `Error creating plan for driver ${driverId}: ${error.message}`);
    throw new Error(`Failed to create plan: ${error.message}`);
  }
};

// src/timeline/timelineManagement.ts
import { gql as gql14 } from "graphql-request";
import dayjs4 from "dayjs";
import utc3 from "dayjs/plugin/utc.js";
import timezone2 from "dayjs/plugin/timezone.js";
dayjs4.extend(utc3);
dayjs4.extend(timezone2);
var assignMovesToPlan = gql14`
  mutation assignMoveToDriver(
    $moveIds: [bigint!]!
    $driverId: bigint!
    $planId: bigint
    $driverStatus: String!
    $driverName: String!
    $updatedBy: String!
    $driverAppVersion: String!
    $status: String
  ) {
    update_moves(
      where: { id: { _in: $moveIds } }
      _set: {
        driver_id: $driverId
        plan_id: $planId
        status: $status
        driver_status: $driverStatus
        driver_name: $driverName
        updatedat: "now()"
        updatedBy: $updatedBy
        driver_app_version: $driverAppVersion
      }
    ) {
      returning {
        id
      }
    }
  }
`;
var assignScenarioToDriver = async ({
  client,
  driver,
  planId,
  scenario,
  bundle
}) => {
  const movesToUpdate = scenario.moves;
  const moveIdsToUpdate = movesToUpdate.map((moveWrapper) => moveWrapper.move.id);
  let driverStatus;
  if (bundle.status === BUNDLE_STATUSES.DISPATCHED) {
    driverStatus = DRIVER_STATUSES.ACCEPTED;
  } else {
    if (bundle.config?.source === OFFER_SOURCES.MANUAL) {
      driverStatus = DRIVER_STATUSES.DRAFT;
    } else {
      driverStatus = DRIVER_STATUSES.ACCEPTED;
    }
  }
  const result = await client.request(assignMovesToPlan, {
    moveIds: moveIdsToUpdate,
    driverId: driver.id,
    driverName: driver.user.display_name,
    driverAppVersion: driver.driver_app_version || "3.0.0",
    planId,
    driverStatus,
    status: "dispatched",
    updatedBy: "auto-planner"
  });
  log("assignScenarioToDriver", `Assigned scenario to driver: ${driver.id}`, { result });
  return result;
};
var assignBundleToDriver = async (client, driver, bundle) => {
  if (!client) {
    throw new Error("Client is required");
  }
  if (!driver) {
    throw new Error("Driver is required");
  }
  if (!bundle) {
    throw new Error("Bundle is required");
  }
  if (!bundle.scenarios || bundle.scenarios.length === 0) {
    throw new Error("Bundle must have at least one scenario");
  }
  if (!bundle.scenarios[0].scenario.moves || bundle.scenarios[0].scenario.moves.length === 0) {
    throw new Error("Bundle must have at least one move");
  }
  const liteMovesBundle = bundle.scenarios.some(
    (scenarioWrapper) => scenarioWrapper.scenario.moves.some(
      (moveWrapper) => moveWrapper.move.lite === true
    )
  );
  if (liteMovesBundle) {
    for (const scenarioWrapper of bundle.scenarios) {
      await assignScenarioToDriver({
        client,
        driver,
        planId: null,
        scenario: scenarioWrapper.scenario,
        bundle
      });
    }
    return;
  }
  let earliestMove = null;
  let earliestPickupTime = null;
  for (const scenario of bundle.scenarios) {
    for (const moveWrapper of scenario.scenario.moves) {
      const move = moveWrapper.move;
      const pickupTime = dayjs4(move.pickup_time);
      if (!earliestPickupTime || pickupTime.isBefore(earliestPickupTime)) {
        earliestPickupTime = pickupTime;
        earliestMove = move;
      }
    }
  }
  if (!earliestMove || !earliestPickupTime) {
    throw new Error("No moves found in bundle scenarios");
  }
  const pickupRegion = earliestMove.lane.pickup.region_id;
  const deliveryRegion = earliestMove.lane.delivery.region_id;
  const regionId = pickupRegion || deliveryRegion;
  const pickupTimezone = earliestMove.lane.pickup.timezone;
  const deliveryTimezone = earliestMove.lane.delivery.timezone;
  const timezone3 = pickupTimezone || deliveryTimezone;
  const tz = timezone3 || "America/New_York";
  let planDate = earliestPickupTime.tz(tz).format("YYYY-MM-DD");
  const today = dayjs4().tz(tz).startOf("day");
  const planDateTime = dayjs4.tz(planDate, tz).startOf("day");
  if (planDateTime.isBefore(today)) {
    planDate = today.format("YYYY-MM-DD");
  }
  try {
    const plan = await getOrCreatePlan(
      client,
      driver.id,
      driver.user.display_name,
      planDate,
      regionId
    );
    for (const scenarioWrapper of bundle.scenarios) {
      await assignScenarioToDriver({
        client,
        driver,
        planId: plan.id,
        scenario: scenarioWrapper.scenario,
        bundle
      });
    }
  } catch (error) {
    logger.error(error);
    throw error;
  }
};

// src/drivers/litePlanning.ts
import dayjs6 from "dayjs";
import { gql as gql16 } from "graphql-request";

// src/drivers/liteConflicts.ts
import dayjs5 from "dayjs";
import utc4 from "dayjs/plugin/utc.js";
import timezonePlugin from "dayjs/plugin/timezone.js";
import { gql as gql15 } from "graphql-request";
dayjs5.extend(utc4);
dayjs5.extend(timezonePlugin);
var GET_LITE_DRIVER_DAY_MOVES = gql15`
  query getLiteDriverDayMoves($driverIds: [bigint!]!, $dayStart: timestamptz!, $dayEnd: timestamptz!, $excludeMoveIds: [bigint!]!) {
    moves(
      where: {
        id: { _nin: $excludeMoveIds }
        driver_id: { _in: $driverIds }
        active: { _eq: 1 }
        cancel_status: { _is_null: true }
        pickup_time: { _gte: $dayStart, _lte: $dayEnd }
      }
    ) {
      id
      driver_id
      move_type
      pickup_time
      lane {
        duration_sec
        pickup {
          latitude
          longitude
        }
        delivery {
          latitude
          longitude
        }
      }
    }
  }
`;
var CONFLICT_LOOKBACK_HOURS = 12;
var pickupLocalDay = (pickupTime, pickupTimezone) => {
  if (!pickupTimezone) {
    const start2 = dayjs5(pickupTime).utc().startOf("day");
    return { start: start2, end: start2.add(1, "day") };
  }
  const start = dayjs5(pickupTime).tz(pickupTimezone).startOf("day");
  const nextDate = start.add(36, "hour").tz(pickupTimezone).format("YYYY-MM-DD");
  return { start, end: dayjs5.tz(nextDate, pickupTimezone) };
};
async function getLiteDriverDayMoves(client, driverIds, pickupTime, pickupTimezone, excludeMoveIds, moveDurationMinutes = 0) {
  const day = pickupLocalDay(pickupTime, pickupTimezone);
  const aroundStart = dayjs5(pickupTime).subtract(CONFLICT_LOOKBACK_HOURS, "hour");
  const aroundEnd = dayjs5(pickupTime).add(Math.max(moveDurationMinutes, 0), "minute");
  const fetchStart = aroundStart.isBefore(day.start) ? aroundStart : day.start;
  const fetchEnd = aroundEnd.isAfter(day.end) ? aroundEnd : day.end;
  const result = await client.request(GET_LITE_DRIVER_DAY_MOVES, {
    driverIds,
    dayStart: fetchStart.toISOString(),
    dayEnd: fetchEnd.toISOString(),
    excludeMoveIds: excludeMoveIds ?? []
  });
  return (result?.moves ?? []).map((move) => {
    const pickup = move.pickup_time ? dayjs5(move.pickup_time) : null;
    return { ...move, onPickupDay: !!pickup && !pickup.isBefore(day.start) && pickup.isBefore(day.end) };
  });
}
async function getLiteDriversWithNoConflictingMoves(client, drivers, pickupTime, moveDurationMinutes, pickupTimezone, excludeMoveIds) {
  const bundleStart = dayjs5(pickupTime);
  const bundleEnd = bundleStart.add(moveDurationMinutes, "minutes");
  const moveTimeWindow = `${bundleStart.format("YYYY-MM-DD HH:mm:ss")} to ${bundleEnd.format("YYYY-MM-DD HH:mm:ss")}`;
  const driverIds = drivers.map((driver) => driver.id);
  if (driverIds.length === 0) {
    return { availableDrivers: [], driverPlansMap: /* @__PURE__ */ new Map(), moveTimeWindow, conflicts: [] };
  }
  const dayMoves = await getLiteDriverDayMoves(client, driverIds, pickupTime, pickupTimezone, excludeMoveIds, moveDurationMinutes);
  logger.info(
    `[getLiteDriversWithNoConflictingMoves] ${dayMoves.length} committed moves across ${driverIds.length} lite drivers`
  );
  const planDate = (pickupTimezone ? dayjs5(pickupTime).tz(pickupTimezone) : dayjs5(pickupTime).utc()).format("YYYY-MM-DD");
  const driverPlansMap = /* @__PURE__ */ new Map();
  for (const move of dayMoves) {
    const driverId = Number(move.driver_id);
    if (!driverId) continue;
    const plan = driverPlansMap.get(driverId) ?? { driver_id: driverId, plan_date: planDate, moves: [] };
    plan.moves.push({ ...move, appayments: [] });
    driverPlansMap.set(driverId, plan);
  }
  const availableDrivers = [];
  const conflicts = [];
  for (const driver of drivers) {
    const plan = driverPlansMap.get(driver.id);
    if (!plan?.moves?.length) {
      availableDrivers.push(driver);
      continue;
    }
    const overlapping = plan.moves.filter((move) => {
      const moveStart = dayjs5(move.pickup_time);
      const moveEnd = moveStart.add(Math.floor((move.lane?.duration_sec || 0) / 60), "minutes");
      return !(bundleEnd.isBefore(moveStart) || moveEnd.isBefore(bundleStart));
    });
    if (overlapping.length === 0) {
      availableDrivers.push(driver);
      continue;
    }
    const detail = overlapping.map((move) => {
      const start = dayjs5(move.pickup_time);
      const end = start.add(Math.floor((move.lane?.duration_sec || 0) / 60), "minutes");
      return `move ${move.id} (${start.format("YYYY-MM-DD HH:mm:ss")}-${end.format("YYYY-MM-DD HH:mm:ss")})`;
    }).join(", ");
    conflicts.push(
      `Driver ${driver.user?.display_name || driver.id} has conflicting ${overlapping.length > 1 ? "moves" : "move"}: ${detail}`
    );
  }
  return { availableDrivers, driverPlansMap, moveTimeWindow, conflicts };
}
var DAY_END_SECONDS = 23 * 3600 + 59 * 60 + 59;
function timeOfDayToSeconds(value, fallback) {
  if (value === null || value === void 0) return fallback;
  const parts = String(value).trim().split(":");
  if (parts.length < 2 || parts.length > 3) return fallback;
  const numbers = parts.map(Number);
  if (numbers.some((n) => !Number.isFinite(n) || n < 0)) return fallback;
  const [hours, minutes, seconds = 0] = numbers;
  if (hours > 23 || minutes > 59 || seconds > 59) return fallback;
  return hours * 3600 + minutes * 60 + seconds;
}
function resolveLiteScheduleVerdict({
  driver,
  pickupTime,
  moveDurationMinutes,
  pickupTimezone
}) {
  const timezone3 = driver.region?.timezone || pickupTimezone;
  if (!timezone3) return "unknown";
  if (!driver.businesshours || !Array.isArray(driver.businesshours) || driver.businesshours.length === 0) {
    return "no_schedule";
  }
  const start = dayjs5(pickupTime).tz(timezone3);
  const end = start.add(moveDurationMinutes, "minutes");
  const dayHours = driver.businesshours.filter(
    (bh) => bh.is_open === true && (bh.day_of_week === start.day() || bh.year === start.year() && bh.month === start.month() + 1 && bh.day === start.date())
  );
  if (dayHours.length === 0) return "outside_schedule";
  if (!end.isSame(start, "day")) return "outside_schedule";
  const startSeconds = timeOfDayToSeconds(start.format("HH:mm:ss"), 0);
  const endSeconds = timeOfDayToSeconds(end.format("HH:mm:ss"), DAY_END_SECONDS);
  const works = dayHours.some((bh) => {
    const open = timeOfDayToSeconds(bh.open_time, 0);
    const close = timeOfDayToSeconds(bh.closed_time, DAY_END_SECONDS);
    return startSeconds >= open && endSeconds <= close;
  });
  return works ? "available" : "outside_schedule";
}
function getLiteDriversWithScheduledAvailability(drivers, pickupTime, moveDurationMinutes, pickupTimezone) {
  const verdicts = /* @__PURE__ */ new Map();
  const availableDrivers = [];
  for (const driver of drivers) {
    const verdict = resolveLiteScheduleVerdict({ driver, pickupTime, moveDurationMinutes, pickupTimezone });
    verdicts.set(driver.id, verdict);
    if (verdict === "available" || verdict === "unknown") availableDrivers.push(driver);
  }
  return { availableDrivers, verdicts };
}

// src/drivers/litePlanning.ts
var MOVE_TYPE_TO_ATTRIBUTE = {
  valet: "mobility_valet",
  mobile_service: "mobility_service",
  parts_delivery: "mobility_parts"
};
var GET_DRIVERS_FOR_LITE_PLANNING = gql16`
  query getDriversForLitePlanning($driverIds: [bigint!]!) {
    drivers(where: { id: { _in: $driverIds } }) {
      id
      created_at
      status
      tax_class
      config
      region {
        timezone
      }
      user {
        id
        display_name
      }
      home_location {
        latitude
        longitude
      }
      starting_location
      businesshours {
        is_open
        day_of_week
        year
        month
        day
        open_time
        closed_time
      }
    }
  }
`;
function countJobsOnDay(moves) {
  return (moves ?? []).filter((move) => move.move_type !== "ride" && move.onPickupDay !== false).length;
}
async function rankDriversForLitePlanning(client, params) {
  const { driverIds, pickupTime, moveDurationMinutes, pickupLocation, pickupTimezone, moveType, excludeMoveIds } = params;
  if (!driverIds || driverIds.length === 0) {
    return { rankedDrivers: [] };
  }
  logger.info(
    `[rankDriversForLitePlanning] Ranking ${driverIds.length} drivers for ${moveType} pickup at ${pickupTime}`
  );
  const data = await client.request(
    GET_DRIVERS_FOR_LITE_PLANNING,
    { driverIds }
  );
  const requiredAttribute = MOVE_TYPE_TO_ATTRIBUTE[moveType];
  const hasRequiredAttribute = (driver) => driver.config?.attributes?.[requiredAttribute] === true;
  const eligibleDrivers = data.drivers.filter(hasRequiredAttribute);
  const ineligibleDrivers = data.drivers.filter((driver) => !hasRequiredAttribute(driver));
  logger.info(
    `[rankDriversForLitePlanning] ${eligibleDrivers.length}/${data.drivers.length} drivers have ${requiredAttribute} attribute`
  );
  if (data.drivers.length === 0) {
    return { rankedDrivers: [] };
  }
  const { availableDrivers, driverPlansMap } = await getLiteDriversWithNoConflictingMoves(
    client,
    data.drivers,
    pickupTime,
    moveDurationMinutes,
    pickupTimezone,
    excludeMoveIds
  );
  const availableDriverIds = new Set(
    availableDrivers.map((driver) => driver.id)
  );
  const { availableDrivers: scheduledDrivers, verdicts: scheduleVerdicts } = getLiteDriversWithScheduledAvailability(
    data.drivers,
    pickupTime,
    moveDurationMinutes,
    pickupTimezone
  );
  const scheduledDriverIds = new Set(
    scheduledDrivers.map((driver) => driver.id)
  );
  const scheduleReason = (driver) => scheduleVerdicts.get(driver.id) === "no_schedule" ? "no_schedule" : "outside_schedule";
  const bundleStart = dayjs6(pickupTime);
  const bundleEnd = bundleStart.add(moveDurationMinutes, "minutes");
  const findConflictingMove = (driverId) => {
    const plan = driverPlansMap.get(driverId);
    for (const move of plan?.moves ?? []) {
      const moveStart = dayjs6(move.pickup_time);
      const moveEnd = moveStart.add(Math.floor((move.lane?.duration_sec || 0) / 60), "minutes");
      const overlaps = !(bundleEnd.isBefore(moveStart) || moveEnd.isBefore(bundleStart));
      if (overlaps) {
        return {
          moveId: move.id,
          start: moveStart.toISOString(),
          end: moveEnd.toISOString()
        };
      }
    }
    return void 0;
  };
  const { scoredDrivers, scoringDetails } = eligibleDrivers.length ? await getDriverWeightedScores(
    client,
    eligibleDrivers,
    pickupTime,
    pickupLocation,
    driverPlansMap,
    "v1_0_0",
    void 0
    // no promo context
  ) : { scoredDrivers: [], scoringDetails: [] };
  const scoringDetailsMap = new Map(
    scoringDetails.map((sd) => [sd.driverId, sd])
  );
  const scoreMap = new Map(
    scoredDrivers.map((sd) => [sd.driverId, sd])
  );
  const toRanked = (driver) => {
    const isAvailable = availableDriverIds.has(driver.id);
    const scored = scoreMap.get(driver.id);
    const details = scoringDetailsMap.get(driver.id);
    const result = {
      driverId: driver.id,
      driverName: scored?.driverName ?? driver.user?.display_name ?? String(driver.id),
      finalScore: scored?.finalWeightedScore ?? 0,
      status: isAvailable ? "available" : "busy",
      movesOnDay: countJobsOnDay(driverPlansMap.get(driver.id)?.moves)
    };
    if (details) {
      const positionUnknown = details.projectedLocationMethod === "default_fallback" || details.projectedLocationMethod === "error_fallback";
      result.scoring = {
        earningsScore: details.earningsScore ?? 0,
        distanceScore: details.distanceScore ?? 0,
        atPickupScore: details.atPickupLocationScore ?? 0,
        distanceToPickup: positionUnknown ? null : details.distanceToPickupLocation ?? 0,
        positionUnknown
      };
    }
    if (!hasRequiredAttribute(driver)) {
      result.ineligibility = { reason: "missing_attribute", requiredAttribute };
    } else if (!scheduledDriverIds.has(driver.id)) {
      result.ineligibility = { reason: scheduleReason(driver) };
    } else if (!isAvailable) {
      const conflictingMove = findConflictingMove(driver.id);
      result.ineligibility = { reason: "conflicting_move", conflictingMove };
      if (conflictingMove) {
        result.conflict = {
          moveId: conflictingMove.moveId,
          window: `${conflictingMove.start}-${conflictingMove.end}`
        };
      }
    } else if (!scored) {
      result.ineligibility = { reason: "not_scored" };
    }
    return result;
  };
  const rankedDrivers = [...eligibleDrivers, ...ineligibleDrivers].map(toRanked);
  const rankGroup = (driver) => {
    const reason = driver.ineligibility?.reason;
    if (reason === "missing_attribute" || reason === "no_schedule" || reason === "no_timezone") return 3;
    if (reason === "not_scored") return 2;
    if (reason === "outside_schedule" || driver.status === "busy") return 1;
    return 0;
  };
  rankedDrivers.sort((a, b) => {
    const byGroup = rankGroup(a) - rankGroup(b);
    if (byGroup !== 0) return byGroup;
    if (b.finalScore !== a.finalScore) return b.finalScore - a.finalScore;
    return a.driverName.localeCompare(b.driverName);
  });
  logger.info(
    `[rankDriversForLitePlanning] Ranked ${rankedDrivers.length} drivers: ${rankedDrivers.filter((d) => d.status === "available").length} available, ${rankedDrivers.filter((d) => d.status === "busy").length} busy`
  );
  return { rankedDrivers };
}

// src/client/factory.ts
import { GraphQLClient } from "graphql-request";
function nodeHttpsAgent(rejectUnauthorized) {
  if (typeof __require !== "function") return void 0;
  try {
    const nodeHttps = __require("https");
    return new nodeHttps.Agent({ rejectUnauthorized });
  } catch {
    return void 0;
  }
}
function createHasuraClient({
  url,
  adminSecret,
  adminJwt,
  options = {}
}) {
  const { extraHeaders = {}, rejectUnauthorized = false } = options;
  let headers = {};
  if (!url) {
    throw new Error("URL is required to create a Hasura GraphQL client");
  }
  if (!adminSecret && !adminJwt) {
    throw new Error("Admin secret or admin JWT is required to create a Hasura GraphQL client");
  }
  if (adminSecret) {
    headers = {
      "x-hasura-admin-secret": adminSecret,
      ...extraHeaders
    };
  } else if (adminJwt) {
    headers = {
      authorization: `Bearer ${adminJwt}`,
      ...extraHeaders
    };
  }
  const agent = nodeHttpsAgent(rejectUnauthorized);
  return new GraphQLClient(url, {
    headers,
    // Omit fetchOptions entirely off-Node rather than passing `agent: undefined`, which some fetch
    // implementations reject.
    ...agent ? { fetchOptions: { agent } } : {}
  });
}
function createDefaultClient(config) {
  return createHasuraClient({
    url: config.url,
    adminSecret: config.adminSecret,
    adminJwt: config.adminJwt,
    options: {
      extraHeaders: {
        "Content-Type": "application/json",
        Accept: "application/json"
      },
      rejectUnauthorized: config.rejectUnauthorized
    }
  });
}

// src/guestride/create-guestride-with-scenario.ts
import { getLogger } from "@hopdrive/sdk-core";

// ../../gql/operations.ts
import { resolveExecutor } from "@hopdrive/sdk-core";

// ../../gql/schema.ts
var CreateGuestrideWithScenarioDocument = { "__text": "mutation CreateGuestrideWithScenario($scenario_key: String!, $product_key: String!, $customer_id: bigint!, $usecase_key: String!, $status: String!, $config: jsonb!, $cardinality: smallint!, $createdat: timestamptz!, $createdby: String!, $updatedat: timestamptz!, $updatedby: String!, $guestride: guestrides_insert_input!) {\n  insert_scenarios_one(\n    object: {scenario_key: $scenario_key, product_key: $product_key, customer_id: $customer_id, usecase_key: $usecase_key, status: $status, config: $config, createdat: $createdat, createdby: $createdby, updatedat: $updatedat, updatedby: $updatedby, guestride_links: {data: [{cardinality: $cardinality, createdat: $createdat, createdby: $createdby, updatedat: $updatedat, updatedby: $updatedby, guestride: {data: $guestride}}]}}\n  ) {\n    id\n    scenario_key\n    status\n    guestride_links {\n      id\n      scenario_id\n      guestride_id\n      cardinality\n      guestride {\n        id\n      }\n    }\n  }\n}", "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "mutation", "name": { "kind": "Name", "value": "CreateGuestrideWithScenario" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "scenario_key" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "String" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "product_key" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "String" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "customer_id" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "usecase_key" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "String" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "status" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "String" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "config" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "jsonb" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "cardinality" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "smallint" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "createdat" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "timestamptz" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "createdby" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "String" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "updatedat" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "timestamptz" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "updatedby" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "String" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "guestride" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "guestrides_insert_input" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "insert_scenarios_one" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "object" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "scenario_key" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "scenario_key" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "product_key" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "product_key" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "customer_id" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "customer_id" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "usecase_key" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "usecase_key" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "status" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "status" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "config" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "config" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "createdat" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "createdat" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "createdby" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "createdby" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "updatedat" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "updatedat" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "updatedby" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "updatedby" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "guestride_links" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "data" }, "value": { "kind": "ListValue", "values": [{ "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "cardinality" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "cardinality" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "createdat" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "createdat" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "createdby" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "createdby" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "updatedat" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "updatedat" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "updatedby" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "updatedby" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "guestride" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "data" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "guestride" } } }] } }] }] } }] } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "scenario_key" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "guestride_links" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "scenario_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "guestride_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "cardinality" } }, { "kind": "Field", "name": { "kind": "Name", "value": "guestride" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }] } }] } }] } }] } }] };
var FindGuestrideWorkflowsetsForCustomerDocument = { "__text": 'query FindGuestrideWorkflowsetsForCustomer($customerId: bigint!) {\n  customer: customers_by_pk(id: $customerId) {\n    id\n    organization_id\n  }\n  workflowsets(\n    where: {type: {_eq: "guestride"}, _or: [{customer_id: {_eq: $customerId}}, {organization: {customers: {id: {_eq: $customerId}}}}, {public: {_eq: true}}]}\n  ) {\n    id\n    customer_id\n    organization_id\n    public\n  }\n}', "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "FindGuestrideWorkflowsetsForCustomer" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "customerId" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "alias": { "kind": "Name", "value": "customer" }, "name": { "kind": "Name", "value": "customers_by_pk" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "customerId" } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "organization_id" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "workflowsets" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "type" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "StringValue", "value": "guestride", "block": false } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "_or" }, "value": { "kind": "ListValue", "values": [{ "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "customer_id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "customerId" } } }] } }] }, { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "organization" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "customers" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "customerId" } } }] } }] } }] } }] }, { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "public" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "BooleanValue", "value": true } }] } }] }] } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "customer_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "organization_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "public" } }] } }] } }] };

// ../../gql/operations.ts
async function createGuestrideWithScenario(variables, options) {
  const { executor, raw, ...requestOptions } = options ?? {};
  const result = await resolveExecutor(executor).mutate(
    CreateGuestrideWithScenarioDocument,
    variables,
    requestOptions
  );
  if (raw) return result;
  return result.insert_scenarios_one;
}
async function findGuestrideWorkflowsetsForCustomer(variables, options) {
  const { executor, ...requestOptions } = options ?? {};
  return resolveExecutor(executor).query(
    FindGuestrideWorkflowsetsForCustomerDocument,
    variables,
    requestOptions
  );
}

// ../products/dist/chunk-7LRMHEXV.mjs
var PRODUCT_KEY_VALET_LITE = "valet-lite";
var PRODUCT_KEY_VALET_FULL_SERVICE = "valet-full-service";
var PRODUCT_KEY_OPERATIONAL_MOVES = "operational-moves";
var PRODUCT_KEY_GUEST_RIDES = "guest-rides";
var PRODUCT_KEY_MOBILE_SERVICE = "mobile-service";
var PRODUCT_KEY_PARTS_DELIVERY = "parts-delivery";
var PRODUCT_KEY_ENTERPRISE = "enterprise";
var PRODUCT_KEY_APPRAISALS = "appraisals";
var PRODUCT_KEY_RETURN_RIDES = "return-rides";
var USECASE_KEY_GUEST_RIDESHARE = "guest-rideshare";
var USECASE_KEY_MOBILE_SERVICE_RUN = "mobile-service-run";
var USECASE_KEY_PARTS_DELIVERY_ROUTE = "parts-delivery-route";
var PRODUCT_DEFINITIONS = [
  {
    key: PRODUCT_KEY_VALET_LITE,
    label: "Valet Lite",
    description: "Software for dealerships to manage vehicle moves with their own drivers."
  },
  {
    key: PRODUCT_KEY_VALET_FULL_SERVICE,
    label: "Valet Full Service",
    description: "Vehicle pickup and delivery performed by HopDrive drivers."
  },
  {
    key: PRODUCT_KEY_OPERATIONAL_MOVES,
    label: "Operational Moves",
    description: "Vehicle move operations and coverage across Valet services."
  },
  {
    key: PRODUCT_KEY_GUEST_RIDES,
    label: "Guest Rides",
    description: "Rideshare transportation for dealership guests and customers."
  },
  {
    key: PRODUCT_KEY_MOBILE_SERVICE,
    label: "Mobile Service",
    description: "Management of mobile service visits away from the dealership."
  },
  {
    key: PRODUCT_KEY_PARTS_DELIVERY,
    label: "Parts Delivery",
    description: "Delivery of automotive parts between dealerships and service locations."
  },
  {
    key: PRODUCT_KEY_ENTERPRISE,
    label: "Enterprise",
    description: "Program-level vehicle transportation for enterprise operations."
  },
  {
    key: PRODUCT_KEY_APPRAISALS,
    label: "Appraisals",
    description: "Vehicle appraisals based on vehicle details and condition."
  },
  {
    key: PRODUCT_KEY_RETURN_RIDES,
    label: "Return Rides",
    description: "Separately purchased rideshare transportation that returns stranded dealership drivers after one-way Valet Lite moves."
  }
];
var SALABLE_PRODUCT_KEYS = PRODUCT_DEFINITIONS.map(
  ({ key }) => key
);

// src/guestride/guestride-scenario-variables.ts
function buildGuestrideScenarioVariables({
  guestride,
  config,
  user,
  now
}) {
  return {
    scenario_key: SCENARIO_TYPES.GUEST_RIDESHARE,
    product_key: PRODUCT_KEY_GUEST_RIDES,
    usecase_key: USECASE_KEY_GUEST_RIDESHARE,
    customer_id: guestride.customer_id,
    status: SCENARIO_STATUS.NEW,
    config,
    cardinality: 0,
    createdat: now,
    createdby: user,
    updatedat: now,
    updatedby: user,
    guestride
  };
}

// src/guestride/create-guestride-with-scenario.ts
var log2 = getLogger();
var GUESTRIDE_WORKFLOWSET_TYPE = "guestride";
async function resolveGuestrideWorkflowsetId(params) {
  const { customerId, executor } = params;
  const data = await findGuestrideWorkflowsetsForCustomer({ customerId }, { executor });
  const candidates = data.workflowsets ?? [];
  const organizationId = data.customer?.organization_id ?? null;
  const customerSpecific = candidates.find((ws) => ws.customer_id === customerId);
  if (customerSpecific) return customerSpecific.id;
  if (organizationId != null) {
    const organizationSpecific = candidates.find(
      (ws) => ws.customer_id == null && ws.organization_id === organizationId
    );
    if (organizationSpecific) return organizationSpecific.id;
  }
  const publicWorkflowset = candidates.find((ws) => ws.public === true);
  if (publicWorkflowset) return publicWorkflowset.id;
  throw new Error(
    `No "${GUESTRIDE_WORKFLOWSET_TYPE}" workflowset found for customer ${customerId} and no public fallback exists`
  );
}
async function createGuestrideWithScenario2(params) {
  const { guestride, config = {}, user = "system", executor } = params;
  if (!guestride || typeof guestride !== "object") {
    throw new Error("Guestride payload is required!");
  }
  if (typeof guestride.customer_id !== "number") {
    throw new Error("guestride.customer_id is required");
  }
  if (typeof guestride.workflowset_id !== "number") {
    throw new Error(
      "guestride.workflowset_id is required (#60). Use the exported `resolveGuestrideWorkflowsetId` helper if you need the legacy type-tag walk."
    );
  }
  try {
    const variables = buildGuestrideScenarioVariables({
      guestride,
      config,
      user,
      now: (/* @__PURE__ */ new Date()).toISOString()
    });
    log2.info(
      `[createGuestrideWithScenario] Creating guestride + scenario atomically (workflowset ${guestride.workflowset_id})`
    );
    const result = await createGuestrideWithScenario(variables, { executor });
    if (!result?.id) throw new Error("Failed to create guestride scenario");
    const link = result.guestride_links?.[0];
    const guestrideId = link?.guestride?.id ?? link?.guestride_id;
    if (!guestrideId) throw new Error("Guestride was not linked to scenario");
    log2.info(
      `[createGuestrideWithScenario] Created scenario ${result.id} linked to guestride ${guestrideId}`
    );
    return {
      scenario: {
        id: result.id,
        scenario_key: result.scenario_key,
        status: result.status,
        guestrideId
      }
    };
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    log2.error(`[createGuestrideWithScenario] Failed. Error: ${msg}`);
    throw new Error(`Failed to create guestride with scenario: ${msg}`);
  }
}

// src/mobility/mobility-run-scenario.ts
var MOBILITY_RUN_SCENARIO_IDENTITY = {
  mobileService: {
    scenarioKey: "mobile-service-run",
    productKey: PRODUCT_KEY_MOBILE_SERVICE,
    usecaseKey: USECASE_KEY_MOBILE_SERVICE_RUN,
    productType: "mobile_service"
  },
  partsDelivery: {
    scenarioKey: "parts-delivery-route",
    productKey: PRODUCT_KEY_PARTS_DELIVERY,
    usecaseKey: USECASE_KEY_PARTS_DELIVERY_ROUTE,
    productType: "parts_delivery"
  }
};
function requireRunCustomer(run) {
  if (!run) throw new Error("Mobility run not found; no scenario was created");
  if (!Number.isInteger(run.customer_id) || run.customer_id <= 0) {
    throw new Error(`Mobility run ${run.id} has no valid customer_id; no scenario was created`);
  }
  return run;
}
function buildMobilityRunScenarioInsert(identity, run, { user, now }) {
  const { id, customer_id } = requireRunCustomer(run);
  return {
    scenario_key: identity.scenarioKey,
    product_key: identity.productKey,
    usecase_key: identity.usecaseKey,
    customer_id,
    status: SCENARIO_STATUS.NEW,
    config: { mobility_run_id: id, product_type: identity.productType },
    createdby: user,
    createdat: now,
    updatedby: user,
    updatedat: now
  };
}
function buildMobileServiceScenarioInsert(run, options) {
  return buildMobilityRunScenarioInsert(MOBILITY_RUN_SCENARIO_IDENTITY.mobileService, run, options);
}
function buildPartsDeliveryScenarioInsert(run, options) {
  return buildMobilityRunScenarioInsert(MOBILITY_RUN_SCENARIO_IDENTITY.partsDelivery, run, options);
}

// src/assignment/derivation.ts
var isDriveLeg = (leg) => leg.move.move_type !== "ride";
var isRideLeg = (leg) => leg.move.move_type === "ride";
var resolveDriveLegs = (legs) => [...legs.filter(isDriveLeg)].sort((a, b) => a.cardinality - b.cardinality);
var ASSIGNABLE_STATUSES = /* @__PURE__ */ new Set([null, "dispatched", "pickup started"]);
var RETRACTABLE_RIDE_STATUSES = ["pending", "dispatched", "failed", "cancelled"];
var isRideRetractable = (status) => !status || RETRACTABLE_RIDE_STATUSES.includes(status);
var isOrderAssignable = (legs) => {
  const driveLegs = resolveDriveLegs(legs);
  if (!driveLegs.length) return false;
  const everyDriveLegOpen = driveLegs.every((leg) => leg.move.lite === true && ASSIGNABLE_STATUSES.has(leg.move.status));
  if (!everyDriveLegOpen) return false;
  return legs.filter(isRideLeg).every((leg) => isRideRetractable(leg.move.status));
};
var isOfferAccepted = (offer) => offer?.status === "assigned" || offer?.status === "accepted";
var resolveOfferAuditFields = (offer) => {
  const hasBeenOffered = offer?.status !== "new";
  return {
    offeredAt: offer?.offered_at ?? (hasBeenOffered ? offer?.createdat : null) ?? null,
    offeredBy: offer?.offered_by ?? (hasBeenOffered ? offer?.createdby : null) ?? null,
    // The accepted fallback is for rows still AT 'accepted' only — the same rule as the
    // backfill migration (1787080088573_offer_audit_backfill): once an offer advances to
    // 'assigned', updatedat/updatedby have been overwritten and no longer mark the accept,
    // so an un-stamped 'assigned' row reads as unknown rather than a confident wrong time.
    acceptedAt: offer?.accepted_at ?? (offer?.status === "accepted" ? offer?.updatedat : null) ?? null,
    acceptedBy: offer?.accepted_by ?? (offer?.status === "accepted" ? offer?.updatedby : null) ?? null
  };
};
var resolveDriverAssignment = ({
  legs,
  offers = []
}) => {
  const driveLegs = resolveDriveLegs(legs);
  const assignedLeg = driveLegs.find((leg) => leg.move.driver_id != null);
  const offer = offers.find(isOfferAccepted) ?? offers[0];
  const driver = assignedLeg ? assignedLeg.move.driver ?? {
    id: assignedLeg.move.driver_id ?? null,
    display_name: assignedLeg.move.driver_name ?? null,
    avatar_url: null
  } : offer?.driver ?? null;
  const isAccepted = isOfferAccepted(offer);
  const { offeredAt, offeredBy, acceptedAt, acceptedBy } = resolveOfferAuditFields(offer);
  if (!isOrderAssignable(legs)) return { driver, state: "locked", offeredAt, offeredBy, acceptedAt, acceptedBy };
  if (assignedLeg) return { driver, state: "assigned", offeredAt, offeredBy, acceptedAt, acceptedBy };
  if (offers.length) return { driver, state: isAccepted ? "accepted" : "offered", offeredAt, offeredBy, acceptedAt, acceptedBy };
  return { driver: null, state: "unassigned", offeredAt: null, offeredBy: null, acceptedAt: null, acceptedBy: null };
};

// src/assignment/format.ts
import dayjs7 from "dayjs";
var formatOfferActor = (actor) => !actor || actor === "system" ? "\u2014" : actor;
var formatOfferTime = (at) => at ? dayjs7(at).format("MMM D, h:mm A") : "\u2014";
var formatAuditTime = (at) => at ? dayjs7(at).format("MMM D \xB7 h:mm A") : null;

// src/index.ts
var defaultExport = {
  // NOTE: npm only sets npm_package_version while running an npm script, so this is almost always
  // the fallback. Carried over from the source repo; changing what `version` reports would be a
  // behaviour change for consumers. See docs/dispatch-port-followups.md.
  // eslint-disable-next-line no-restricted-syntax -- see NOTE above
  version: process.env.npm_package_version || "0.1.0"
};
export {
  ALGORITHM_KEY,
  BUNDLE_SET_TYPES,
  BUNDLE_STATUSES,
  COUNTING_STATUSES,
  CUSTOMER_TIER,
  DEFAULT_BUNDLE_CONFIG,
  DEFAULT_DRIVER_STATUS,
  DEFAULT_OFFER_SOURCE,
  DEFAULT_OFFER_STRATEGY,
  DEFAULT_STRATEGY_PARAMS,
  DISPATCHABLE_DRIVER_STATUSES,
  DISPATCHABLE_DRIVER_WHERE_CLAUSES,
  DRIVERSTOPROMOS_STATUS,
  DRIVER_ACCOUNT_STATUSES,
  DRIVER_SELECTION_ALGORITHMS,
  DRIVER_SELECTION_ALGORITHM_VARIABLES,
  DRIVER_STATUSES,
  DRIVER_TAX_CLASS,
  ELIGIBILITY_REASON,
  MOVE_TYPES,
  OFFER_SOURCES,
  OFFER_STATUSES,
  OFFER_STRATEGIES,
  OfferAlreadyAcceptedError,
  PROMO_STATUS,
  PROMO_TEMPLATE_KEY,
  RETRACTABLE_RIDE_STATUSES,
  RIDE_POSITION,
  RIDE_TYPES,
  SCENARIO_STATUS,
  SCENARIO_TYPES,
  TEMPLATE_EXCLUDED_TAX_CLASSES,
  TEMPLATE_REGISTRY,
  USE_CASES,
  VALID_BUNDLE_SET_TYPES,
  VALID_BUNDLE_STATUSES,
  VALID_DRIVER_SELECTION_ALGORITHMS,
  VALID_DRIVER_STATUSES,
  VALID_ELIGIBILITY_REASONS,
  VALID_MOVE_TYPES,
  VALID_OFFER_SOURCES,
  VALID_OFFER_STATUSES,
  VALID_OFFER_STRATEGIES,
  VALID_RIDE_POSITIONS,
  VALID_RIDE_TYPES,
  VALID_SCENARIO_STATUSES,
  VALID_SCENARIO_TYPES,
  VALID_USE_CASES,
  acceptOffer,
  addNewBundleToBundleSet,
  addScenarioToBundle,
  addScenariosToBundle,
  advanceToNextStrategy,
  applyPromoCommitmentBoost,
  applyRequiredCerts,
  areAllOffersResolved,
  areAllRoundsCompleted,
  assignBundleToDriver,
  assignOfferToBundle,
  buildDriverContext,
  buildMobileServiceScenarioInsert,
  buildOfferNotificationPayload,
  buildPartsDeliveryScenarioInsert,
  buildPromoBoostContext,
  buildScopedDriversWhere,
  calculateBundleTotals,
  conciergeConversions,
  conciergeLoanerConversions,
  consoleLoggerShim,
  convertOrRepairScenario,
  createBundle,
  createBundleSet,
  createConciergeLoanerPickupScenario,
  createConciergeLoanerReturnScenario,
  createConciergePickupNoRideshareScenario,
  createConciergePickupScenario,
  createConciergeReturnNoRideshareScenario,
  createConciergeReturnScenario,
  createDefaultClient,
  createGuestrideWithScenario2 as createGuestrideWithScenario,
  createHasuraClient,
  createOffer,
  createRoundTripFlexScenario,
  createRoundTripScenario,
  createScenarioWithMoves,
  createShuttleDriveScenario,
  createShuttleLinkerScenario,
  createStrandedWithRideAfterScenario,
  createStrandedWithRideBeforeScenario,
  createStrandedWithRideBothScenario,
  createStrandedWithShuttleAfterScenario,
  createTriangleScenario,
  createUseCaseChecks,
  declineOffer,
  defaultExport as default,
  defaultExport,
  deleteBundle,
  evaluatePromoEligibility,
  expireOffer,
  expireOldOffers,
  extendOfferPayloadWithPromo,
  filterByPromoEligibility,
  formatAuditTime,
  formatOfferActor,
  formatOfferTime,
  getActiveDriverstopromosForCandidates,
  getBundleById,
  getBundleWithMoveDetails,
  getBundleWithMoveDetailsLightweight,
  getDriverById,
  getDriverPool,
  getDriverPoolForPromo,
  getDriverPoolForScenarios,
  getDriverPoolPage,
  getDriverWithEligibilityFields,
  getDriversInRegion,
  getDriversInRegions,
  getLinkedPromosForBundle,
  getNestedProperty,
  getNextIncompleteRound,
  getOfferById,
  getOfferRounds,
  getOfferStrategy,
  getOffersForBundle,
  getOrCreatePlan,
  getOriginLocationFromBundle,
  getPromoById,
  getRegionIdForCustomer,
  getRegionIdsForOrganization,
  getScenarioBundle,
  getScenarioById,
  getScenarioWithMoves,
  groupByDriverId,
  handleOfferDeclined,
  initializeOfferRounds,
  initializeOfferStrategy,
  isOfferAccepted,
  isOrderAssignable,
  isRideRetractable,
  log,
  logger,
  markRoundCompleted,
  oneWayConversions,
  processNextOfferRound,
  rankDriversForLitePlanning,
  rescindOffer,
  rescindOtherOffers,
  resolveDriverAssignment,
  resolveGuestrideWorkflowsetId,
  resolveLogger,
  resolveOfferAuditFields,
  resolveScopeToRegionIds,
  roundTripConversions,
  runEligibility2 as runEligibility,
  safeJsonParse,
  safeJsonStringify,
  updateBundleConfig,
  updateBundleScenariosStatus,
  updateBundleStatus,
  updateBundleStatuses,
  updateScenario,
  updateScenarioConfig,
  updateScenarioStatus,
  updateScenarioStatuses,
  updateScenarioWorkflowData
};
//# sourceMappingURL=index.mjs.map